import { FunctionComponent } from 'react';
import type { CancelTokenSource } from 'axios';
import { LS_IQ11_TOKEN } from '../../../constants';
import { arrayToObject, sanitizeToNumeric, toBool } from '../../../utils/utils';

import type {
  FetchTranslationsUrlRequest,
  FetchTranslationsUrlResponse,
  Domain,
  FetchDomainResponse,
  FetchDomainApiResponse,
  AddDomainRequest,
  AddDomainResponse,
  VerifyDomainRequest,
  VerifyDomainResponse,
  DeleteDomainRequest,
  DeleteDomainResponse,
  FileConversionRequest,
  FileConversionResponse,
  FileConversionConfiguration,
  FileConversionConfigurationsResponse,
  FileConversionHistoryRequest,
  FileConversionHistoryResponse,
  FileConversionDownloadRequest,
  FileConversionDownloadResponse,
  FileConversionSubmitRequest,
  FileConversionSubmitResponse,
  LinkGooglePayRequest,
  LinkGooglePayResponse,
  AccountBillingSettingsResponse,
  AccountTransactions,
  AccountTransactionsRequest,
  EftFundingTransactionResponse,
  EftWithdrawTransactionResponse,
  InteracTransactionResponse,
  InteracBulkPayoutTransactionResponse,
  CreditCardTransactionResponse,
  TransactionId,
  ApiResponse,
  AuthorizationResponse,
  Login,
  PartnerAccounts,
  PartnerAccountsRequest,
  PartnerAccountBusinessCaseRequest,
  PartnerAccountCreationResponse,
  PartnerAccountCreationLinkResponse,
  PartnerAccountSummaryRequest,
  PartnerAccountSummaryResponse,
  PartnerAccountSummaryDetailsResponse,
  PartnerAccountSummaryDetailsRequest,
  PartnerAccountRequest,
  PartnerBillingSettingsDetailsRequest,
  PartnerBillingSettingsDetailsResponse,
  PartnerAccountUsersChangePasswordRequest,
  PartnerAccountUsersChangePasswordResponse,
  PartnerBillingPackageDetailsRequest,
  PartnerBillingPackageDetailsResponse,
  PartnerBillingPackagesResponse,
  PartnerCustomPackageRequest,
  PartnerCustomPackageResponse,
  PartnerInvoiceDetailsRequest,
  PartnerInvoiceDetailsResponse,
  InvoicePdfRequest,
  InvoicePdfResponse,
  InvoicesRequest,
  PartnerInvoicesResponse,
  StatementCsvRequest,
  StatementCsvResponse,
  PartnerStatementPdfRequest,
  PartnerStatementPdfResponse,
  PartnerStatisticsResponse,
  PartnersResponse,
  UserListsResponse,
  UserListsRequest,
  FetchPartnerAccountUsersApiResponse,
  FetchPartnerAccountUsersResponse,
  CreateUserRequest,
  CreateUserResponse,
  DeleteUserRequest,
  DeleteUserResponse,
  UserChangePasswordRequest,
  UserChangePasswordResponse,
  SingleUseTokenResponse,
  SingleUseTokenRequest,
  TransactionsRefundResponse,
  TransactionsRefundRequest,
  CreateAccountUserRequest,
  DeleteAccountUserRequest,
  CreateAccountUserResponse,
  DeleteAccountUserResponse,
  FetchAccountUsersApiResponse,
  FetchAccountUsersResponse,
  AccountUsersChangePasswordRequest,
  AccountUsersChangePasswordResponse,
  CreateLeadRequest,
  LeadsDetailsRequest,
  LeadsDetailsResponse,
  LeadsAgentRequest,
  LeadsParentRequest,
  LeadsResponse,
  TokenInfoResponse,
  IQ11TokenResponse,
  IFrameEventRequest,
  IQ11CompleteRequest,
  LinkBankAccountManuallyRequest,
  LinkCreditCardRequest,
  GlobalPayTokenRequest,
  GlobalPayTokenResponse,
  FinancialInstitutionsRequest,
  FinancialInstitutionsResponse,
  FinancialInstitutionBranchesRequest,
  FinancialInstitutionBranchesResponse,
  FinancialInstitutionBranchesApiResponse,
  UserDataOverviewResponse,
  ActivityOverviewResponse,
  PartnerStatisticsDataResponse,
  ActivityOverviewRequest,
  IQ11Login,
  IQ11AuthorizationResponse,
  IQ11EmbedUrlResponse,
  IQ11CreditCardEmbedUrlRequest,
  CreditCardRequest,
  CreditCardResponse,
  ContactVerificationConfirmRequest,
  ContactVerificationConfirmResponse,
  DeleteCreditCardRequest,
  DeleteDebitCardRequest,
  BankAccountsRequest,
  BankAccountsResponse,
  DeleteBankAccountRequest,
  SetMyBankAccountRequest,
  SetMyBankAccountResponse,
  RTRWithdrawTransactionResponse,
  SetMyCreditCardRequest,
  SetMyCreditCardResponse,
  SetMyDebitCardRequest,
  SetMyDebitCardResponse,
  ELinxSingleUseTokenRequest,
  ELinxSingleUseTokenResponse,
  ELinxSetAccountRequest,
  ELinxDeclineRequest,
  PortalVideoRequest,
  TicketsApiResponse,
  TicketsResponse,
  TicketDetailsResponse,
  SubmitTicketCommentRequst,
  TicketDetialRequest,
  SubmitTicketRequest,
  AccountUserPermissionsResponse,
  AccountUserPermissionsRequest,
  AccountUserPermissionsPostRequest,
  AgreementDetailsResponse,
  SigningAuthorityInfoResponse,
  AgreementDownloadRequest,
  AgreementDownloadResponse,
  VoidAgreementRequest,
  DeleteSignerRequest,
  DeleteSignerResponse,
  EditSignerRequest,
  EditSignerResponse,
  AddSignerRequest,
  AddSignerResponse,
  BankAccountConnectionRequest,
  BankAccountConnectionResponse,
  LinkBankAccountRequest,
  ClientLogin,
  ClientAuthorizationResponse,
  ClientAccountBalanceRangeResponse,
  ClientResetPasswordEmailRequest,
  ClientResetPasswordEmailResponse,
  VerifyRecaptchaRequest,
  VerifyRecaptchaResponse,
  BusinessTypesResponse,
  SendOnboardingEmailRequest,
  AccountBalanceRequest,
  AccountBalanceResponse,
  AccountLimitsRequest,
  AccountLimitsResponse,
  TransactionsHistoryRequest,
  TransactionsHistoryResponse,
  SubAccountTransactionsHistoryRequest,
  SubAccountTransactionsHistoryResponse,
  BankHolidaysResponse,
  BankHolidaysApiResponse,
  ClientBankAccountsRequest,
  ClientBankAccountsResponse,
  ClientCreditCardsRequest,
  ClientCreditCardsResponse,
  ClientTransactionsRequest,
  ClientTransactionsApiResponse,
  ClientTransactionsResponse,
  ClientBalanceRequest,
  ClientBalanceResponse,
  DeleteClientBankAccountRequest,
  DeleteClientCreditCardRequest,
  EmbedUrlRequest,
  EmbedUrlResponse,
  SendELinxRequest,
  SendELinxResponse,
  EftFundRequest,
  EftFundResponse,
  AccountPermissionsRequest,
  AccountPermissionsResponse,
  AutoBalanceTransferRequest,
  AutoBalanceTransferResponse,
  CancelAutoBalanceTransferRequest,
  ChargeCreditCardRequest,
  ChargeCreditCardResponse,
  ScheduledEftFundRequest,
  ScheduledEftFundResponse,
  EftWithdrawRequest,
  EftWithdrawResponse,
  RtrWithdrawResponse,
  RtrWithdrawRequest,
  ScheduledEftWithdrawRequest,
  ScheduledEftWithdrawResponse,
  CreateAutoBalanceTransferRequest,
  SubAccountsRequest,
  SubAccountsResponse,
  EditAccountPermissionsRequest,
  PortalGenerateQRCodeRequest,
  PortalGenerateQRCodeResponse,
  PortalAuthenticateRequest,
  PortalAuthenticateResponse,
  PortalChangePasswordRequest,
  PortalResetPasswordRequest,
  AccountTransferFromRequest,
  AccountTransferFromResponse,
  AccountTransferToRequest,
  AccountTransferToResponse,
  CreateSubAccountRequest,
  CreateSubAccountResponse,
  ContactGroupsResponse,
  RenewAccountRequest,
  CreateGroupRequest,
  CreateGroupResponse,
  EditGroupRequest,
  DeleteGroupRequest,
  AddGroupContactRequest,
  AddGroupContactResponse,
  GroupContactRequest,
  GroupContactResponse,
  DeleteGroupContactRequest,
  BulkPaymentRequest,
  BulkCollectionRequest,
  BulkResponse,
  DefaultPaymentMethodRequest,
  DefaultPaymentMethodResponse,
  ContactPaymentMethodRequest,
  SetDefaultPaymentMethodRequest,
  ScheduledTransactionsRequest,
  ScheduledTransactionsResponse,
  CancelScheduledWithdrawRequest,
  CancelScheduledWithdrawResponse,
  CancelScheduledFundRequest,
  CancelScheduledFundResponse,
  EditScheduledWithdrawRequest,
  EditScheduledFundRequest,
  ConfirmTransactionRequest,
  GetTransactionCountsRequest,
  GetTransactionCountsResponse,
  InboundETransferTransactionRequest,
  InboundETransferTransactionResponse,
  VisaDirectPushTransactionResponse,
  CurrencyConversionTransactionRequest,
  CurrencyConversionTransactionResponse,
  TransactionsCancelRequest,
  TransactionsCancelResponse,
  ClientAccountListRequest,
  ClientAccountListResponse,
  ClientAccountListApiResponse,
  ApiPaymentMethodResponse,
  ClientAccountPaymentMethodRequest,
  PaymentMethodResponse,
  CreateClientAccountIndividualRequest,
  CreateClientAccountBusinessRequest,
  CreateClientAccountReceiveOnlyRequest,
  CreateClientAccountResponse,
  PortalUser,
  PartnerUser,
  UpdateWebhookRequest,
  RetriggerWebhookRequest,
  WebhookResponse,
  SetPlaidCredentialsRequest,
  SetFlinksCredentialsRequest,
  SetInveriteCredentialsRequest,
  SetGlobalPayCredentialsRequest,
  CreateClientAccountStandardRequest,
  ClientAccountBalanceRequest,
  ClientAccountBalanceResponse,
  TokenInfoRequest,
  AccountTransferFundRequest,
  AccountTransferFundResponse,
  AccountTransferWithdrawRequest,
  AccountTransferWithdrawResponse,
  AccountTransferFundWithdrawRequest,
  AccountTransferFundWithdrawResponse,
  DefaultBankAccountRequest,
  DefaultBankAccountResponse,
  AuthorizedIPsResponse,
  SaveAuthorizedIPsRequest,
  ELinxSettingsResponse,
  ELinxSettingsSetSendeeEmailRequest,
  ELinxSettingsSetSenderEmailRequest,
  ELinxSettingsSetTokenExpirationRequest,
  PostELinxSettingsUpdateRequestRequest,
  ELinxSettingsSetDeclineRequest,
  ClientAccountsRequest,
  ClientAccountsResponse,
  ClientChangePasswordRequest,
  IQ11EmbedUrlRequest,
  ParentAccountTransactionsRequest,
  UploadBatchFileDetailsRequest,
  UploadBatchFileResponse,
  UploadBatchFileApiResponse,
  UploadBatchRequest,
  UploadBatchFileDetailseResponse,
  PaasTransactionsRequest,
  PaasTransactionsResponse,
  BatchFileTransactionsRequest,
  BatchFileTransactionsResponse,
  ELinxHistoryResponse,
  ELinxHistoryRequest,
  DeleteInteracEmailRequest,
  InteracEmailRequest,
  InteracEmailResponse,
  InteracMoneyRequestRequest,
  InteracMoneyRequestResponse,
  CancelInteracMoneyRequest,
  CancelVoPayInstantRequest,
  CancelInteracMoneyResponse,
  CancelVoPayInstantResponse,
  LinkPushToCardRequest,
  ELinxCancelRequest,
  TicketCategoriesResponse,
  InteracAddEmailRequest,
  InteracAddEmailResponse,
  IQ11TokenizeRequest,
  IQ11TokenizeResponse,
  PartnerAccountTransferRequest,
  PartnerAccountTransferResponse,
  PartnerAccountFundAndTransferResponse,
  PartnerAccountTransferAndWithdrawResponse,
  PartnerAccountFundTransferAndWithdrawResponse,
  InteracBulkPayoutRequest,
  InteracBulkPayoutResponse,
  HubSpotDropdownDataResponse,
  HubSpotDealDataRequest,
  HubSpotDealDetailsResponse,
  HubSpotDealUpdateRequest,
  DocumentUploadRequest,
  DocumentUploadResponse,
  AccessTokenInfoRequest,
  AccessTokenInfoResponse,
  LinkPushToCardMapRequest,
  PushToCardMapResponse,
  OnboardingAppTokenResponse,
  OnboardingAppTokenRequest,
  AssignedBillingPackagesRequest,
  AssignedBillingPackagesResponse,
  WithdrawMyAccountRequest,
  WithdrawMyAccountResponse,
  FundMyAccountRequest,
  FundMyAccountResponse,
  VoidACHTransactionRequest,
  VoidACHTransactionResponse,
  RefundACHTransactionRequest,
  RefundACHTransactionResponse,
  ACHFundRequest,
  ACHFundResponse,
  ACHWithdrawRequest,
  ACHWithdrawResponse,
  ACHFundScheduleRequest,
  ACHFundScheduleResponse,
  ACHFundTransactionRequest,
  ACHFundTransactionResponse,
  ACHWithdrawTransactionRequest,
  ACHWithdrawTransactionResponse,
  ACHSheduleTransactionRequest,
  ACHSheduleTransactionResponse,
  RefundCreditCardTransactionRequest,
  RefundCreditCardTransactionResponse,
  AccountInvoicesResponse,
  GeoLocationResponse,
  SendLegalAgreementResponse,
  OnboardingTokenRequest,
  OnboardingTokenResponse,
  PostLegalAgreementResponse,
  FetchClientAccountLabelsRequest,
  FetchContactLabelsRequest,
  AddContactLabelRequest,
  FetchLabelsResponse,
  FetchLabelsApiResponse,
  AddClientAccountLabelRequest,
  AddLabelResponse,
  DeleteClientAccountLabelRequest,
  DeleteContactLabelRequest,
  DeleteLabelResponse,
  EditClientAccountRequest,
  MyBankAccountRequest,
  MyBankAccountResponse,
  BankAccountDocumentUploadRequest,
  AccountBillingDetailsResponse,
  AccountBillingDetailsRequest,
  BankAccountDocumentDeleteRequest,
  SetMyBankAccountPurposeResponse,
  SetMyBankAccountPurposeRequest,
  AccountDocumentResponse,
  AccountDocumentRequest,
  ReportsRequest,
  FetchSubscriptionListResponse,
  DownloadReportsResponse,
  ReportDetailResponse,
  ReportDetailApiResponse,
  EditReportRequest,
  CreateReportRequest,
  CreateReportsResponse,
  AccountVerificationRequest,
  AccountVerificationResponse,
  AccountVerificationConfirmRequest,
  AccountVerificationConfirmResponse,
  GetDebitAuthorityAgreementRequest,
  GetDebitAuthorityAgreementResponse,
  CreateDebitAuthorityAgreementRequest,
  AccountTransferScheduleResponse,
  AccountTransferScheduleRequest,
  AccountTransferScheduleCancelRequest,
  AccountTransferScheduleCancelResponse,
  AccountTransferSchedulesRequest,
  AccountTransferSchedulesResponse,
  UserChangeRoleRequest,
  PartnerAccountMatrixRequest,
  PortalVideoResponse,
  StatementPDFResponse,
  AccountVerificationInitiateRequest,
  AccountVerificationInitiateResponse,
  AccountBillingDetailsSaveRequest,
  DownloadReportRequest,
  LoginBrandingApiResponse,
  LoginBrandingResponse,
  AccountBrandingApiResponse,
  AccountBrandingResponse,
  AccountBrandingRequest,
  SaveAccountBrandingRequest,
  FAQResponse,
  FAQRequest,
  FAQAllQuestionsResponse,
  FAQCategoriesResponse,
  AccountBrandingLoginPageRequest,
  ApprovalInboxTransactionsRequest,
  ApprovalInboxTransactionsResponse,
  InboxStatusChageRequest,
  UserRolesRequest,
  UserRolesResponse,
  UpdateUserRoleRequest,
  UpdateUserRolePermissionRequest,
  WorkFlowsResponse,
  WorkflowCreateRequest,
  WorkflowUpdateRequest,
  TransactionTypesRequst,
  TransactionTypesResponse,
  BaseRailzeRequest,
  ChartOfAccountRequest,
  ChartOfAccountsResponse,
  ChartOfAccountResponse,
  RailzBusinessAccountResponse,
  RailzBillsResponse,
  RailzInvoicesResponse,
  PayBillsRequest,
  PayInvoicesRequest,
  RailzStatusSyncData,
  PortalDocumentResponse,
  AccountTransactionTypesRequest,
  AccountTransactionTypesResponse,
  DeleteBankAccountDocumentsRequest,
  GetChartOfAccountRequest,
  OnboardingAppUrlRequest,
  OnboardingAppUrlResponse,
  ClientDebitCardsRequest,
  ClientDebitCardsResponse,
  TicketAttachmentRequst,
  SubmitTicketResponse,
  TicketAttachmentRequest,
  TicketAttachmentResponse,
  AccountBillingDisconnectionRequest,
  AccountBillingSavePaymentInformationRequest,
  AccountBillingSavePaymentInformationResponse,
  RailzBillsCancelRequest,
  RailzInvoiceCancelRequest,
  FedwireTransactionResponse,
  RTPWithdrawTransactionResponse,
  MyAccountScheduleRequest,
  MyAccountScheduleResponse,
  EmailRequest,
  RailzBillsOrInvoicesRequest,
  RailzInvoicesApiResponse,
  RailzBillsApiResponse,
  WebhooksResponse,
  WebhooksRequest,
  AutoBalanceTransferReportResponse,
  CreditNotesInvoicesRequest,
  CreditNotesBillsRequest,
  CreditNotesApiResponse,
  CreditNotesResponse,
  ContactsRequest,
  ContactsResponse,
  ContactsApiResponse,
  GlobalSendRecentTransactionsResponse,
  GlobalSendRecentTransactionsRequest,
  GlobalSendRecentTransactionsApiResponse,
  CrossBorderCountriesRequest,
  CrossBorderCountriesResponse,
  CrossBorderCountriesApiResponse,
  DeliveryChannelsFieldsRequest,
  DeliveryChannelsFieldsApiResponse,
  DeliveryChannelsFieldsResponse,
  DeliveryChannelsRequest,
  DeliveryChannelsApiResponse,
  DeliveryChannelsResponse,
  CrossBorderQuotesRequest,
  CrossBorderQuotesResponse,
  DeliveryChannelsFieldsDataRequest,
  DeliveryChannelsFieldsDataResponse,
  CrossBorderRecipientContactsRequest,
  CrossBorderRecipientContactsApiResponse,
  CrossBorderRecipientContactsResponse,
  AddFavoriteRecipientContactRequest,
  AddFavoriteRecipientContactResponse,
  DeleteFavoriteRecipientContactRequest,
  DeleteFavoriteRecipientContactResponse,
  RecipientContactDeliveryMethodsRequest,
  RecipientContactDeliveryMethodsResponse,
  RecipientContactDeliveryMethodsApiResponse,
  CrossBorderUrlRequest,
  CrossBorderUrlResponse,
  CrossBorderUrlInfoRequest,
  CrossBorderUrlInfoResponse,
  CrossBorderUrlApiInfoResponse,
  GlobalSendAdminUrlInfoRequest,
  GlobalSendAdminUrlInfoResponse,
  TransactionsHistoryApiResponse,
  CrossBorderBankCitiesApiResponse,
  CrossBorderBankCitiesRequest,
  CrossBorderBankCitiesResponse,
  CrossBorderBankBranchesApiResponse,
  CrossBorderBankBranchesRequest,
  CrossBorderBankBranchesResponse,
  CrossBorderBankNamesApiResponse,
  CrossBorderBankNamesRequest,
  CrossBorderBankNamesResponse,
  CrossBorderBankLocationsRequest,
  CrossBorderBankLocationsResponse,
  CrossBorderBankLocationsApiResponse,
  CrossBorderAddDeliveryMethodRequest,
  CrossBorderAddDeliveryMethodResponse,
  EditRecipientRequest,
  EditRecipientResponse,
  GovernmentIdentifierRequest,
  GovernmentIdentifierSchemasResponse,
  GovernmentIdentifierSchemasRequest,
  AddGovernmentIdentifierRequest,
  GovernmentIdentifierResponse,
  AddGovernmentIdentifierResponse,
  GovernmentIdentifierApiResponse,
  AddRecipientRequest,
  AddRecipientResponse,
  CreateGlobalSendTransactionRequest,
  CreateGlobalSendTransactionResponse,
  EditContactRequest,
  BankSchemaRequest,
  BankSchemaResponse,
  BankSchemaApiResponse,
  GenerateOtpRequest,
  GenerateOtpResponse,
  ValidateOtpRequest,
  RequirePadAgreementRequest,
  RequirePadAgreementResponse,
  ElinxPaymentMethodsRequest,
  ElinxPaymentMethodsResponse,
  ElinxPaymentMethodsApiResponse,
  AcceptAgreementRequest,
  InterestAccountRequest,
  InterestAccountResponse,
  InterestAccountApiResponse,
  InterestAccountDetailRequest,
  InterestAccountDetailApiResponse,
  InterestAccountDetailResponse,
  InterestAccountRatesApiResponse,
  InterestAccountTransferRequest,
  InterestAccountTransferResponse,
  InterestAccountRatesRequest,
  InterestAccountRatesResponse,
  InterestAccountAccruedInterestRequest,
  InterestAccountAccruedInterestResponse,
  InterestAccountAccruedInterestApiResponse,
  InterestAccountTransactionsRequest,
  InterestAccountTransactionsResponse,
  InterestAccountTransactionsApiResponse,
  PortalClientLoginHistoryRequest,
  PortalClientLoginHistoryResponse,
  VerifyTransactionApproveRequest,
  VerifyTrasnsactionApproveResponse,
  UserSettingsRequest,
  UserSettingsResponse,
  SubmitUserSettingsRequest,
  RecipientContactsGenerateEmbedUrlRequest,
  RecipientContactsGenerateEmbedUrlResponse,
  ActivateDeactiveteClientAccountRequest,
  ActivateDeactiveteClientAccountResponse,
  ActivateDeactiveteContactRequest,
  ActivateDeactiveteContactResponse,
  IdVerificationResponse,
  IdVerificationRequest,
  TimezoneRequest,
  TicketClientsRequest,
  TicketDetailsClientRequest,
  TransactionRequest,
  ClientWebhooksRequest,
  SetClientEmailRequest,
  SetClientEmailResponse,
  SetMyApplePayRequest,
  SetMyApplePayResponse,
  SetMyPayPalRequest,
  SetMyPayPalResponse,
  SetMyVenmoRequest,
  SetMyVenmoResponse,
  GetDebitCardsRequest,
  GetDebitCardsResponse,
  GetDebitCardsApiResponse,
  RtpWithdrawRequest,
  RtpWithdrawResponse,
  FedwireWithdrawRequest,
  FedwireWithdrawResponse,
  AccountExtendedInfoResponse,
  PartnerAccountPermissionFieldsResponse,
  AccountPermissionRequest,
  ELinxSettingsSetNotificationRequest,
  ELinxSettingsSetReminderRequest,
  WatchListRequest,
  WatchListBaseResponse,
  WatchListResponse,
  AddWatchListRequest,
  DeleteWatchListRequest,
  SetWatchListRequest,
  CurrenciesRequest,
  CurrenciesResponse,
  CurrenciesApiResponse,
  InteracEditEmailRequest,
  InteracEditEmailResponse,
  AddContactRequest,
  AddContactResponse,
  GenerateGCMEmbedUrlResponse,
  GenerateGCMEmbedUrlRequest,
  RailzCreateInvoiceRequest,
  RailzCustomersRequest,
  RailzInventoryRequest,
  RailzTaxRatesRequest,
  RailzTrackingCategoriesRequest,
  RailzCreateBillRequest,
  RailzVendorsRequest,
  RailzCategories,
  RailzTrackingCategoriesResponse,
  RailzTaxRatesResponse,
  RailzInventoryResponse,
  RailzCustomersResponse,
  RailzVendorsBaseApiResponse,
  RailzCategoriesResponse,
  RailzCategoriesBaseApiResponse,
  RailzTrackingCategoriesBaseApiResponse,
  RailzTaxRatesBaseApiResponse,
  RailzInventoryBaseApiResponse,
  RailzCustomerBaseApiResponse,
  RailzVendorsResponse,
  AuthenticateMerchantUserResponse,
  ClientAuthorizationBaseResponse,
  CrossBorderCashPickupStatesRequest,
  CrossBorderCashPickupStatesResponse,
  CrossBorderCashPickupCitiesRequest,
  CrossBorderCashPickupCitiesResponse,
  CrossBorderCashPickupLocationsRequest,
  CrossBorderCashPickupLocationsResponse,
  PartnerSigningAuthorityInfoApiResponse,
  PartnerSigningAuthorityInfoResponse,
  GCMBalanceRequest,
  GCMBalanceResponse,
  GCMRatesFeesComparisonRequest,
  GCMRatesFeesComparisonResponse,
  GCMRatesFeesComparisonApiResponse,
  LinkPaypalRequest,
  LinkPaypalResponse,
  LinkVenmoRequest,
  LinkVenmoResponse,
  InteracLinkEmailRequest,
  InteracLinkEmailResponse,
  GenerateEmployeeOTPRequest,
  GenerateEmployeeOTPResponse,
  ValidateEmployeeOTPRequest,
  ValidateEmployeeOTPResponse,
  GCMFundingRailsResponse,
  InteractVerificationResponse,
  InteractVerificationRequest,
  SetMyGooglePayRequest,
  SetMyGooglePayResponse,
  AFTPullFundsRequest,
  AFTPullFundsResponse,
  ChargeGooglePayRequest,
  ChargeGooglePayResponse,
  DeletePaypalAccountRequest,
  DeleteVenmoAccountRequest,
  BillPaymentAccountMerchantsApiResponse,
  BillPaymentAccountMerchantsResponse,
  BillPaymentAccountMerchantsRequest,
  BillPaymentMerchantsApiResponse,
  BillPaymentMerchantsResponse,
  BillPaymentMerchantsRequest,
  BillPayInfoRequest,
  BillPayInfoResponse,
  BillPayCreateRequest,
  BillPayCreateResponse,
  BillPayAccountMerchantAddRequest,
  BillPayAccountMerchantAddResponse,
  BillPayAccountMerchantEditRequest,
  BillPayAccountMerchantEditResponse,
  GlobalSendDeliveryChannelsResponse,
  FetchGlobalSendAdminFundingOptionsResponse,
  DisputeManagementUrlInfoRequest,
  DisputeManagementUrlInfoResponse,
  GenerateEmbedUrlDisputeManagementRequest,
  GenerateEmbedUrlDisputeManagementResponse,
  DisputeManagementOverviewRequest,
  DisputeManagementOverviewResponse,
  DisputeManagementListRequest,
  DisputeManagementListApiResponse,
  DisputeManagementListResponse,
  DisputeStagesRequest,
  DisputeStagesApiResponse,
  DisputeStagesResponse,
  DisputeDetailsRequest,
  DisputeDetailsResponse,
  DisputeDocumentsRequest,
  DisputeDocumentsResponse,
  AddDisputeNoteRequest,
  AddDisputeNoteResponse,
  RespondAndChallengeDisputeRequest,
  RespondAndChallengeDisputeResponse,
  AcceptDisputeRequest,
  AcceptDisputeResponse,
  DisputeReasonsRequest,
  DisputeReasonsResponse,
  PublishGlobalSendAdminDeliveryChannelsRequest,
  SubmitGlobalSendAdminFundingOptionsRequest,
  InteracAutoDepositStatusRequest,
  InteracAutoDepositStatusResponse,
  ConvenienceFeesResponse,
  ConvenienceFeesResponseApi,
  ConvenienceFeesRequest,
  GooglePayChargeTransactionResponse,
  PayPalWithdrawTransactionResponse,
  VenmoWithdrawTransactionResponse,
  VenmoWithdrawRequest,
  VenmoWithdrawResponse,
  PayPalWithdrawRequest,
  PayPalWithdrawResponse,
  VenmoRecipientsRequest,
  VenmoRecipientsApiResponse,
  VenmoRecipientsResponse,
  PayPalRecipientsRequest,
  PayPalRecipientsApiResponse,
  PayPalRecipientsResponse,
  PayPalDefaultRecipientRequest,
  PayPalDefaultRecipientApiResponse,
  PayPalDefaultRecipientResponse,
  VenmoDefaultRecipientRequest,
  VenmoDefaultRecipientApiResponse,
  VenmoDefaultRecipientResponse,
  BillPayTransactionRequest,
  BillPayTransactionResponse,
  BillPayGenerateEmbedUrlRequest,
  BillPayGenerateEmbedUrlResponse,
  FileConversionConfigurationsResponseApi,
  FileConversionHistoryResponseApi,
  AccountUserPermissionsNewResponse,
  SubAccountPermissionsRequest,
  SubAccountPermissionResponse,
  SubAccountPermissionApiResponse,
  FileConversionDownloadSourceRequest,
  FileConversionDownloadSourceResponse,
} from './VoPayApi.types';
import { VoPayResponse } from './VoPayApi.types';

import { createHttp } from '../../../services/http';
import { verifyResponseType } from './VoPayApi.validation';

const createVoPayApi = (baseUrl: string) => {
  const http = createHttp(baseUrl);
  const learningHttp = createHttp('https://vopay.com/wp-json/learning-centre/v1');
  const ss = window.sessionStorage;
  /* Alphabetical Order */

  const api: Api = {
    fetchAccountTranslationsUrl: async (params, options) => {
      const url = '/portal/translations';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source: options?.source,
        bearer: options?.bearer,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<FetchTranslationsUrlResponse>(url, data, possibleResTypes);
    },
    generateEmployeeOTP: async (params, source?) => {
      const url = '/services/employee/login';

      const { data } = await http.post<typeof params, unknown>({
        url,
        // config: {},
        config: {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        },
        body: new URLSearchParams(params).toString(),
        // body: JSON.stringify(params),
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse, VoPayResponse.GenerateEmployeeOTPResponse];

      return verifyResponseType<GenerateEmployeeOTPResponse>(url, data, possibleResTypes);
    },
    validateEmployeeOTP: async (params, source?) => {
      const url = '/services/employee/validate-otp';

      const { data } = await http.post<typeof params, unknown>({
        url,
        // config: {},
        config: {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        },
        body: new URLSearchParams(params).toString(),
        // body: JSON.stringify(params),
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse, VoPayResponse.ValidateEmployeeOTPResponse];

      return verifyResponseType<ValidateEmployeeOTPResponse>(url, data, possibleResTypes);
    },
    fetchDomain: async (source) => {
      const url = '/account/domain';

      const { data } = await http.get({
        url,
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.FetchDomainApiResponse];

      const res = verifyResponseType<FetchDomainApiResponse>(url, data, possibleResTypes);

      return {
        ...res,
        Domain: hasDomain(res.Domain) ? res.Domain : null,
      };

      function hasDomain(domain: FetchDomainApiResponse['Domain']): domain is Domain {
        return !!domain && Object.keys(domain).length > 0;
      }
    },
    addDomain: async (params, source) => {
      const url = '/account/domain/add';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.AddDomainResponse];

      return verifyResponseType<AddDomainResponse>(url, data, possibleResTypes);
    },
    verifyDomain: async (params, source) => {
      const url = '/account/domain/verify';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.VerifyDomainResponse];

      return verifyResponseType<VerifyDomainResponse>(url, data, possibleResTypes);
    },
    convertFile: async (params, source) => {
      const url = '/file-conversion/convert';

      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {
          headers: {
            'Content-Type': 'application/json',
          },
        },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<FileConversionResponse>(url, data, possibleResTypes);
    },
    getFileConversionConfigurations: async (source) => {
      const url = '/file-conversion/configurations';

      const { data } = await http.get<undefined, unknown>({
        url,
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      const resp = verifyResponseType<FileConversionConfigurationsResponseApi>(url, data, possibleResTypes);

      return Object.entries(resp.Configurations).reduce(
        (acc, [key, value]) => {
          const newValue = {
            ...value,
            PaymentRails: {
              Credit: Object.values(value.PaymentRails.Credit || {}),
              Debit: Object.values(value.PaymentRails.Debit || {}),
            },
          };
          acc.Configurations[key] = newValue;
          return acc;
        },
        {
          ...resp,
          Configurations: {} as Record<string, FileConversionConfiguration>,
        } as FileConversionConfigurationsResponse,
      );
    },
    getFileConversionHistory: async (params, source) => {
      const url = '/file-conversion/history';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      const resp = verifyResponseType<FileConversionHistoryResponseApi>(url, data, possibleResTypes);
      return {
        ...resp,
        Conversions: Object.values(resp.Conversions || {}).map((conversion) => ({
          ...conversion,
          IsSubmitted: toBool(conversion.IsSubmitted || '0'),
        })),
      };
    },
    downloadFileConversion: async (params, source) => {
      const url = `/file-conversion/download`;

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<FileConversionDownloadResponse>(url, data, possibleResTypes);
    },
    downloadSourceFileConversion: async (params, source) => {
      const url = `/file-conversion/download-source`;

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<FileConversionDownloadResponse>(url, data, possibleResTypes);
    },
    submitFileConversion: async (params, source) => {
      const url = '/file-conversion/submit';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<FileConversionSubmitResponse>(url, data, possibleResTypes);
    },
    deleteDomain: async (params, source) => {
      const url = '/account/domain/delete';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.DeleteDomainResponse];

      return verifyResponseType<DeleteDomainResponse>(url, data, possibleResTypes);
    },
    linkGooglePay: async (params, source) => {
      const url = '/google-pay/link-google-pay';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.LinkGooglePayResponse];

      return verifyResponseType<LinkGooglePayResponse>(url, data, possibleResTypes);
    },
    fetchAccountBillingSettings: async (params, source) => {
      const url = '/partner/account/billing-settings';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.AccountBillingSettingsResponse];

      return verifyResponseType<AccountBillingSettingsResponse>(url, data, possibleResTypes);
    },
    fetchAccountTransactions: async (params, bearer, source) => {
      const url = '/account/transactions';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.AccountTransactions];
      return verifyResponseType<AccountTransactions>(url, data, possibleResTypes);
    },
    fetchAccountUsers: async (params, bearer, source) => {
      const url = '/account/users';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      const res = verifyResponseType<FetchAccountUsersApiResponse>(url, data, possibleResTypes);

      return {
        ...res,
        Users: Object.values(res.Users),
      };
    },
    fetchBusinessTypes: async (source) => {
      const url = 'partner/account/business-types';

      const { data } = await http.get<Record<never, never>, unknown>({
        url,
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<BusinessTypesResponse>(url, data, possibleResTypes);
    },
    fetchBillingSettingsDetails: async (params, source) => {
      const url = '/partner/billing-settings-details';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.PartnerBillingSettingsDetailsResponse];

      return verifyResponseType<PartnerBillingSettingsDetailsResponse>(url, data, possibleResTypes);
    },
    fetchBillingPackageDetails: async (params, source) => {
      const url = '/partner/billing-package-details';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.PartnerBillingPackageDetailsResponse];

      return verifyResponseType<PartnerBillingPackageDetailsResponse>(url, data, possibleResTypes);
    },
    fetchBillingPackages: async (source) => {
      const url = '/partner/billing-packages';

      const { data } = await http.get<Record<never, never>, unknown>({
        url,
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.PartnerBillingPackagesResponse];

      return verifyResponseType<PartnerBillingPackagesResponse>(url, data, possibleResTypes);
    },
    fetchCreditCardTransaction: async (params, bearer, source) => {
      const url = '/credit-card/charge/transaction';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.CreditCardTransactionResponse];
      return verifyResponseType<CreditCardTransactionResponse>(url, data, possibleResTypes);
    },
    fetchEFTFundTransaction: async (params, bearer, source) => {
      const url = '/eft/fund/transaction';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.EftFundingTransactionResponse];
      return verifyResponseType<EftFundingTransactionResponse>(url, data, possibleResTypes);
    },
    fetchEFTWithdrawTransaction: async (params, bearer, source) => {
      const url = '/eft/withdraw/transaction';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.EftWithdrawTransactionResponse];
      return verifyResponseType<EftWithdrawTransactionResponse>(url, data, possibleResTypes);
    },
    fetchFinancialInstitutionBranches: async (params, source) => {
      const url = '/iq11/financial-institutions-branches';

      const { data } = await http.get<typeof params, FinancialInstitutionBranchesApiResponse>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      const res = verifyResponseType<FinancialInstitutionBranchesApiResponse>(url, data, possibleResTypes);

      return {
        ...res,
        Branches: Object.values(res.Branches || {}),
        FinancialInstitutions: res.FinancialInstitutions ? Object.values(res.FinancialInstitutions || {}) : undefined,
      };
    },
    fetchFinancialInstitutions: async (params, source) => {
      const url = '/iq11/financial-institutions';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<FinancialInstitutionsResponse>(url, data, possibleResTypes);
    },
    fetchGlobalPayToken: async (params, bearer, source) => {
      const url = '/ucp/payment-methods';
      const GPVersion = '2021-03-22';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: {},
        source,
        bearer,
        GPVersion,
        body: JSON.stringify(params),
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.GlobalPayTokenResponse];

      return verifyResponseType<GlobalPayTokenResponse>(url, data, possibleResTypes);
    },
    fetchInteracMoneyRequestTransaction: async (params, bearer, source) => {
      const url = '/interac/money-request/transaction';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.InteracTransactionResponse];
      return verifyResponseType<InteracTransactionResponse>(url, data, possibleResTypes);
    },
    fetchInteracBulkPayoutTransaction: async (params, bearer, source) => {
      const url = '/interac/bulk-payout/transaction';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.InteracBulkPayoutTransactionResponse];
      return verifyResponseType<InteracBulkPayoutTransactionResponse>(url, data, possibleResTypes);
    },
    fetchRTRWithdrawTransaction: async (params, bearer, source) => {
      const url = '/rtr/withdraw/transaction';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.RTRWithdrawTransactionResponse];
      return verifyResponseType<RTRWithdrawTransactionResponse>(url, data, possibleResTypes);
    },
    fetchFedwireWithdrawTransaction: async (params, bearer, source) => {
      const url = '/fedwire/withdraw/transaction';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<FedwireTransactionResponse>(url, data, possibleResTypes);
    },
    fetchFedwireFundTransaction: async (params, bearer, source) => {
      const url = '/fedwire/fund/transaction';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<FedwireTransactionResponse>(url, data, possibleResTypes);
    },
    fetchRTPWithdrawTransaction: async (params, bearer, source) => {
      const url = '/rtp/withdraw/transaction';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<RTPWithdrawTransactionResponse>(url, data, possibleResTypes);
    },
    fetchiQ11TokenInfo: async (params, source) => {
      const url = '/iq11/embed-url';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<TokenInfoResponse>(url, data, possibleResTypes);
    },
    fetchLeadsAgentId: async (params, source) => {
      const url = '/partner/hubspot/leads-by-agentid';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<PartnerBillingPackagesResponse>(url, data, possibleResTypes);
    },
    fetchLeadsPartnerId: async (params, source) => {
      const url = '/partner/hubspot/leads-by-partnerid';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<PartnerBillingPackagesResponse>(url, data, possibleResTypes);
    },
    fetchLeadsDetails: async (params, source) => {
      const url = '/partner/hubspot/lead';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      // add new response to verify
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<LeadsDetailsResponse>(url, data, possibleResTypes);
    },
    fetchPartnerAccounts: async (params, source) => {
      const { data } = await http.get<PartnerAccounts>({
        url: '/partner/account',
        config: { params },
        source,
      });

      return data;
    },
    fetchPartnerInvoices: async (params, source) => {
      const url = '/partner/invoices';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.PartnerInvoicesResponse];

      return verifyResponseType<PartnerInvoicesResponse>(url, data, possibleResTypes);
    },
    fetchPartnerAccountSummary: async (params, source) => {
      const url = '/partner/account-summary';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<PartnerAccountSummaryResponse>(url, data, possibleResTypes);
    },
    fetchPartnerAccountSummaryDetails: async (params, source) => {
      const url = '/partner/account-summary-detail';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<PartnerAccountSummaryDetailsResponse>(url, data, possibleResTypes);
    },
    fetchPartnerInvoiceDetails: async (params, source) => {
      const url = '/partner/invoice-details';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.PartnerInvoiceDetailsResponse];

      return verifyResponseType<PartnerInvoiceDetailsResponse>(url, data, possibleResTypes);
    },
    fetchPartnerInvoicePdf: async (params, source) => {
      const url = '/partner/invoice-pdf';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.PartnerInvoicePdfResponse];

      return verifyResponseType<InvoicePdfResponse>(url, data, possibleResTypes);
    },
    fetchPartnerStatementPdf: async (params, source) => {
      const url = '/partner/statement-pdf';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.PartnerStatementPdfResponse];

      return verifyResponseType<PartnerStatementPdfResponse>(url, data, possibleResTypes);
    },
    fetchPartnerStatementCsv: async (params, source) => {
      const url = '/partner/statement-csv';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<StatementCsvResponse>(url, data, possibleResTypes);
    },
    fetchPartnerStatistics: async (source) => {
      const url = '/partner/statistics';

      const { data } = await http.get<Record<string, never>, unknown>({
        url,
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.PartnerStatisticsResponse];

      return verifyResponseType<PartnerStatisticsResponse>(url, data, possibleResTypes);
    },
    fetchSingleUseToken: async (params, source) => {
      const url = '/portal/retrieve-single-use-token';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.SingleUseTokenResponse];

      return verifyResponseType<SingleUseTokenResponse>(url, data, possibleResTypes);
    },
    fetchOnboardingAppUrl: async (params, source) => {
      const url = '/partner/account/onboarding-url';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.OnboardingAppUrlResponse];

      return verifyResponseType<OnboardingAppUrlResponse>(url, data, possibleResTypes);
    },
    fetchTicketDetails: async (params, source) => {
      const url = '/partner/ticket-detail';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<TicketDetailsResponse>(url, data, possibleResTypes);
    },
    fetchTickets: async (params, source) => {
      const url = '/partner/tickets';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<TicketsResponse>(url, data, possibleResTypes);
    },
    fetchAccountsOverviewActive: async (source) => {
      const url = '/partner/statistics';
      const params = { StatType: 'AccountsOverviewActive' };
      const { data } = await http.get<Record<string, never>, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<PartnerStatisticsDataResponse>(url, data, possibleResTypes);
    },
    fetchTransactionsOverview: async (source) => {
      const url = '/partner/statistics';
      const params = { StatType: 'TransactionsOverview' };
      const { data } = await http.get<Record<string, never>, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<PartnerStatisticsDataResponse>(url, data, possibleResTypes);
    },
    fetchPerformanceOverview: async (source) => {
      const url = '/partner/statistics';
      const params = { StatType: 'PerformanceOverview' };
      const { data } = await http.get<Record<string, never>, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<PartnerStatisticsDataResponse>(url, data, possibleResTypes);
    },
    fetchUserDataOverview: async (source) => {
      const url = '/partner/statistics';
      const params = { StatType: 'UserDataOverview' };
      const { data } = await http.get<Record<string, never>, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<UserDataOverviewResponse>(url, data, possibleResTypes);
    },
    fetchActivityOverview: async (baseParams, source) => {
      const url = '/partner/statistics';
      const params = { ...baseParams, StatType: 'ActivityOverview' };

      const { data } = await http.get<Record<string, never>, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<ActivityOverviewResponse>(url, data, possibleResTypes);
    },
    fetchPartnerAccountUsers: async (source) => {
      const url = '/partner/users';

      const { data } = await http.get<unknown>({
        url,
        config: {},
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      const res = verifyResponseType<FetchPartnerAccountUsersApiResponse>(url, data, possibleResTypes);

      return {
        ...res,
        Users: Object.values(res.Users),
      };
    },

    fetchCreditCards: async (params, bearer, source) => {
      const url = '/credit-card';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.CreditCardResponse];

      return verifyResponseType<CreditCardResponse>(url, data, possibleResTypes);
    },
    fetchBankAccounts: async (params, bearer, source) => {
      const url = '/bank-account';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.BankAccountsResponse];

      return verifyResponseType<BankAccountsResponse>(url, data, possibleResTypes);
    },

    fetchDebitCards: async (params, source) => {
      const url = '/debit-card';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.GetDebitCardsApiResponse];

      const res = verifyResponseType<GetDebitCardsApiResponse>(url, data, possibleResTypes);

      return {
        ...res,
        DebitCards: Object.values(res.DebitCards || {}).map((c) => ({
          ...c,
          IsDefault: toBool(c.IsDefault),
        })),
      };
    },

    fetchPaylinkSingleUseToken: async (params, source) => {
      const url = '/portal/retrieve-single-use-token';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ELinxSingleUseTokenResponse];

      return verifyResponseType<ELinxSingleUseTokenResponse>(url, data, possibleResTypes);
    },
    fetchAccountUserPermissions: async (params, bearer, source) => {
      const url = '/account/users/permissions';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<AccountUserPermissionsResponse>(url, data, possibleResTypes);
    },
    fetchAccountUserPermissionsNew: async (params, bearer, source) => {
      const url = '/account/users/permissions-new';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<AccountUserPermissionsNewResponse>(url, data, possibleResTypes);
    },
    fetchAgreementDetails: async (bearer, source) => {
      const url = '/account/agreement/status';

      const { data } = await http.get<unknown>({
        url,
        config: {},
        bearer,
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<AgreementDetailsResponse>(url, data, possibleResTypes);
    },
    fetchAgreementDownload: async (params, bearer, source) => {
      const url = '/account/agreement/download';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<AgreementDownloadResponse>(url, data, possibleResTypes);
    },
    fetchSigningAuthorityInfo: async (bearer, source) => {
      const url = '/account/submit-extended-info/signing-authority-info';

      const { data } = await http.get<unknown>({
        url,
        config: {},
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<SigningAuthorityInfoResponse>(url, data, possibleResTypes);
    },
    fetchAccountLimits: async (params, source) => {
      const url = '/account/remaining-limit';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<AccountLimitsResponse>(url, data, possibleResTypes);
    },
    fetchBankHolidays: async (source) => {
      const url = '/portal/bank-account-holidays';

      const { data } = await http.get<Record<never, never>, unknown>({
        url,
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      const res = verifyResponseType<BankHolidaysApiResponse>(url, data, possibleResTypes);
      return {
        ...res,
        BankHolidays: Object.values(res.BankHolidays || {}),
      };
    },
    fetchClientBankAccounts: async (params, source) => {
      const url = '/bank-account';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<ClientBankAccountsResponse>(url, data, possibleResTypes);
    },
    fetchClientCreditCards: async (params, source) => {
      const url = '/credit-card';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<ClientCreditCardsResponse>(url, data, possibleResTypes);
    },
    fetchClientDebitCards: async (params, source) => {
      const url = '/oct';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<ClientDebitCardsResponse>(url, data, possibleResTypes);
    },

    fetchClientTransactions: async (params, source) => {
      const url = '/iq11/transactions';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<ClientTransactionsApiResponse>(url, data, possibleResTypes);
      const transactions =
        typeof res.AggregatorTransactions === 'string'
          ? []
          : Object.values(res.AggregatorTransactions ?? {}).map((transaction) => {
              return {
                ...transaction,
                IsDataEnriched: toBool(transaction.IsDataEnriched),
              };
            });

      return {
        ...res,
        AggregatorTransactions: transactions,
      };
    },
    fetchClientBalance: async (params, source) => {
      const url = '/iq11/balance';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<ClientBalanceResponse>(url, data, possibleResTypes);
    },
    fetchAccountPermissions: async (params, source) => {
      const url = '/account/users/permissions';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<AccountPermissionsResponse>(url, data, possibleResTypes);
    },
    fetchAutoBalanceTransfer: async (params, source) => {
      const url = '/account/auto-balance-transfer';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<AutoBalanceTransferResponse>(url, data, possibleResTypes);
    },
    fetchSubAccounts: async (params, source) => {
      const url = '/account/subaccount';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<SubAccountsResponse>(url, data, possibleResTypes);
    },
    fetchContactGroups: async (params, source) => {
      const url = '/group';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<ContactGroupsResponse>(url, data, possibleResTypes);
    },
    fetchGroupContact: async (params, source) => {
      const url = '/group/contacts';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<GroupContactResponse>(url, data, possibleResTypes);
    },
    fetchScheduledTransactions: async (params, source) => {
      const url = '/eft/scheduled-transactions';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<ScheduledTransactionsResponse>(url, data, possibleResTypes);
    },
    fetchInboundETransferTransaction: async (params, source) => {
      const url = '/interac/inbound/transaction';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<InboundETransferTransactionResponse>(url, data, possibleResTypes);
    },
    fetchVisaDirectPushTransaction: async (params, source) => {
      const url = '/push-to-card/push-funds/transaction';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<VisaDirectPushTransactionResponse>(url, data, possibleResTypes);
    },
    fetchCurrencyConversionTransaction: async (params, source) => {
      const url = '/gcm/conversion/transaction';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<CurrencyConversionTransactionResponse>(url, data, possibleResTypes);
    },
    fetchMyBankAccount: async (params, bearer, source) => {
      const url = 'my-bank-account';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        bearer,
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<MyBankAccountResponse>(url, data, possibleResTypes);
    },
    fetchClientAccounts: async (params, bearer, source) => {
      const url = '/account/client-accounts';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<ClientAccountsResponse>(url, data, possibleResTypes);
    },
    fetchClientAccountList: async (params, source) => {
      const url = '/account/client-accounts';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<ClientAccountListApiResponse>(url, data, possibleResTypes);

      return {
        ...res,
        ClientAccounts: Object.values(res.ClientAccounts || {}).map((account) => ({
          ...account,
          IsActive: toBool(account.IsActive),
          Labels: account.Labels.split(',').filter((label) => label !== ''),
          Balances: Object.values(account.Balances || {}).map((balance) => ({
            ...balance,
            Balance: Number(sanitizeToNumeric(balance.Balance)),
            PendingBalance: Number(sanitizeToNumeric(balance.PendingBalance)),
            AvailableBalance: Number(sanitizeToNumeric(balance.AvailableBalance)),
          })),
        })),
      };
    },

    fetchClientAccountBalanceRange: async (source) => {
      const url = '/portal/client-account/balance-range';

      const { data } = await http.get<Record<never, never>, unknown>({
        url,
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<ClientAccountBalanceRangeResponse>(url, data, possibleResTypes);

      return {
        ...res,
      };
    },

    fetchClientAccountPaymentMethods: async (params, source) => {
      const url = '/account/client-accounts/payment-methods';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<ApiPaymentMethodResponse>(url, data, possibleResTypes);

      return {
        ...res,
        PaymentMethods: Object.values(res.PaymentMethods || {}).map((m) => ({
          ...m,
          IsDefault: toBool(m.IsDefault),
        })),
      };
    },

    fetchContactPaymentMethods: async (params, source) => {
      const url = '/contacts/payment-methods';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<ApiPaymentMethodResponse>(url, data, possibleResTypes);

      return {
        ...res,
        PaymentMethods: Object.values(res.PaymentMethods || {}).map((m) => ({
          ...m,
          IsDefault: toBool(m.IsDefault),
        })),
      };
    },

    fetchClientBusinessTypes: async (source) => {
      const url = '/account/business-types';

      const { data } = await http.get<Record<never, never>, unknown>({
        url,
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<BusinessTypesResponse>(url, data, possibleResTypes);
    },
    fetchClientAccountBalance: async (params, source) => {
      const url = '/account/client-accounts/balance';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<ClientAccountBalanceResponse>(url, data, possibleResTypes);
    },
    fetchDefaultBankAccount: async (params, source) => {
      const url = '/bank-account/default-bank-account';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<DefaultBankAccountResponse>(url, data, possibleResTypes);
    },

    postWithdrawMyAccount: async (params, source) => {
      const url = '/account/withdraw-my-account';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<WithdrawMyAccountResponse>(url, data, possibleResTypes);
    },
    postFundMyAccount: async (params, source) => {
      const url = '/account/fund-my-account';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<FundMyAccountResponse>(url, data, possibleResTypes);
    },
    postVoidACHTransaction: async (params, source) => {
      const url = '/ach/void';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<VoidACHTransactionResponse>(url, data, possibleResTypes);
    },
    postRefundACHTransaction: async (params, source) => {
      const url = '/ach/refund';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<RefundACHTransactionResponse>(url, data, possibleResTypes);
    },
    postACHFund: async (params, source) => {
      const url = '/ach/fund';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<ACHFundResponse>(url, data, possibleResTypes);
    },
    postACHWithdraw: async (params, source) => {
      const url = '/ach/withdraw';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<ACHWithdrawResponse>(url, data, possibleResTypes);
    },
    postACHFundSchedule: async (params, source) => {
      const url = '/ach/fund/schedule';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<ACHFundScheduleResponse>(url, data, possibleResTypes);
    },
    postACHWithdrawSchedule: async (params, source) => {
      const url = '/ach/withdraw/schedule';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<ACHFundScheduleResponse>(url, data, possibleResTypes);
    },
    fetchACHFundTransaction: async (params, source) => {
      const url = '/ach/fund/transaction';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<ACHFundTransactionResponse>(url, data, possibleResTypes);
    },
    fetchACHWithdrawTransaction: async (params, source) => {
      const url = '/ach/withdraw/transaction';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<ACHWithdrawTransactionResponse>(url, data, possibleResTypes);
    },
    fetchACHSheduleTransaction: async (params, source) => {
      const url = '/ach/scheduled-transactions';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<ACHSheduleTransactionResponse>(url, data, possibleResTypes);
    },
    postRefundCreditCardTransaction: async (params, source) => {
      const url = '/credit-card/refund';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<RefundCreditCardTransactionResponse>(url, data, possibleResTypes);
    },

    fetchPaasTransactions: async (params, source) => {
      const url = '/iq11/paas/transactions';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<PaasTransactionsResponse>(url, data, possibleResTypes);
    },
    fetchBatchFileTransactions: async (params, source) => {
      const url = '/account/transactions/batch-file';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<BatchFileTransactionsResponse>(url, data, possibleResTypes);
    },
    fetchInteracEmail: async (params, source) => {
      const url = '/interac/email';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<InteracEmailResponse>(url, data, possibleResTypes);
    },
    fetchSendLegalAgreement: async (bearer, source) => {
      const url = '/vopay/onboarding-app/legal-agreement';
      const { data } = await http.get<unknown>({
        url,
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<SendLegalAgreementResponse>(url, data, possibleResTypes);
    },
    fetchAccountVerification: async (params, source) => {
      const url = '/account-verification';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<AccountVerificationResponse>(url, data, possibleResTypes);
    },

    postVoidAgreement: async (params, bearer, source) => {
      const url = '/account/agreement/void';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    postDeleteSigner: async (params, bearer, source) => {
      const url = '/account/submit-extended-info/signing-authority-info/delete';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<DeleteSignerResponse>(url, data, possibleResTypes);
    },
    postEditSigner: async (params, bearer, source) => {
      const url = `/account/submit-extended-info/signing-authority-info/edit`;
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<EditSignerResponse>(url, data, possibleResTypes);
    },
    postAddSigner: async (params, bearer, source) => {
      const url = `/account/submit-extended-info/signing-authority-info/add`;
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<AddSignerResponse>(url, data, possibleResTypes);
    },
    postCreateUser: async (params, source) => {
      const url = '/partner/user';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<CreateUserResponse>(url, data, possibleResTypes);
    },
    postDeleteUser: async (params, source) => {
      const url = '/partner/users/delete';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<DeleteUserResponse>(url, data, possibleResTypes);
    },
    postSendOnboardingEmail: async (params, source) => {
      const url = '/partner/send-onboarding-url';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    postUserChangePassword: async (params, source) => {
      const url = '/partner/users/change-password';
      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<UserChangePasswordResponse>(url, data, possibleResTypes);
    },
    postUserChangeRole: async (params, source) => {
      const url = '/partner/users/role';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    generateSingleUseToken: async (params, source) => {
      const url = '/partner/generate-single-use-token';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    postAccountUsersChangePassword: async (params, bearer?, source?) => {
      const url = '/account/users/change-password';
      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        source,
        bearer,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.AccountUsersChangePasswordResponse];

      return verifyResponseType<AccountUsersChangePasswordResponse>(url, data, possibleResTypes);
    },
    postCreateAccountUser: async (params, bearer, source) => {
      const url = '/account/user';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<CreateAccountUserResponse>(url, data, possibleResTypes);
    },
    postCreateLead: async (params, source) => {
      const url = '/partner/hubspot/create-lead';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    postDeleteAccountUser: async (params, bearer, source) => {
      const url = '/account/users/delete';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<DeleteAccountUserResponse>(url, data, possibleResTypes);
    },
    postIFrameEvent: async (params, source) => {
      const url = '/iq11/log-iframe-event';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    postIQ11Complete: async (params, source) => {
      const Token = ss.getItem(LS_IQ11_TOKEN) || '';
      const url = `/iq11/complete/${Token}`;
      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<IQ11TokenResponse>(url, data, possibleResTypes);
    },
    postLinkBankAccountManually: async (params, source) => {
      const url = '/iq11/link-bank-account-manually';

      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<IQ11TokenResponse>(url, data, possibleResTypes);
    },
    postLinkCreditCard: async (params, source) => {
      const url = '/credit-card/link-credit-card';

      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.IQ11TokenResponse];

      return verifyResponseType<IQ11TokenResponse>(url, data, possibleResTypes);
    },
    postLinkPushToCard: async (params, source) => {
      const url = '/debit-card/link-card';

      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<IQ11TokenResponse>(url, data, possibleResTypes);
    },
    postPartnerAccount: async (params, source, overrideAuth) => {
      const url = '/partner/account';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: {},
        body: JSON.stringify(params),
        source,
        overrideAuth,
      });

      const possibleResTypes: VoPayResponse[] = [
        VoPayResponse.PartnerAccountCreationLinkResponse,
        VoPayResponse.PartnerAccountCreationResponse,
      ];

      return verifyResponseType<PartnerAccountCreationResponse | ApiResponse>(url, data, possibleResTypes);
    },
    postPartnerAccountBusinessCase: async (params, source?) => {
      const url = '/partner/account/business-case';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    postPartnerAccountMatrix: async (params, source?) => {
      const url = '/partner/account/account-matrix';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    postPartnerAccountPermissions: async (params, source?) => {
      const { data } = await http.post<typeof params, ApiResponse>({
        url: '/partner/account/set-permissions',
        body: JSON.stringify(params),
        config: {},
        source,
      });

      return data;
    },
    postAccountUserPermissions: async (params, bearer, source?) => {
      const { data } = await http.post<typeof params, ApiResponse>({
        url: '/account/users/permissions',
        config: { params },
        source,
        bearer,
      });

      return data;
    },
    postPartnerAccountUsersChangePassword: async (params, source?) => {
      const url = '/partner/account/users/change-password';
      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.PartnerAccountUsersChangePasswordResponse];

      return verifyResponseType<PartnerAccountUsersChangePasswordResponse>(url, data, possibleResTypes);
    },
    postPartnerAuthPing: async (source?) => {
      const url = '/partner/auth/ping';

      const { data } = await http.post<Record<string, never>, unknown>({
        url,
        config: {},
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    postPartnerCustomPackage: async (params, source?) => {
      const url = '/partner/custom-package';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.PartnerCustomPackageResponse];

      return verifyResponseType<PartnerCustomPackageResponse>(url, data, possibleResTypes);
    },
    postPaylinkDecline: async (params, source) => {
      const url = '/elinx/decline';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ELinxSingleUseTokenResponse];

      return verifyResponseType<ELinxSingleUseTokenResponse>(url, data, possibleResTypes);
    },
    postPaylinkSetAccount: async (params, source) => {
      const url = '/elinx/set-account';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ELinxSingleUseTokenResponse];

      return verifyResponseType<ELinxSingleUseTokenResponse>(url, data, possibleResTypes);
    },
    postPortalPartnerLogin: async (params, source?) => {
      const url = '/portal/partner/login';

      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.AuthorizationResponse];

      return verifyResponseType<AuthorizationResponse>(url, data, possibleResTypes);
    },

    postPortalGenerateQRCode: async (params, source?) => {
      const url = '/portal/generate-qr-code';

      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<PortalGenerateQRCodeResponse>(url, data, possibleResTypes);
    },
    postPortalAuthenticate: async (params, source?) => {
      const url = '/portal/authenticate';

      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<PortalAuthenticateResponse>(url, data, possibleResTypes);
    },
    postPortalChangePassword: async (params, source?) => {
      const url = '/portal/change-password';

      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    postPortalResetPassword: async (params, source?) => {
      const url = '/portal/reset-password';

      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },

    postIQ11Login: async (params, source?) => {
      const url = '/account/client-accounts/login';
      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.IQ11AuthorizationResponse];

      return verifyResponseType<IQ11AuthorizationResponse>(url, data, possibleResTypes);
    },
    postIQ11AuthPing: async (bearer, source?) => {
      const url = '/account/client-accounts/ping';
      const { data } = await http.post<Record<string, never>, unknown>({
        url,
        config: {},
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    postBankAccountConnection: async (params, source) => {
      const url = '/iq11/bank-account-connection';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<BankAccountConnectionResponse>(url, data, possibleResTypes);
    },
    postLinkBankAccount: async (params, source) => {
      const url = '/iq11/link-bank-account';
      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<IQ11TokenResponse>(url, data, possibleResTypes);
    },
    submitPartnerSupportTicket: async (params, source) => {
      const url = '/partner/submit-ticket';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: {},
        body: JSON.stringify(params),
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<SubmitTicketResponse>(url, data, possibleResTypes);
    },
    postTicketAttachment: async (params, source) => {
      const url = '/partner/ticket-attachment';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: {},
        body: JSON.stringify(params),
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.UploadFileAttachmentResponse];

      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    postSubmitTicketComment: async (params, source) => {
      const url = '/partner/submit-ticket-comment';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: {},
        body: JSON.stringify(params),
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    postIQ11CreditCardEmbedUrl: async (params, bearer, source) => {
      const url = '/credit-card/generate-embed-url';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.IQ11EmbedUrlResponse];

      return verifyResponseType<IQ11EmbedUrlResponse>(url, data, possibleResTypes);
    },
    postIQ11EmbedUrl: async (params, bearer, source) => {
      const url = '/iq11/generate-embed-url';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.IQ11EmbedUrlResponse];

      return verifyResponseType<IQ11EmbedUrlResponse>(url, data, possibleResTypes);
    },
    postDeleteCreditCard: async (params, bearer, source) => {
      const url = '/credit-card/delete';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    postDeleteBankAccount: async (params, bearer, source) => {
      const url = '/bank-account/delete';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    postDeleteBankAccountDocuments: async (params, bearer, source) => {
      const url = '/bank-account/documents/delete';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    postTransactionRefund: async (params, bearer, source) => {
      const url = 'account/transactions/refund';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.TransactionsRefundResponse];

      return verifyResponseType<TransactionsRefundResponse>(url, data, possibleResTypes);
    },
    postTransactionCancel: async (params, bearer, source) => {
      const url = 'account/transactions/cancel';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<TransactionsCancelResponse>(url, data, possibleResTypes);
    },
    postSetMyBankAccount: async (params, bearer, source) => {
      const url = 'bank-account/set-my-bank-account';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.SetMyBankAccountResponse];

      return verifyResponseType<SetMyBankAccountResponse>(url, data, possibleResTypes);
    },
    postSetMyBankAccountWithPurpose: async (params, bearer, source) => {
      const url = 'bank-account/set-my-bank-account';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.SetMyBankAccountPurposeResponse];

      return verifyResponseType<SetMyBankAccountPurposeResponse>(url, data, possibleResTypes);
    },
    postSetMyCreditCard: async (params, bearer, source) => {
      const url = 'credit-card/set-my-credit-card';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.SetMyCreditCardResponse];

      return verifyResponseType<SetMyCreditCardResponse>(url, data, possibleResTypes);
    },
    postPortalClientLogin: async (params, source?) => {
      const url = '/portal/login';
      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      const resp = verifyResponseType<ClientAuthorizationBaseResponse>(url, data, possibleResTypes);
      return {
        ...resp,
        ChatSupportEnabled: toBool(resp.ChatSupportEnabled || '0'),
        BankAccountVerificationEnabled: toBool(resp.BankAccountVerificationEnabled || '0'),
        InteracVerificationEnabled: toBool(resp.InteracVerificationEnabled || '0'),
        SupportTicketEnabled: toBool(resp.SupportTicketEnabled || '0'),
        GooglePayEnabled: toBool(resp.GooglePayEnabled || '0'),
        MicroDepositBankAccountVerificationEnabled: toBool(resp.MicroDepositBankAccountVerificationEnabled || '0'),
        CardBasicVerificationEnabled: toBool(resp.CardBasicVerificationEnabled || '0'),
        CardEnhancedVerificationEnabled: toBool(resp.CardEnhancedVerificationEnabled || '0'),
        FileConversionEnabled: toBool(resp.FileConversionEnabled || '0'),
        FileSubmissionEnabled: toBool(resp.FileSubmissionEnabled || '0'),
        BillPayEnabled: toBool(resp.BillPayEnabled || '0'),
        AccountAndBillingEnabled: toBool(resp.AccountAndBillingEnabled || '0'),
        FundMyAccountEnabled: toBool(resp.FundMyAccountEnabled || '0'),
        WithdrawMyAccountEnabled: toBool(resp.WithdrawMyAccountEnabled || '0'),
        BankAccountTransferEnabled: toBool(resp.BankAccountTransferEnabled || '0'),
        OperationalAccountsEnabled: toBool(resp.OperationalAccountsEnabled || '0'),
        PartnerBillsAccount: toBool(resp.PartnerBillsAccount || '0'),
        PaymentsEnabled: toBool(resp.PaymentsEnabled || '0'),
        CollectionsEnabled: toBool(resp.CollectionsEnabled || '0'),
        ShowSubAccountOrgChart: toBool(resp.ShowSubAccountOrgChart || '0'),
        DigitalWalletsEnabled: toBool(resp.DigitalWalletsEnabled || '0'),
      };
    },
    postPortalClientLoginHistory: async (params, source?) => {
      const url = '/account/user/log-access';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<PortalClientLoginHistoryResponse>(url, data, possibleResTypes);
    },
    fetchGeolocation: async () => {
      const url = 'https://ipinfo.io/json?token=f8d866afa09bc6';
      const { data } = await http.get<Record<never, never>, unknown>({
        url,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.GeoLocationResponse];

      return verifyResponseType<GeoLocationResponse>(url, data, possibleResTypes);
    },

    postClientChangePassword: async (params, source) => {
      const url = '/account/client-accounts/change-password';
      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },

    postClientAuthPing: async (source?) => {
      const url = '/auth/ping';
      const { data } = await http.post<Record<string, never>, unknown>({
        url,
        config: {},
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    postClientResetPasswordEmail: async (params, source) => {
      const url = '/portal/request-password-reset';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<ClientResetPasswordEmailResponse>(url, data, possibleResTypes);
    },
    postVerifyRecaptcha: async (params, source) => {
      const url = '/portal/verify-recaptcha';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<VerifyRecaptchaResponse>(url, data, possibleResTypes);
    },
    postAccountBalance: async (params, source) => {
      const url = '/account/balance';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<AccountBalanceResponse>(url, data, possibleResTypes);
    },
    postDeleteClientBankAccount: async (params, source) => {
      const url = '/bank-account/delete';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    postDeletePaypalAccount: async (params, source) => {
      const url = '/paypal/delete';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    postDeleteVenmoAccount: async (params, source) => {
      const url = '/venmo/delete';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    postSetMyClientBankAccount: async (params, source) => {
      const url = 'bank-account/set-my-bank-account';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.SetMyBankAccountResponse];
      return verifyResponseType<SetMyBankAccountResponse>(url, data, possibleResTypes);
    },
    postSetClientMyCreditCard: async (params, source) => {
      const url = 'credit-card/set-my-credit-card';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.SetMyCreditCardResponse];

      return verifyResponseType<SetMyCreditCardResponse>(url, data, possibleResTypes);
    },
    postSetClientMyDebitCard: async (params, source) => {
      const url = '/debit-card/set-debit-card';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.SetMyCreditCardResponse];

      return verifyResponseType<SetMyDebitCardResponse>(url, data, possibleResTypes);
    },
    postSetClientEmail: async (params, source) => {
      const url = '/interac/email/set-my-email';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.SetClientEmailResponse];

      return verifyResponseType<SetClientEmailResponse>(url, data, possibleResTypes);
    },
    postSetClientGooglePay: async (params, source) => {
      const url = '/google-pay/set-my-google-pay';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.SetMyGooglePayResponse];

      return verifyResponseType<SetMyGooglePayResponse>(url, data, possibleResTypes);
    },
    postSetClientApplePay: async (params, source) => {
      const url = '/apple-pay/set-my-apple-pay';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.SetMyApplePayResponse];

      return verifyResponseType<SetMyApplePayResponse>(url, data, possibleResTypes);
    },
    postSetClientPayPal: async (params, source) => {
      const url = '/paypal/set-my-recipient';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.SetMyPayPalResponse];

      return verifyResponseType<SetMyPayPalResponse>(url, data, possibleResTypes);
    },
    postSetClientVenmo: async (params, source) => {
      const url = '/venmo/set-my-recipient';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.SetMyVenmoResponse];

      return verifyResponseType<SetMyVenmoResponse>(url, data, possibleResTypes);
    },
    postDeleteClientCreditCard: async (params, source) => {
      const url = '/credit-card/delete';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    postDeleteDebitCard: async (params, source) => {
      const url = '/oct/delete';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    postEmbedUrl: async (params, source, bearer) => {
      const url = '/iq11/generate-embed-url';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<EmbedUrlResponse>(url, data, possibleResTypes);
    },
    postSendPaylink: async (params, source) => {
      const url = '/elinx';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<SendELinxResponse>(url, data, possibleResTypes);
    },
    postEftFund: async (params, source) => {
      const url = '/eft/fund';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<EftFundResponse>(url, data, possibleResTypes);
    },
    postScheduledEftFund: async (params, source) => {
      const url = '/eft/fund/schedule';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<ScheduledEftFundResponse>(url, data, possibleResTypes);
    },
    postCancelAutoBalanceTransfer: async (params, source) => {
      const url = '/account/auto-balance-transfer/cancel';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    postChargeCreditCard: async (params, source) => {
      const url = '/credit-card/charge';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<ChargeCreditCardResponse>(url, data, possibleResTypes);
    },
    postIdVerificationRequest: async (params, source) => {
      const url = '/qa/onfido-verification-link';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<IdVerificationResponse>(url, data, possibleResTypes);
    },
    postInteracMoneyRequest: async (params, source) => {
      const url = '/interac/money-request';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<InteracMoneyRequestResponse>(url, data, possibleResTypes);
    },
    postBulkPayoutRequest: async (params, source) => {
      const url = '/interac/bulk-payout';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<InteracBulkPayoutResponse>(url, data, possibleResTypes);
    },
    postCancelInteracMoneyRequest: async (params, source) => {
      const url = '/interac/money-request/transaction/request-cancellation';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<CancelInteracMoneyResponse>(url, data, possibleResTypes);
    },
    postCancelInteracBulkPayout: async (params, source) => {
      const url = '/interac/bulk-payout/transaction/request-cancellation';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<CancelInteracMoneyResponse>(url, data, possibleResTypes);
    },
    postCancelVoPayInstant: async (params, source) => {
      const url = '/rtr/withdraw/transaction/request-cancellation';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<CancelVoPayInstantResponse>(url, data, possibleResTypes);
    },
    postEftWithdraw: async (params, source) => {
      const url = '/eft/withdraw';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<EftWithdrawResponse>(url, data, possibleResTypes);
    },
    postRtrWithdraw: async (params, source) => {
      const url = '/rtr/withdraw';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<RtrWithdrawResponse>(url, data, possibleResTypes);
    },
    postScheduledEftWithdraw: async (params, source) => {
      const url = '/eft/withdraw/schedule';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<ScheduledEftWithdrawResponse>(url, data, possibleResTypes);
    },
    postCreateAutoBalanceTransfer: async (params, source) => {
      const url = '/account/auto-balance-transfer';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    saveSubAccountPermissions: async (params, source) => {
      const url = '/account/subaccount/set-permissions';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    postAccountTransferFrom: async (params, source) => {
      const url = '/account/transfer-from';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<AccountTransferToResponse>(url, data, possibleResTypes);
    },
    postAccountTransferTo: async (params, source) => {
      const url = '/account/transfer-to';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<AccountTransferToResponse>(url, data, possibleResTypes);
    },
    postAccountTransferFund: async (params, source) => {
      const url = '/account/client-accounts/fund-transfer';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<AccountTransferFundResponse>(url, data, possibleResTypes);
    },
    postAccountTransferWithdraw: async (params, source) => {
      const url = '/account/client-accounts/transfer-withdraw';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<AccountTransferWithdrawResponse>(url, data, possibleResTypes);
    },
    postAccountTransferFundWithdraw: async (params, source) => {
      const url = '/account/client-accounts/fund-transfer-withdraw';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<AccountTransferFundWithdrawResponse>(url, data, possibleResTypes);
    },
    postAccountTransferFundSchedule: async (params, source) => {
      const url = '/account/client-accounts/fund-transfer/schedule';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<AccountTransferScheduleResponse>(url, data, possibleResTypes);
    },
    postAccountTransferWithdrawSchedule: async (params, source) => {
      const url = '/account/client-accounts/transfer-withdraw/schedule';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<AccountTransferScheduleResponse>(url, data, possibleResTypes);
    },
    postAccountTransferFundWithdrawSchedule: async (params, source) => {
      const url = '/account/client-accounts/fund-transfer-withdraw/schedule';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<AccountTransferScheduleResponse>(url, data, possibleResTypes);
    },
    fetchAccountTransferSchedules: async (params, source) => {
      const url = '/account/client-accounts/scheduled-transfers';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<AccountTransferSchedulesResponse>(url, data, possibleResTypes);
    },
    postAccountTransferScheduleCancel: async (params, source) => {
      const url = '/account/client-accounts/transfer/schedule/cancel';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<AccountTransferScheduleCancelResponse>(url, data, possibleResTypes);
    },
    fetchTransactionsHistory: async (params, source) => {
      const url = '/account/transactions';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: {
          params: {
            ...params,
            ExportParams: params.ExportParams ? JSON.stringify(params.ExportParams) : undefined,
          },
        },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const response = verifyResponseType<TransactionsHistoryApiResponse>(url, data, possibleResTypes);
      return {
        ...response,
        Transactions: response.Transactions
          ? Object.values(response.Transactions).map((transaction) => ({
              ...transaction,
              IsRefund: toBool(transaction.IsRefund),
              IsRefunded: toBool(transaction.IsRefunded),
              ChildTransactionIDs: transaction.ChildTransactionIDs
                ? Object.values(transaction.ChildTransactionIDs)
                : [],
            }))
          : [],
      };
    },
    getSubAccountTransactionsHistory: async (params, source) => {
      const url = '/account/subaccount/transactions';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<SubAccountTransactionsHistoryResponse>(url, data, possibleResTypes);
    },
    postCreateSubAccount: async (params, source) => {
      const url = '/account/subaccount';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<CreateSubAccountResponse>(url, data, possibleResTypes);
    },
    postCreateGroup: async (params, source) => {
      const url = '/group';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<CreateGroupResponse>(url, data, possibleResTypes);
    },
    postEditGroup: async (params, source) => {
      const url = '/group/edit';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    postDeleteGroup: async (params, source) => {
      const url = '/group/delete';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    postAddGroupContact: async (params, source) => {
      const url = '/group/contact';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<AddGroupContactResponse>(url, data, possibleResTypes);
    },
    postDeleteGroupContact: async (params, source) => {
      const url = '/group/contact/delete';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    postBulkPayment: async (params, source) => {
      const url = '/batch/eft/withdraw';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: {},
        body: JSON.stringify(params),
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<BulkResponse>(url, data, possibleResTypes);
    },
    postBulkCollection: async (params, source) => {
      const url = '/batch/eft/fund';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: {},
        body: JSON.stringify(params),
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<BulkResponse>(url, data, possibleResTypes);
    },

    postBulkRTRPayment: async (params, source) => {
      const url = '/batch/rtr/withdraw';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: {},
        body: JSON.stringify(params),
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<BulkResponse>(url, data, possibleResTypes);
    },
    postBulkRTRCollection: async (params, source) => {
      const url = '/batch/rtr/fund';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: {},
        body: JSON.stringify(params),
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<BulkResponse>(url, data, possibleResTypes);
    },
    postBulkMoneyRequest: async (params, source) => {
      const url = '/batch/interac/money-request';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: {},
        body: JSON.stringify(params),
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<BulkResponse>(url, data, possibleResTypes);
    },
    postBulkBulkPayout: async (params, source) => {
      const url = '/batch/interac/bulk-payout';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: {},
        body: JSON.stringify(params),
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<BulkResponse>(url, data, possibleResTypes);
    },
    postBulkPaylink: async (params, source) => {
      const url = '/batch/elinx';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: {},
        body: JSON.stringify(params),
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<BulkResponse>(url, data, possibleResTypes);
    },
    postBulkPushToCardPayment: async (params, source) => {
      const url = '/batch/debit-card/push-funds';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: {},
        body: JSON.stringify(params),
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<BulkResponse>(url, data, possibleResTypes);
    },
    postBulkCreditCardCollection: async (params, source) => {
      const url = '/batch/credit-card/charge';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: {},
        body: JSON.stringify(params),
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<BulkResponse>(url, data, possibleResTypes);
    },
    postBulkACHCollection: async (params, source) => {
      const url = '/batch/ach/fund';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: {},
        body: JSON.stringify(params),
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<BulkResponse>(url, data, possibleResTypes);
    },
    postBulkACHPayment: async (params, source) => {
      const url = '/batch/ach/withdraw';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: {},
        body: JSON.stringify(params),
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<BulkResponse>(url, data, possibleResTypes);
    },
    PostBulkRTPPayment: async (params, source) => {
      const url = '/batch/rtp/withdraw';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: {},
        body: JSON.stringify(params),
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<BulkResponse>(url, data, possibleResTypes);
    },
    postBulkFedwireCollection: async (params, source) => {
      const url = '/batch/fedwire/fund';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: {},
        body: JSON.stringify(params),
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<BulkResponse>(url, data, possibleResTypes);
    },
    postBulkFedwirePayment: async (params, source) => {
      const url = '/batch/fedwire/withdraw';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: {},
        body: JSON.stringify(params),
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<BulkResponse>(url, data, possibleResTypes);
    },
    postBulkClientAccountTransfer: async (params, source) => {
      const url = '/batch/client-account/transfer';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: {},
        body: JSON.stringify(params),
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<BulkResponse>(url, data, possibleResTypes);
    },
    fetchUploadedFiles: async (params, source) => {
      const url = '/batch';

      const { data } = await http.get<unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      const res = verifyResponseType<UploadBatchFileApiResponse>(url, data, possibleResTypes);
      return {
        ...res,
        Batches: Object.values(res.Batches || {}),
      };
    },
    fetchUploadedFileDetails: async (params, source) => {
      const url = '/batch/details';

      const { data } = await http.get<unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<UploadBatchFileDetailseResponse>(url, data, possibleResTypes);
    },
    postCancelScheduledWithdraw: async (params, source) => {
      const url = '/eft/withdraw/schedule/cancel';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<CancelScheduledWithdrawResponse>(url, data, possibleResTypes);
    },
    postCancelScheduledFund: async (params, source) => {
      const url = '/eft/fund/schedule/cancel';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<CancelScheduledFundResponse>(url, data, possibleResTypes);
    },
    fetchClientAccountLabels: async (params, source) => {
      const url = '/account/client-accounts/label';

      const { data } = await http.get<unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      const res = verifyResponseType<FetchLabelsApiResponse>(url, data, possibleResTypes);

      return {
        ...res,
        Labels: Object.values(res.Labels || {}),
      };
    },
    addClientAccountLabel: async (params, source) => {
      const url = '/account/client-accounts/label/add';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<AddLabelResponse>(url, data, possibleResTypes);
    },
    deleteClientAccountLabel: async (params, source) => {
      const url = '/account/client-accounts/label/delete';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<DeleteLabelResponse>(url, data, possibleResTypes);
    },
    fetchContactLabels: async (params, source) => {
      const url = '/contacts/label';

      const { data } = await http.get<unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      const res = verifyResponseType<FetchLabelsApiResponse>(url, data, possibleResTypes);

      return {
        ...res,
        Labels: Object.values(res.Labels || {}),
      };
    },
    addContactLabel: async (params, source) => {
      const url = '/contacts/label/add';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<AddLabelResponse>(url, data, possibleResTypes);
    },
    deleteContactLabel: async (params, source) => {
      const url = '/contacts/label/delete';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<DeleteLabelResponse>(url, data, possibleResTypes);
    },
    postEditScheduledWithdraw: async (params, source) => {
      const url = '/eft/withdraw/schedule/edit';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    postEditScheduledFund: async (params, source) => {
      const url = '/eft/fund/schedule/edit';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    fetchTransactionCounts: async (params, source) => {
      const url = 'account/transaction/counts';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<GetTransactionCountsResponse>(url, data, possibleResTypes);
    },
    postConfirmTransaction: async (params, source) => {
      const url = '/account/transaction/confirm';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    postSendLegalAgreement: async (bearer, source) => {
      const url = '/vopay/onboarding-app/legal-agreement';
      const { data } = await http.post<Record<string, never>, unknown>({
        url,
        config: {},
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },

    template: async (params, source) => {
      const url = '/template';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<unknown>(url, data, possibleResTypes);
    },

    fetchPartners: async (source) => {
      const url = '/partner/list';

      const { data } = await http.get<unknown>({
        url,
        config: {},
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<PartnersResponse>(url, data, possibleResTypes);
    },

    fetchUserLists: async (params, source) => {
      const url = '/partner/user-list';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<UserListsResponse>(url, data, possibleResTypes);
    },
    fetchPartnerAccountTransactions: async (params, source) => {
      const url = '/partner/account/transactions';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    fetchHubSpotDropdownData: async (source) => {
      const url = '/partner/account/hubspot/dropdown/data';

      const { data } = await http.get<Record<never, never>, unknown>({
        url,
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.HubSpotDropdownDataResponse];
      return verifyResponseType<HubSpotDropdownDataResponse>(url, data, possibleResTypes);
    },
    fetchHubSpotDealData: async (params, source) => {
      const url = '/partner/account/hubspot/deal/id';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.HubSpotDealDetailsResponse];
      return verifyResponseType<HubSpotDealDetailsResponse>(url, data, possibleResTypes);
    },
    postAccountBillingSavePaymentInformation: async (params, source, bearer) => {
      const url = '/vopay/onboarding-app/account-billing/save-payment-information';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        bearer,
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.AccountBillingSavePaymentInformationResponse];
      return verifyResponseType<AccountBillingSavePaymentInformationResponse>(url, data, possibleResTypes);
    },
    fetchAccountBillingDetails: async (params, source, bearer) => {
      const url = '/vopay/onboarding-app/account-billing';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        bearer,
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.AccountBillingDetailsResponse];
      return verifyResponseType<AccountBillingDetailsResponse>(url, data, possibleResTypes);
    },
    postAccountBillingDetails: async (params, source, bearer) => {
      const url = '/vopay/onboarding-app/account-billing';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        bearer,
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    postAccountBillingForDisconnection: async (params, source, bearer) => {
      const url = '/vopay/onboarding-app/account-billing/disconnect-account';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        bearer,
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    fetchAccountDocument: async (params, source, bearer) => {
      const url = '/document/file/content';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        bearer,
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.AccountDocumentResponse];
      return verifyResponseType<AccountDocumentResponse>(url, data, possibleResTypes);
    },
    postHubSpotDealUpdate: async (params, source?) => {
      const url = '/partner/account/hubspot/deal/update';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    postAccountDocumentUpload: async (params, bearer, source?) => {
      const url = '/document';

      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        bearer,
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.DocumentUploadResponse];

      return verifyResponseType<DocumentUploadResponse>(url, data, possibleResTypes);
    },

    postBankAccountDocumentUpload: async (params, bearer, source?) => {
      const url = '/vopay/onboarding-app/bank-account/document';

      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        bearer,
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.DocumentUploadResponse];

      return verifyResponseType<DocumentUploadResponse>(url, data, possibleResTypes);
    },
    postBankAccountDocumentDelete: async (params, bearer, source?) => {
      const url = '/vopay/onboarding-app/bank-account/document/delete';

      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        bearer,
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    postPartnerAccountTransfer: async (params, source) => {
      const url = '/partner/account/transfer';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<PartnerAccountTransferResponse>(url, data, possibleResTypes);
    },
    postPartnerAccountFundAndTransfer: async (params, source) => {
      const url = '/partner/account/fund-transfer';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<PartnerAccountFundAndTransferResponse>(url, data, possibleResTypes);
    },
    postPartnerAccountTransferAndWithdraw: async (params, source) => {
      const url = '/partner/account/transfer-withdraw';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<PartnerAccountTransferAndWithdrawResponse>(url, data, possibleResTypes);
    },
    postPartnerAccountFundTransferAndWithdraw: async (params, source) => {
      const url = '/partner/account/fund-transfer-withdraw';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<PartnerAccountFundTransferAndWithdrawResponse>(url, data, possibleResTypes);
    },
    postRenewAccount: async (params, source) => {
      const url = '/account/renew';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },

    fetchTicketCategories: async (source) => {
      const url = '/partner/get-ticket-categories';

      const { data } = await http.get<Record<never, never>, unknown>({
        url,
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<TicketCategoriesResponse>(url, data, possibleResTypes);

      return {
        ...res,
        SupportTicketCategory: Object.values(res.SupportTicketCategory || {}),
        SupportTicketSubCategory: Object.values(res.SupportTicketSubCategory || {}),
      };
    },

    fetchPortalVideos: async (params, source) => {
      const url = '/media?media_type=video';

      const formattedParams = { ...params, IsPartner: true };
      const { data } = await learningHttp.get<Record<never, never>, unknown>({
        url,
        config: { params: formattedParams },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.PortalVideoResponse];
      return verifyResponseType<PortalVideoResponse>(url, data, possibleResTypes);
    },

    fetchClientDashboardTicketCategories: async (source) => {
      const url = '/account/get-ticket-categories';

      const { data } = await http.get<Record<never, never>, unknown>({
        url,
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<TicketCategoriesResponse>(url, data, possibleResTypes);

      return {
        ...res,
        SupportTicketCategory: Object.values(res.SupportTicketCategory || {}),
        SupportTicketSubCategory: Object.values(res.SupportTicketSubCategory || {}),
      };
    },

    fetchDefaultPaymentMethod: async (params, source) => {
      const url = '/account/default-payment-method';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<DefaultPaymentMethodResponse>(url, data, possibleResTypes);
    },

    postSetDefaultPaymentMethod: async (params, source, bearer) => {
      const url = '/account/set-default-payment-method';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: {},
        body: JSON.stringify(params),
        bearer,
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    postCreateClientAccountIndividual: async (params, source) => {
      const url = '/account/client-accounts/individual';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<CreateClientAccountResponse>(url, data, possibleResTypes);
    },
    postCreateClientAccountBusiness: async (params, source) => {
      const url = '/account/client-accounts/business';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<CreateClientAccountResponse>(url, data, possibleResTypes);
    },
    createClientAccountReceiveOnly: async (params, source) => {
      const url = '/account/client-accounts/receive-only';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<CreateClientAccountResponse>(url, data, possibleResTypes);
    },
    createClientAccountStandard: async (params, source) => {
      const url = '/account/client-accounts/standard';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<CreateClientAccountResponse>(url, data, possibleResTypes);
    },

    postEditClientAccount: async (params, source) => {
      const url = '/account/client-accounts/edit';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },

    fetchTicketDetailsClient: async (params, source) => {
      const url = '/account/ticket-detail';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<TicketDetailsResponse>(url, data, possibleResTypes);
    },
    fetchTicketsClient: async (params, source) => {
      const url = '/account/tickets';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      const res = verifyResponseType<TicketsApiResponse>(url, data, possibleResTypes);

      return {
        ...res,
        Tickets: Object.values(res.Tickets),
      };
    },
    submitClientSupportTicket: async (params, source) => {
      const url = '/account/submit-ticket';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: {},
        body: JSON.stringify(params),
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<SubmitTicketResponse>(url, data, possibleResTypes);
    },
    postSubmitTicketCommentClient: async (params, source) => {
      const url = '/account/submit-ticket-comment';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: {},
        body: JSON.stringify(params),
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    postSubmitTicketAttachment: async (params, source) => {
      const url = '/partner/ticket-attachment';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: {},
        body: JSON.stringify(params),
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<TicketAttachmentResponse>(url, data, possibleResTypes);
    },
    postSubmitTicketAttachmentClient: async (params, source) => {
      const url = '/account/ticket-attachment';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: {},
        body: JSON.stringify(params),
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<TicketAttachmentResponse>(url, data, possibleResTypes);
    },
    fetchWebhooks: async (params, source) => {
      const url = '/account/webhooks';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<WebhooksResponse>(url, data, possibleResTypes);
    },

    fetchPartnerWebhooks: async (params, source) => {
      const url = '/partner/webhooks';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<WebhooksResponse>(url, data, possibleResTypes);
    },
    fetchWebhookInfo: async (params, source) => {
      const url = '/account/webhook-url/info';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<WebhookResponse>(url, data, possibleResTypes);
    },
    fetchPartnerWebhook: async (params, source) => {
      const url = '/partner/webhook-url/info';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<WebhookResponse>(url, data, possibleResTypes);
    },
    fetchTestWebhook: async (params, source) => {
      const url = '/account/webhook-url/test';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    fetchPartnerTestWebhook: async (params, source) => {
      const url = '/partner/webhook-url/test';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    postPartnerUpdateWebhook: async (params, source) => {
      const url = '/partner/webhook-url';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    postUpdateWebhook: async (params, source) => {
      const url = '/account/webhook-url';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    postResendWebhook: async (params, source) => {
      const url = '/account/webhook/resend';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    postPartnerResendWebhook: async (params, source) => {
      const url = '/partner/webhook/resend';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    postSetPlaidCredentials: async (params, source) => {
      const url = '/account/set-plaid-credentials';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    postSetFlinksCredentials: async (params, source) => {
      const url = '/account/set-flinks-credentials';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    postSetInveriteCredentials: async (params, source) => {
      const url = '/account/set-inverite-credentials';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    postSetGlobalPayCredentials: async (params, source) => {
      const url = '/account/set-global-pay-credentials';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    postSaveAuthorizedIPs: async (params, source) => {
      const url = '/account/authorized-ips';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    fetchAuthorizedIPs: async (params, source) => {
      const url = '/account/authorized-ips';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<AuthorizedIPsResponse>(url, data, possibleResTypes);
    },
    fetchELinxHistory: async (params, source) => {
      const url = '/elinx';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<ELinxHistoryResponse>(url, data, possibleResTypes);
    },
    postPaylinkCancel: async (params, source) => {
      const url = '/elinx/cancel';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    fetchPaylinkSettings: async (params, source) => {
      const url = '/elinx/settings';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<ELinxSettingsResponse>(url, data, possibleResTypes);
    },
    postPaylinkSettingsSetSendeeEmail: async (params, source) => {
      const url = '/elinx/settings/setup-email-notifications';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    postPaylinkSettingsSetSenderEmail: async (params, source) => {
      const url = '/elinx/settings/setup-sender-email';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    postPaylinkSettingsSetTokenExpiration: async (params, source) => {
      const url = '/elinx/settings/setup-token-expiration';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },

    postPaylinkSettingsUpdate: async (params, source) => {
      const url = '/elinx/update-settings';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },

    postPaylinkSettingsSetDecline: async (params, source) => {
      const url = '/elinx/update-settings';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    postDeleteInteracEmail: async (params, source) => {
      const url = '/interac/email/delete';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    postAddInteracEmail: async (params, source, bearer) => {
      const url = '/interac/email/add';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<InteracAddEmailResponse>(url, data, possibleResTypes);
    },
    postLinkInteracEmail: async (params, source) => {
      const url = '/interac/email/link-email';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<InteracLinkEmailResponse>(url, data, possibleResTypes);
    },
    postIQ11Tokenize: async (params, source) => {
      const url = '/iq11/tokenize';
      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<IQ11TokenizeResponse>(url, data, possibleResTypes);
    },
    fetchiQ11AccessTokenInfo: async (params, source) => {
      const url = '/iq11/access-token';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<AccessTokenInfoResponse>(url, data, possibleResTypes);
    },
    postLinkPushToCardRequest: async (params, source) => {
      const url = '/debit-card/link-card-request';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<PushToCardMapResponse>(url, data, possibleResTypes);
    },
    postOnboardingToken: async (params, source) => {
      const url = '/vopay/onboarding-app/token';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<OnboardingTokenResponse>(url, data, possibleResTypes);
    },
    postOnboardingAppToken: async (params, source) => {
      const url = '/vopay/onboarding-app/token';
      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<OnboardingAppTokenResponse>(url, data, possibleResTypes);
    },
    getAssignedBillingPackage: async (params, source, bearer) => {
      const url = '/account/assigned-billing-package';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        bearer,
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<AssignedBillingPackagesResponse>(url, data, possibleResTypes);
    },
    getAccountTransactionTypes: async (params, source, bearer) => {
      const url = '/account/transactions/types';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        bearer,
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.AccountTransactionTypesResponse];
      return verifyResponseType<AccountTransactionTypesResponse>(url, data, possibleResTypes);
    },
    fetchSubscriptions: async (params, source) => {
      const url = '/report-subscription';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<FetchSubscriptionListResponse>(url, data, possibleResTypes);

      return {
        ...res,
        Subscriptions: Object.values(res.Subscriptions),
      };
    },
    fetchSubscriptionDetail: async (params, source) => {
      const url = '/report-subscription/detail';
      const { data } = await http.get<typeof params, ReportDetailApiResponse>({
        url,
        config: { params },
        source,
      });
      const response: ReportDetailResponse = {
        ...data,
        AccountSubscriptionDetail: {
          ...data.AccountSubscriptionDetail,
          IncludeSubAccounts: data.AccountSubscriptionDetail.IncludeSubAccounts === '1',
          IncludeGraph: data.AccountSubscriptionDetail.IncludeGraph === '1',
          FilterParams: {
            TransactionReportFailureReasons: Object.values(
              data.AccountSubscriptionDetail.FilterParams.TransactionReportFailureReasons || {},
            ),
          },
        },
      };
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<ReportDetailResponse>(url, response, possibleResTypes);
    },
    postEditReport: async (params, source) => {
      const url = '/report-subscription/edit';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    postCreateReport: async (params, source) => {
      const url = '/report-subscription';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<CreateReportsResponse>(url, data, possibleResTypes);
    },
    postDownloadReport: async (params, source) => {
      const url = '/report/download';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<DownloadReportsResponse>(url, data, possibleResTypes);
    },
    fetchAccountInvoicePdf: async (params, source) => {
      const url = '/account/invoice-pdf';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<InvoicePdfResponse>(url, data, possibleResTypes);
    },
    fetchAccountStatementPDF: async (params, source) => {
      const url = '/account/statement-pdf';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<StatementPDFResponse>(url, data, possibleResTypes);
    },
    fetchAccountStatementCsv: async (params, source) => {
      const url = '/account/statement-csv';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<StatementCsvResponse>(url, data, possibleResTypes);
    },
    fetchAccountInvoices: async (params, source) => {
      const url = '/account/invoices';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<AccountInvoicesResponse>(url, data, possibleResTypes);
    },
    postInteracVerification: async (params, source) => {
      const url = '/account-verification/interac';
      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<InteractVerificationResponse>(url, data, possibleResTypes);
    },
    postAccountVerificationInitiate: async (params, source) => {
      const url = '/account-verification/bank-account/initiate';
      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<AccountVerificationInitiateResponse>(url, data, possibleResTypes);
    },

    postAccountVerificationConfirm: async (params, source) => {
      const url = '/account-verification/bank-account/confirm';
      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<AccountVerificationConfirmResponse>(url, data, possibleResTypes);
    },
    fetchDebitAuthority: async (params, source) => {
      const url = '/account/debit-authority-agreement';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<GetDebitAuthorityAgreementResponse>(url, data, possibleResTypes);
    },
    postDebitAuthority: async (params, source) => {
      const url = '/account/debit-authority-agreement';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    fetchAccountBranding: async (params, source, bearer) => {
      const url = '/account/branding-details';
      const { data } = await http.get<typeof params, AccountBrandingApiResponse>({
        url,
        config: { params },
        bearer,
        source,
      });

      return {
        ...data,
        AppTitle: data.PageTitle,
      };
    },
    fetchPartnerBranding: async (params, source) => {
      const url = '/partner/branding-details';
      const { data } = await http.get<typeof params, AccountBrandingApiResponse>({
        url,
        config: { params },
        source,
      });

      return {
        ...data,
        AppTitle: data.PageTitle,
      };
    },
    fetchPartnerAccountPermissionFields: async (params, source) => {
      const url = '/partner/account/permissions';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<PartnerAccountPermissionFieldsResponse>(url, data, possibleResTypes);
    },
    postAccountBranding: async (params, source) => {
      const url = '/account/branding';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: {},
        body: JSON.stringify(params),
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    postPartnerBranding: async (params, source) => {
      const url = '/partner/branding';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: {},
        body: JSON.stringify(params),
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    fetchLoginPageBranding: async (params, source) => {
      const url = '/portal/login-branding-details';
      const { data } = await http.get<typeof params, LoginBrandingApiResponse>({
        url,
        config: { params },
        source,
      });

      return {
        ...data,
        AppTitle: data.PageTitle,
      };
    },
    fetchFAQs: async (source) => {
      const url = '/faq';
      const { data } = await http.get<unknown>({
        url,
        config: {},
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<FAQResponse>(url, data, possibleResTypes);
    },
    fetchFAQAllQuestions: async (source) => {
      const url = '/faq-questions';
      const { data } = await http.get<unknown>({
        url,
        config: {},
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<FAQAllQuestionsResponse>(url, data, possibleResTypes);
    },
    postFAQUpdateClickCount: async (params, source) => {
      const url = 'faq/click-count';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<FAQResponse>(url, data, possibleResTypes);
    },
    fetchFAQCategories: async (source) => {
      const url = '/faq-categories';
      const { data } = await http.get<unknown>({
        url,
        config: {},
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<FAQCategoriesResponse>(url, data, possibleResTypes);
    },
    fetchApprovalTransactions: async (
      params: ApprovalInboxTransactionsRequest,
      source?: CancelTokenSource,
    ): Promise<ApprovalInboxTransactionsResponse> => {
      const url = '/transaction/approval/transactions';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<ApprovalInboxTransactionsResponse>(url, data, possibleResTypes);
    },
    fetchApprovalTransactionsInbox: async (
      params: ApprovalInboxTransactionsRequest,
      source?: CancelTokenSource,
    ): Promise<ApprovalInboxTransactionsResponse> => {
      const url = '/transaction/approval/transaction/inbox';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<ApprovalInboxTransactionsResponse>(url, data, possibleResTypes);
    },
    postVerifyApprovalTransation: async (
      params: VerifyTransactionApproveRequest,
      source?: CancelTokenSource,
    ): Promise<ApiResponse> => {
      const url = '/qa/transaction/approve';
      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<VerifyTrasnsactionApproveResponse>(url, data, possibleResTypes);
    },
    postVerifyMarkAsProceedTransaction: async (
      params: VerifyTransactionApproveRequest,
      source?: CancelTokenSource,
    ): Promise<ApiResponse> => {
      const url = '/qa/transaction/markAsProcessed';
      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<VerifyTrasnsactionApproveResponse>(url, data, possibleResTypes);
    },
    postApproveApprovalTransation: async (
      params: InboxStatusChageRequest,
      source?: CancelTokenSource,
    ): Promise<ApiResponse> => {
      const url = '/transaction/approval/transaction/approve';
      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    postUnapproveApprovalTransation: async (
      params: InboxStatusChageRequest,
      source?: CancelTokenSource,
    ): Promise<ApiResponse> => {
      const url = '/transaction/approval/transaction/unapprove';
      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    postRejectApprovalTransation: async (
      params: InboxStatusChageRequest,
      source?: CancelTokenSource,
    ): Promise<ApiResponse> => {
      const url = '/transaction/approval/transaction/reject';
      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    fetchUserRoles: async (params: UserRolesRequest, source?: CancelTokenSource): Promise<UserRolesResponse> => {
      const url = '/transaction/approval/user/roles';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<UserRolesResponse>(url, data, possibleResTypes);
    },
    postCreateUserRole: async (params: UpdateUserRoleRequest, source?: CancelTokenSource): Promise<ApiResponse> => {
      const url = '/transaction/approval/user/role';
      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    postDeleteUserRole: async (params: UpdateUserRoleRequest, source?: CancelTokenSource): Promise<ApiResponse> => {
      const url = '/transaction/approval/user/role/delete';
      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    postCreateUserRolePermission: async (
      params: UpdateUserRolePermissionRequest,
      source?: CancelTokenSource,
    ): Promise<ApiResponse> => {
      const url = '/transaction/approval/user/role/permission';
      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    postUpdateUserRolePermission: async (
      params: UpdateUserRolePermissionRequest,
      source?: CancelTokenSource,
    ): Promise<ApiResponse> => {
      const url = '/transaction/approval/user/role/permission/update';
      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    postDeleteUserRolePermission: async (
      params: UpdateUserRolePermissionRequest,
      source?: CancelTokenSource,
    ): Promise<ApiResponse> => {
      const url = '/transaction/approval/user/role/permission/delete';
      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    fetchWorkflows: async (params: PortalUser, source?: CancelTokenSource): Promise<WorkFlowsResponse> => {
      const url = '/transaction/approval/workflows';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<WorkFlowsResponse>(url, data, possibleResTypes);
    },
    postCreateWorkflow: async (params: WorkflowCreateRequest, source?: CancelTokenSource): Promise<ApiResponse> => {
      const url = '/transaction/approval/workflow';
      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    postUpdateWorkflow: async (params: WorkflowUpdateRequest, source?: CancelTokenSource): Promise<ApiResponse> => {
      const url = '/transaction/approval/workflow/update';
      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    fetchTransactionTypes: async (
      params: TransactionTypesRequst,
      source?: CancelTokenSource,
    ): Promise<TransactionTypesResponse> => {
      const url = '/account/transactions/types';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<TransactionTypesResponse>(url, data, possibleResTypes);
    },
    fetchRailzBusinessAccount: async (params, source) => {
      const url = '/railz/business-accounts';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<RailzBusinessAccountResponse>(url, data, possibleResTypes);
    },
    fetchRailzAccount: async (params, source) => {
      const url = '/railz/account';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<ChartOfAccountResponse>(url, data, possibleResTypes);
    },
    fetchRailzChartOfAccounts: async (params, source) => {
      const url = '/railz/account/railz-accounts';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<ChartOfAccountsResponse>(url, data, possibleResTypes);
    },
    postSetRailzChartOfAccount: async (params, source) => {
      const url = '/railz/account/railz-accounts';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },

    fetchRailzBills: async (params, source) => {
      const url = '/railz/account/railz-bills';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<RailzBillsApiResponse>(url, data, possibleResTypes);
      return {
        ...res,
        Bills: Object.values(res.Bills || {}).map((bill) => ({
          ...bill,
          PaymentDetails: Object.values(bill.PaymentDetails || {}),
        })),
      };
    },
    postBills: async (params, source) => {
      const url = '/railz/account/railz-bills';
      const parseParams = {
        ...params,
        Bills: JSON.stringify(
          params.Bills.map((bill) => ({
            ...bill,
            PaymentDetails: arrayToObject(bill.PaymentDetails),
          })),
        ),
      };
      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(parseParams),
        config: {},
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },

    fetchRailzInvoices: async (params, source) => {
      const url = '/railz/account/railz-invoices';
      const { data } = await http.get<typeof params, Record<string, unknown>>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<RailzInvoicesApiResponse>(url, data, possibleResTypes);
      return {
        ...res,
        Invoices: Object.values(res.Invoices || {}).map((invoice) => ({
          ...invoice,
          PaymentDetails: Object.values(invoice.PaymentDetails || {}),
        })),
      };
    },
    postInvoices: async (params, source) => {
      const url = '/railz/account/railz-invoices';
      const parseParams = {
        ...params,
        Invoices: JSON.stringify(
          params.Invoices.map((invoice) => ({
            ...invoice,
            PaymentDetails: arrayToObject(invoice.PaymentDetails),
          })),
        ),
      };

      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(parseParams),
        config: {},
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    postRailzSync: async (params, source) => {
      const url = '/railz/data/sync';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    postCancelInvoice: async (params, source) => {
      const url = '/railz/account/railz-invoices/cancel';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    postCancelBill: async (params, source) => {
      const url = '/railz/account/railz-bills/cancel';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },

    fetchRailzSyncStatus: async (params, source) => {
      const url = '/railz/data/sync/status';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<RailzStatusSyncData>(url, data, possibleResTypes);
    },
    fetchPortalDocument: async (source) => {
      const url = '/media?media_type=pdf';
      const { data } = await learningHttp.get<Record<never, never>, unknown>({
        url,
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.PortalDocumentResponse];
      return verifyResponseType<PortalDocumentResponse>(url, data, possibleResTypes);
    },
    postFundMyAccountSchedule: async (params, source) => {
      const url = '/account/fund-my-account/schedule';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<MyAccountScheduleResponse>(url, data, possibleResTypes);
    },
    postWithdrawMyAccountSchedule: async (params, source) => {
      const url = '/account/withdraw-my-account/schedule';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<MyAccountScheduleResponse>(url, data, possibleResTypes);
    },
    fetchAutoBalanceTransferReport: async (params, source) => {
      const url = '/account/auto-balance-transfer/report';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<AutoBalanceTransferReportResponse>(url, data, possibleResTypes);
    },
    fetchRailzBillCreditNotes: async (params, source) => {
      const url = '/railz/account/railz-bills/credit-notes';
      const { data } = await http.get<typeof params, CreditNotesApiResponse>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<CreditNotesApiResponse>(url, data, possibleResTypes);
      return {
        ...res,
        CreditNotes: Object.values(res.CreditNotes || {}),
      };
    },
    fetchRailzInvoiceCreditNotes: async (params, source) => {
      const url = '/railz/account/railz-invoices/credit-notes';
      const { data } = await http.get<typeof params, CreditNotesApiResponse>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<CreditNotesApiResponse>(url, data, possibleResTypes);
      return {
        ...res,
        CreditNotes: Object.values(res.CreditNotes || {}),
      };
    },
    fetchContacts: async (params = {}, source) => {
      const url = '/contacts';
      const { data } = await http.get<typeof params, ContactsApiResponse>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<ContactsApiResponse>(url, data, possibleResTypes);

      return {
        ...res,
        Contacts: Object.values(data.Contacts || {}).map((contact) => ({
          ...contact,
          IsActive: toBool(contact.IsActive),
          Labels: contact.Labels.split(',').filter((label) => label !== ''),
        })),
      };
    },

    fetchRecentGlobalSendTransactions: async (params, source) => {
      const url = '/gcm/withdraw/transactions';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<GlobalSendRecentTransactionsApiResponse>(url, data, possibleResTypes);
      return {
        ...res,
        Transactions: Object.values(res.Transactions || {}).map((transaction) => ({
          ...transaction,
          FeesIncluded: toBool(transaction.FeesIncluded),
        })),
      };
    },

    postCrossBorderUrl: async (params, source, bearer) => {
      const url = '/gcm/generate-embed-url';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<CrossBorderUrlResponse>(url, data, possibleResTypes);
    },
    fetchCrossBorderUrlInfo: async (params, source) => {
      const url = '/portal/retrieve-single-use-token';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: {
          params: {
            Token: params.Token,
            ApplicationType: 'crossborder',
          },
        },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      const res = verifyResponseType<CrossBorderUrlApiInfoResponse>(url, data, possibleResTypes);

      return {
        ...res,
        ClientAccountsEnabled: toBool(res.ClientAccountsEnabled),
        FundingOptions: Object.values(res.FundingOptions || {}),
      };
    },
    fetchGlobalSendAdminUrlInfo: async (params, source) => {
      const url = '/portal/retrieve-single-use-token';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: {
          params: {
            Token: params.Token,
            ApplicationType: 'crossborder-admin',
          },
        },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<GlobalSendAdminUrlInfoResponse>(url, data, possibleResTypes);

      return res;
    },
    generateEmbedUrlDisputeManagement: async (params, source) => {
      const url = '/dispute-management/generate-embed-url';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<GenerateEmbedUrlDisputeManagementResponse>(url, data, possibleResTypes);

      return res;
    },
    fetchDisputeManagementUrlInfo: async (params, source) => {
      const url = '/portal/retrieve-single-use-token';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: {
          params: {
            Token: params.Token,
            ApplicationType: 'dispute-management',
          },
        },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<DisputeManagementUrlInfoResponse>(url, data, possibleResTypes);

      return {
        ...res,
        FeatureList: Object.values(res.FeatureList || {}),
      };
    },
    fetchBillPayInfo: async (params, source) => {
      const url = '/portal/retrieve-single-use-token';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: {
          params: {
            Token: params.Token,
            ApplicationType: 'bill-pay',
          },
        },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<BillPayInfoResponse>(url, data, possibleResTypes);
    },

    fetchCrossBorderCountries: async (params, source) => {
      const url = '/gcm/countries';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<CrossBorderCountriesApiResponse>(url, data, possibleResTypes);
      return {
        ...res,
        Countries: Object.values(res.Countries || {}),
      };
    },
    fetchDeliveryChannelsFields: async (params, source) => {
      const url = '/gcm/delivery-channels/fields';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<DeliveryChannelsFieldsApiResponse>(url, data, possibleResTypes);

      return {
        ...res,
        DeliveryChannelFields: Object.values(res.DeliveryChannelFields || {}).map((field) => ({
          ...field,
          GroupOrder: Number(field.GroupOrder),
        })),
      };
    },
    fetchDeliveryChannelsFieldsData: async (params, source?) => {
      const url = '/gcm/delivery-channels/fields/data';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<DeliveryChannelsFieldsDataResponse>(url, data, possibleResTypes);
    },
    fetchDeliveryChannels: async (params, source) => {
      const url = '/gcm/delivery-channels';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<DeliveryChannelsApiResponse>(url, data, possibleResTypes);
      return {
        ...res,
        DeliveryChannels: Object.values(res.DeliveryChannels || {}),
      };
    },
    postCrossBorderQuotes: async (params, source) => {
      const url = '/gcm/withdraw/quote';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<CrossBorderQuotesResponse>(url, data, possibleResTypes);
    },
    fetchCrossBorderRecipientContacts: async (params, source) => {
      const url = '/gcm/recipient-contacts';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<CrossBorderRecipientContactsApiResponse>(url, data, possibleResTypes);
      return {
        ...res,
        RecipientContacts: Object.values(res.RecipientContacts || {}).map((contact) => ({
          ...contact,
          IsFavorite: toBool(contact.IsFavorite),
        })),
      };
    },
    postAddFavoriteRecipientContact: async (params, source) => {
      const url = '/gcm/recipient-contacts/favorites/add';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<AddFavoriteRecipientContactResponse>(url, data, possibleResTypes);
    },
    postDeleteFavoriteRecipientContact: async (params, source) => {
      const url = '/gcm/recipient-contacts/favorites/delete';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<DeleteFavoriteRecipientContactResponse>(url, data, possibleResTypes);
    },
    fetchRecipientContactDeliveryMethods: async (params, source) => {
      const url = '/gcm/delivery-methods';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<RecipientContactDeliveryMethodsApiResponse>(url, data, possibleResTypes);

      return {
        ...res,
        DeliveryMethods: Object.values(res.DeliveryMethods || {}),
      };
    },
    fetchCrossBorderBankCities: async (params, source) => {
      const url = '/gcm/banks/cities';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<CrossBorderBankCitiesApiResponse>(url, data, possibleResTypes);
      return {
        ...res,
        BankCities: Object.values(res.BankCities || {}),
      };
    },
    fetchCrossBorderBankBranches: async (params, source) => {
      const url = '/gcm/banks/branches';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<CrossBorderBankBranchesApiResponse>(url, data, possibleResTypes);
      return {
        ...res,
        BankBranches: Object.values(res.BankBranches || {}),
      };
    },
    fetchCrossBorderBankNames: async (params, source) => {
      const url = '/gcm/banks/names';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<CrossBorderBankNamesApiResponse>(url, data, possibleResTypes);
      return {
        ...res,
        BankNames: Object.values(res.BankNames || {}),
      };
    },
    fetchCrossBorderBankLocations: async (params, source) => {
      const url = '/gcm/banks/locations';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<CrossBorderBankLocationsApiResponse>(url, data, possibleResTypes);
      return {
        ...res,
        BankLocations: Object.values(res.BankLocations || {}),
      };
    },
    postCrossBorderAddDeliveryMethod: async (params, source) => {
      const url = '/gcm/delivery-methods/add';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<CrossBorderAddDeliveryMethodResponse>(url, data, possibleResTypes);
    },
    fetchGovernmentIdentifier: async (params, source) => {
      const url = '/gcm/government-identifier';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<GovernmentIdentifierApiResponse>(url, data, possibleResTypes);
      return {
        ...res,
        GovernmentIdentifiers: Object.values(res.GovernmentIdentifiers || {}),
      };
    },
    fetchGovernmentIdentifierSchemas: async (params, source?) => {
      const url = '/gcm/government-identifier/schemas';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<GovernmentIdentifierSchemasResponse>(url, data, possibleResTypes);
    },
    postGovernmentIdentifier: async (params, source?) => {
      const url = '/gcm/government-identifier/add';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<AddGovernmentIdentifierResponse>(url, data, possibleResTypes);
    },
    postEditRecipient: async (params, source?) => {
      const url = '/gcm/recipient-contacts/edit';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<EditRecipientResponse>(url, data, possibleResTypes);
    },
    postAddRecipient: async (params, source?) => {
      const url = '/gcm/recipient-contacts/add';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<EditRecipientResponse>(url, data, possibleResTypes);
    },
    postCreateGlobalSendTransaction: async (params, source) => {
      const url = '/gcm/withdraw';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<CreateGlobalSendTransactionResponse>(url, data, possibleResTypes);
    },
    postEditContact: async (params, source) => {
      const url = '/contacts/edit';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    fetchBankSchema: async (params, source) => {
      const url = '/gcm/banks/schemas';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<BankSchemaApiResponse>(url, data, possibleResTypes);
      return {
        ...res,
        BanksSchemaValues: Object.values(res.BanksSchemaValues || {}),
      };
    },
    confirmEmailVerification: async (params, source?) => {
      const url = '/contact-verification/email/confirm';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<ContactVerificationConfirmResponse>(url, data, possibleResTypes);
    },
    confirmPhoneVerification: async (params, source?) => {
      const url = '/contact-verification/phone/confirm';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<ContactVerificationConfirmResponse>(url, data, possibleResTypes);
    },
    postGenerateOtp: async (params, source?) => {
      const url = '/elinx/generate-otp';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<GenerateOtpResponse>(url, data, possibleResTypes);
    },
    postValidateOtp: async (params, source?) => {
      const url = '/elinx/validate-otp';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    fetchElinxPaymentMethods: async (params, source, bearer) => {
      const url = '/elinx/payment-methods';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        bearer,
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<ElinxPaymentMethodsApiResponse>(url, data, possibleResTypes);
      return {
        ...res,
        PaymentMethods: Object.values(res.PaymentMethods || {}),
      };
    },
    postRequirePadAgreement: async (params, source, bearer) => {
      const url = '/elinx/requires-pad-agreement';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<RequirePadAgreementResponse>(url, data, possibleResTypes);
    },
    postAcceptAgreement: async (params, source) => {
      const url = '/account/accept-agreement';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    postInterestAccount: async (source?: CancelTokenSource): Promise<ApiResponse> => {
      const url = '/interest-account/activate';
      const { data } = await http.post<unknown, unknown>({
        url,
        config: {},
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    fetchInterestAccount: async (params, source?: CancelTokenSource): Promise<InterestAccountResponse> => {
      const url = '/interest-account';
      const { data } = await http.get<typeof params, InterestAccountApiResponse>({
        url,
        config: { params },
        source,
      });
      const response: InterestAccountResponse = {
        ...data,
        InterestAccounts: Object.values(data.InterestAccounts || {}),
      };
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<InterestAccountResponse>(url, response, possibleResTypes);
    },
    fetchInterestAccountDetail: async (params, source?: CancelTokenSource): Promise<InterestAccountDetailResponse> => {
      const url = '/interest-account/detail';
      const { data } = await http.get<typeof params, InterestAccountDetailApiResponse>({
        url,
        config: { params },
        source,
      });
      const response: InterestAccountDetailResponse = {
        ...data,
        InterestAccruedDetails: Object.values(data.InterestAccrued || {}),
      };
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<InterestAccountDetailResponse>(url, response, possibleResTypes);
    },
    postInterestAccountTransfer: async (
      params,
      source?: CancelTokenSource,
    ): Promise<InterestAccountTransferResponse> => {
      const url = '/interest-account/transfer';
      const { data } = await http.post<unknown, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<InterestAccountTransferResponse>(url, data, possibleResTypes);
    },
    fetchInterestAccountRates: async (params, source?: CancelTokenSource): Promise<InterestAccountRatesResponse> => {
      const url = '/interest-account/rates';
      const { data } = await http.get<typeof params, InterestAccountRatesApiResponse>({
        url,
        config: { params },
        source,
      });

      const response: InterestAccountRatesResponse = {
        ...data,
        InterestRates: Object.values(data.InterestRates || {}),
      };

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<InterestAccountRatesResponse>(url, response, possibleResTypes);
    },
    fetchInterestAccountAccruedInterest: async (
      params,
      source?: CancelTokenSource,
    ): Promise<InterestAccountAccruedInterestResponse> => {
      const url = '/interest-account/accrued-interest';
      const { data } = await http.get<typeof params, InterestAccountAccruedInterestApiResponse>({
        url,
        config: { params },
        source,
      });
      const response: InterestAccountAccruedInterestResponse = {
        ...data,
        AccruedInterestList: Object.values(data.AccruedInterest || {}),
      };
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<InterestAccountAccruedInterestResponse>(url, response, possibleResTypes);
    },
    fetchInterestAccountTransactions: async (
      params,
      source?: CancelTokenSource,
    ): Promise<InterestAccountTransactionsResponse> => {
      const url = '/interest-account/transactions';
      const { data } = await http.get<typeof params, InterestAccountTransactionsApiResponse>({
        url,
        config: { params },
        source,
      });
      const response: InterestAccountTransactionsResponse = {
        ...data,
        InterestAccountTransactions: Object.values(data.Transactions || {}),
      };
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<InterestAccountTransactionsResponse>(url, response, possibleResTypes);
    },
    postRecipientContactGenerateEmbedUrl: async (params, source) => {
      const url = '/gcm/recipient-contacts/generate-embed-url';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<RecipientContactsGenerateEmbedUrlResponse>(url, data, possibleResTypes);
    },
    fetchUserSettings: async (params, source?) => {
      const url = '/account/users/settings';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<UserSettingsResponse>(url, data, possibleResTypes);
    },
    postSubmitUserSettings: async (params, source) => {
      const url = '/account/users/settings';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<UserSettingsResponse>(url, data, possibleResTypes);
    },
    activateClientAccount: async (params, source) => {
      const url = '/account/client-accounts/activate';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<ActivateDeactiveteClientAccountResponse>(url, data, possibleResTypes);
    },
    deactivateClientAccount: async (params, source) => {
      const url = '/account/client-accounts/deactivate';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<ActivateDeactiveteClientAccountResponse>(url, data, possibleResTypes);
    },
    activateContact: async (params, source) => {
      const url = '/contacts/activate';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<ActivateDeactiveteContactResponse>(url, data, possibleResTypes);
    },
    deactivateContact: async (params, source) => {
      const url = '/contacts/deactivate';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<ActivateDeactiveteContactResponse>(url, data, possibleResTypes);
    },
    postRtpWithdraw: async (params, source) => {
      const url = '/rtp/withdraw';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<RtrWithdrawResponse>(url, data, possibleResTypes);
    },
    postFedwireWithdraw: async (params, source) => {
      const url = '/fedwire/withdraw';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<RtrWithdrawResponse>(url, data, possibleResTypes);
    },
    fetchAccountExtendedInfo: async (bearer, source) => {
      const url = '/account/submit-extended-info';
      const { data } = await http.get<unknown>({
        url,
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<AccountExtendedInfoResponse>(url, data, possibleResTypes);
    },
    postELinxSettingsSetNotification: async (params, source) => {
      const url = '/elinx/settings/setup-notification';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    postELinxSettingsSetReminder: async (params, source) => {
      const url = '/elinx/settings/setup-reminder';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    fetchWatchList: async (params, source?) => {
      const url = '/gcm/watchlist';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const resp = verifyResponseType<WatchListBaseResponse>(url, data, possibleResTypes);
      const response: WatchListResponse = {
        ...resp,
        Watchlist: Object.values(resp.Watchlist || {}),
      };
      return response;
    },

    addWatchList: async (params, source?) => {
      const url = '/gcm/watchlist/add';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    postEditInteracEmail: async (params, source, bearer) => {
      const url = '/interac/email/edit';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<InteracEditEmailResponse>(url, data, possibleResTypes);
    },

    addContact: async (params, source) => {
      const url = '/contacts/add';

      const { data } = await http.post<typeof params, AddContactResponse>({
        url,
        config: { params },
        source,
      });
      return data;
    },

    deleteWatchList: async (params, source?) => {
      const url = '/gcm/watchlist/delete';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },

    setWatchList: async (params, source?) => {
      const url = '/gcm/watchlist/set';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    fetchCurrencies: async (params, source?) => {
      const url = '/gcm/currencies';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const resp = verifyResponseType<CurrenciesApiResponse>(url, data, possibleResTypes);
      return {
        ...resp,
        Currencies: Object.values(resp.Currencies || {}),
      };
    },
    generateGCMEmbedUrl: async (params, source) => {
      const url = '/gcm/generate-embed-url';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<GenerateGCMEmbedUrlResponse>(url, data, possibleResTypes);
    },
    fetchGCMBalance: async (params, source) => {
      const url = '/gcm/balance';
      const { data } = await http.get<typeof params, GCMBalanceResponse>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<GCMBalanceResponse>(url, data, possibleResTypes);
    },
    fetchGCMRatesFeesComparison: async (params, source) => {
      const url = '/gcm/rates-fees-comparison';
      const { data } = await http.get<typeof params, GCMRatesFeesComparisonApiResponse>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<GCMRatesFeesComparisonApiResponse>(url, data, possibleResTypes);
      return {
        ...res,
        Results: Object.values(res.Results || {}),
      };
    },
    railzCreateInvoice: async (params, source) => {
      const url = '/railz/account/railz-invoices/create';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: {},
        body: JSON.stringify({
          ...params,
          Invoice: JSON.stringify(params.Invoice),
        }),
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    railzCustomers: async (params, source) => {
      const url = '/railz/account/railz-customers';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<RailzCustomerBaseApiResponse>(url, data, possibleResTypes);
      return {
        ...res,
        Customers: Object.values(res.Customers || {}),
      };
    },
    railzInventory: async (params, source) => {
      const url = '/railz/account/railz-inventory';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<RailzInventoryBaseApiResponse>(url, data, possibleResTypes);
      return {
        ...res,
        Inventory: Object.values(res.Inventory || {}).map((inventory) => {
          return {
            ...inventory,
            quantityOnHand: Object.values(inventory.quantityOnHand || {}),
          };
        }),
      };
    },
    railzTaxRates: async (params, source) => {
      const url = '/railz/account/railz-tax-rates';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<RailzTaxRatesBaseApiResponse>(url, data, possibleResTypes);
      return {
        ...res,
        TaxRates: Object.values(res.TaxRates || {}).map((taxRate) => ({
          ...taxRate,
          components: Object.values(taxRate.components || {}),
        })),
      };
    },
    railzTrackingCategories: async (params, source) => {
      const url = '/railz/account/railz-tracking-categories';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<RailzTrackingCategoriesBaseApiResponse>(url, data, possibleResTypes);
      return {
        ...res,
        TrackingCategories: Object.values(res.TrackingCategories || {}),
      };
    },
    railzCreateBill: async (params, source) => {
      const url = '/railz/account/railz-bills/create';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: {},
        body: JSON.stringify({
          ...params,
          Bill: JSON.stringify(params.Bill),
        }),
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },
    railzVendors: async (params, source) => {
      const url = '/railz/account/railz-vendors';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<RailzVendorsBaseApiResponse>(url, data, possibleResTypes);
      return {
        ...res,
        Vendors: Object.values(res.Vendors || {}),
      };
    },
    railzCategories: async (params, source) => {
      const url = '/railz/account/railz-categories';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<RailzCategoriesBaseApiResponse>(url, data, possibleResTypes);
      return {
        ...res,
        Categories: Object.values(res.Categories || {}).map((category) => {
          return {
            ...category,
            subAccounts: Object.values(category.subAccounts || {}),
          };
        }),
      };
    },
    authenticateMerchantUser: async (source) => {
      const url = '/partner/onboarding/merchant/authenticate';
      const { data } = await http.post<never, unknown>({
        url,
        config: {},
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<AuthenticateMerchantUserResponse>(url, data, possibleResTypes);
    },
    fetchCrossBorderCashPickupStates: async (params, source) => {
      const url = '/gcm/cash-pickup/states';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<CrossBorderCashPickupStatesResponse>(url, data, possibleResTypes);
      return {
        ...res,
        CashPickupStates: Object.values(res.CashPickupStates || {}),
      };
    },
    fetchCrossBorderCashPickupCities: async (params, source) => {
      const url = '/gcm/cash-pickup/cities';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<CrossBorderCashPickupCitiesResponse>(url, data, possibleResTypes);
      return {
        ...res,
        CashPickupCities: Object.values(res.CashPickupCities || {}),
      };
    },
    fetchCrossBorderCashPickupLocations: async (params, source) => {
      const url = '/gcm/cash-pickup/locations';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<CrossBorderCashPickupLocationsResponse>(url, data, possibleResTypes);
      return {
        ...res,
        CashPickupLocations: Object.values(res.CashPickupLocations || {}),
      };
    },
    fetchPartnerSigningAuthorityInfo: async (bearer, source) => {
      const url = '/partner/submit-extended-info/signing-authority-info';

      const { data } = await http.get<unknown>({
        url,
        config: {},
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      const res = verifyResponseType<PartnerSigningAuthorityInfoApiResponse>(url, data, possibleResTypes);
      return {
        ...res,
        SigningAuthority: Object.values(res.SigningAuthority || {}),
      };
    },
    postAddPartnerSigner: async (params, bearer, source) => {
      const url = `/partner/submit-extended-info/signing-authority-info`;
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<AddSignerResponse>(url, data, possibleResTypes);
    },
    postDeletePartnerSigner: async (params, bearer, source) => {
      const url = '/partner/submit-extended-info/delete-signing-authority';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<DeleteSignerResponse>(url, data, possibleResTypes);
    },
    postEditPartnerSigner: async (params, bearer, source) => {
      const url = `/partner/submit-extended-info/signing-authority-info/${params?.SigningAuthorityID || ''}`;
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<EditSignerResponse>(url, data, possibleResTypes);
    },

    fetchGCMFundingRails: async (source) => {
      const url = '/gcm/withdraw/funding-options';

      const { data } = await http.get<unknown>({
        url,
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<GCMFundingRailsResponse>(url, data, possibleResTypes);
    },
    postLinkPaypal: async (params, bearer, source) => {
      const url = '/paypal/link-recipient';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<LinkPaypalResponse>(url, data, possibleResTypes);
    },
    postLinkVenmo: async (params, bearer, source) => {
      const url = '/venmo/link-recipient';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
        bearer,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<LinkVenmoResponse>(url, data, possibleResTypes);
    },

    fetchGlobalSendAdminDeliveryChannels: async () => {
      const url = '/gcm/partner/delivery-channels';

      const { data } = await http.get<Record<never, never>, unknown>({
        url,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<GlobalSendDeliveryChannelsResponse>(url, data, possibleResTypes);
    },
    publishGlobalSendAdminDeliveryChannels: async (params) => {
      const url = '/gcm/partner/delivery-channels';

      const { data } = await http.post<Record<never, never>, unknown>({
        url,
        config: {},
        body: JSON.stringify({ ...params, DeliveryChannelsData: JSON.stringify(params.DeliveryChannelsData) }),
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<ApiResponse>(url, data, possibleResTypes);
    },

    fetchGlobalSendAdminFundingOptions: async () => {
      const url = 'gcm/partner/funding-options';

      const { data } = await http.get<Record<never, never>, unknown>({
        url,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<FetchGlobalSendAdminFundingOptionsResponse>(url, data, possibleResTypes);
    },

    setMyGooglePay: async (params, source) => {
      const url = '/google-pay/set-my-google-pay';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<SetMyGooglePayResponse>(url, data, possibleResTypes);
    },
    postAFTPullFunds: async (params, source) => {
      const url = '/debit-card/aft/pull-funds';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<AFTPullFundsResponse>(url, data, possibleResTypes);
    },
    postChargeGooglePay: async (params, source) => {
      const url = '/google-pay/charge';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<ChargeGooglePayResponse>(url, data, possibleResTypes);
    },
    submitGlobalSendAdminFundingOptions: async ({ FundingOptions }, source) => {
      const url = 'gcm/partner/funding-options';

      const { data } = await http.post<Record<never, never>, unknown>({
        url,
        config: {},
        body: JSON.stringify({
          // I'm doing the join here to enforce valid TS types
          FundingOptions: FundingOptions.join(','),
        }),
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<FetchGlobalSendAdminFundingOptionsResponse>(url, data, possibleResTypes);
    },
    fetchBillPaymentAccountMerchants: async (params, source) => {
      const url = '/bill-pay/account/merchants';

      const { data } = await http.get<typeof params, BillPaymentAccountMerchantsApiResponse>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      const res = verifyResponseType<BillPaymentAccountMerchantsApiResponse>(url, data, possibleResTypes);

      return {
        ...res,
        AccountBillPayMerchants: Object.values(res.AccountBillPayMerchants || {}),
      };
    },
    fetchBillPaymentMerchants: async (params, source) => {
      const url = '/bill-pay/merchants';

      const { data } = await http.get<typeof params, BillPaymentMerchantsApiResponse>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      const res = verifyResponseType<BillPaymentMerchantsApiResponse>(url, data, possibleResTypes);
      return {
        ...res,
        Merchants: Object.values(res.Merchants || {}),
      };
    },
    createBillPayment: async (params, source) => {
      const url = '/bill-pay/create';

      const { data } = await http.post<typeof params, BillPayCreateResponse>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<BillPayCreateResponse>(url, data, possibleResTypes);
    },
    addBillPaymentMerchant: async (params, source) => {
      const url = '/bill-pay/account/merchants/add';

      const { data } = await http.post<typeof params, BillPayAccountMerchantAddResponse>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<BillPayAccountMerchantAddResponse>(url, data, possibleResTypes);
    },
    editBillPaymentMerchant: async (params, source) => {
      const url = '/bill-pay/account/merchants/edit';

      const { data } = await http.post<typeof params, BillPayAccountMerchantEditResponse>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<BillPayAccountMerchantEditResponse>(url, data, possibleResTypes);
    },
    fetchDisputeManagementOverview: async (params, source) => {
      const url = '/dispute-management/report/dispute-overview';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<DisputeManagementOverviewResponse>(url, data, possibleResTypes);

      return res;
    },
    fetchDisputeManagementList: async (params, source) => {
      const url = '/dispute-management/list';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      const res = verifyResponseType<DisputeManagementListApiResponse>(url, data, possibleResTypes);
      return {
        ...res,
        Disputes: Object.values(res.Disputes || {}),
      };
    },
    fetchInteracAutoDepositStatus: async (params, bearer, source) => {
      const url = '/interac/auto-deposit';

      const { data } = await http.get<typeof params, InteracAutoDepositStatusResponse>({
        url,
        config: { params },
        source,
        bearer,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<InteracAutoDepositStatusResponse>(url, data, possibleResTypes);
    },
    fetchDisputeStages: async (params, source) => {
      const url = '/dispute-management/report/dispute-stages';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<DisputeStagesApiResponse>(url, data, possibleResTypes);
      return {
        ...res,
        DisputeStages: Object.values(res.DisputeStages || {}),
      };
    },
    fetchDisputeDetails: async (params, source) => {
      const url = '/dispute-management/detail';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<DisputeDetailsResponse>(url, data, possibleResTypes);

      return {
        ...res,
        Dispute: {
          ...res.Dispute,
          Documents: Object.values(res.Dispute.Documents || {}),
          Notes: Object.values(res.Dispute.Notes || {}),
        },
      };
    },
    fetchDisputeDocuments: async (params, source) => {
      const url = '/dispute-management/document';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const res = verifyResponseType<DisputeDocumentsResponse>(url, data, possibleResTypes);
      return res;
    },
    respondToDispute: async (params, source) => {
      const url = '/dispute-management/respond';

      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<RespondAndChallengeDisputeResponse>(url, data, possibleResTypes);
    },
    challengeDispute: async (params, source) => {
      const url = '/dispute-management/challenge';

      const { data } = await http.post<typeof params, unknown>({
        url,
        body: JSON.stringify(params),
        config: {},
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<RespondAndChallengeDisputeResponse>(url, data, possibleResTypes);
    },
    addDisputeNote: async (params, source) => {
      const url = '/dispute-management/add-note';

      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<AddDisputeNoteResponse>(url, data, possibleResTypes);
    },
    acceptDispute: async (params, source) => {
      const url = '/dispute-management/accept';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<AcceptDisputeResponse>(url, data, possibleResTypes);
    },
    fetchDisputeReasons: async (params, source) => {
      const url = '/dispute-management/report/dispute-reasons';

      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<DisputeReasonsResponse>(url, data, possibleResTypes);
    },
    fetchConvenienceFees: async (params, source) => {
      const url = '/account/convenience-fees';

      const { data } = await http.get<undefined, ConvenienceFeesResponseApi>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      const response = verifyResponseType<ConvenienceFeesResponseApi>(url, data, possibleResTypes);
      return {
        ...response,
        FeeSettings: Object.values(response.FeeSettings || {}),
        Restrictions: Object.values(response.Restrictions || {}),
      };
    },
    generateBillPayEmbedUrl: async (params, source) => {
      const url = '/bill-pay/generate-embed-url';

      const { data } = await http.post<typeof params, BillPayGenerateEmbedUrlResponse>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<BillPayGenerateEmbedUrlResponse>(url, data, possibleResTypes);
    },
    fetchBillPayTransaction: async (params, source) => {
      const url = '/bill-pay/transaction';

      const { data } = await http.get<typeof params, BillPayTransactionResponse>({
        url,
        config: { params },
        source,
      });

      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];

      return verifyResponseType<BillPayTransactionResponse>(url, data, possibleResTypes);
    },
    fetchGooglePayChargeTransaction: async (params, source) => {
      const url = '/google-pay/charge/transaction';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<GooglePayChargeTransactionResponse>(url, data, possibleResTypes);
    },
    fetchPayPalWithdrawTransaction: async (params, source) => {
      const url = '/paypal/withdraw/transaction';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<PayPalWithdrawTransactionResponse>(url, data, possibleResTypes);
    },
    fetchVenmoWithdrawTransaction: async (params, source) => {
      const url = '/venmo/withdraw/transaction';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<VenmoWithdrawTransactionResponse>(url, data, possibleResTypes);
    },
    postVenmoWithdraw: async (params, source) => {
      const url = '/venmo/withdraw';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<VenmoWithdrawResponse>(url, data, possibleResTypes);
    },
    postPayPalWithdraw: async (params, source) => {
      const url = '/paypal/withdraw';
      const { data } = await http.post<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      return verifyResponseType<PayPalWithdrawResponse>(url, data, possibleResTypes);
    },
    fetchVenmoRecipients: async (params, source) => {
      const url = '/venmo/recipients';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const resp = verifyResponseType<VenmoRecipientsApiResponse>(url, data, possibleResTypes);
      return {
        ...resp,
        VenmoRecipients: Object.values(resp.VenmoRecipients || {}).map((recipient) => ({
          ...recipient,
          IsDefault: toBool(recipient.IsDefault || '0'),
        })),
      };
    },
    fetchPayPalRecipients: async (params, source) => {
      const url = '/paypal/recipients';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const resp = verifyResponseType<PayPalRecipientsApiResponse>(url, data, possibleResTypes);
      return {
        ...resp,
        PaypalRecipients: Object.values(resp.PaypalRecipients || {}).map((recipient) => ({
          ...recipient,
          IsDefault: toBool(recipient.IsDefault || '0'),
        })),
      };
    },
    fetchPayPalDefaultRecipient: async (params, source) => {
      const url = '/paypal/default-recipient';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const resp = verifyResponseType<PayPalDefaultRecipientApiResponse>(url, data, possibleResTypes);
      const recipients = Object.values(resp.DefaultPayPalRecipient || {});
      return {
        ...resp,
        DefaultPayPalRecipient:
          recipients.length > 0
            ? {
                ...recipients[0],
                IsActive: toBool(recipients[0].IsActive || '0'),
                IsDefault: toBool(recipients[0].IsDefault || '0'),
              }
            : null,
      };
    },
    fetchVenmoDefaultRecipient: async (params, source) => {
      const url = '/venmo/default-recipient';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const resp = verifyResponseType<VenmoDefaultRecipientApiResponse>(url, data, possibleResTypes);
      const recipients = Object.values(resp.DefaultVenmoRecipient || {});
      return {
        ...resp,
        DefaultVenmoRecipient:
          recipients.length > 0
            ? {
                ...recipients[0],
                IsActive: toBool(recipients[0].IsActive || '0'),
                IsDefault: toBool(recipients[0].IsDefault || '0'),
              }
            : null,
      };
    },
    fetchSubAccountPermissions: async (params, source) => {
      const url = '/account/subaccount/permissions';
      const { data } = await http.get<typeof params, unknown>({
        url,
        config: { params },
        source,
      });
      const possibleResTypes: VoPayResponse[] = [VoPayResponse.ApiResponse];
      const resp = verifyResponseType<SubAccountPermissionApiResponse>(url, data, possibleResTypes);
      return {
        ...resp,
        PermissionValues: Object.values(resp.PermissionValues || {}).map((permission) => ({
          ...permission,
          ExtraFields: {
            ...permission.ExtraFields,
            EnumValues: permission.Type === 'enum' ? Object.values(permission.ExtraFields.EnumValues || {}) : undefined,
          },
        })),
      };
    },
  };
  function withApiComponent<P>(C: FunctionComponent<Omit<P, 'api'> & { api: Api }>): FunctionComponent<Omit<P, 'api'>> {
    return (props: Omit<P, 'api'>) => C({ ...props, api });
  }

  function withApiHook<P, R>(Hook: (hookProps: Omit<P, 'api'> & { api: Api }) => R): (props: Omit<P, 'api'>) => R {
    return (props: Omit<P, 'api'>) => Hook({ ...props, api });
  }

  return {
    api,
    withApiComponent,
    withApiHook,
  };
};

export default createVoPayApi;

export type Api = {
  fetchAccountTranslationsUrl: (
    params: FetchTranslationsUrlRequest,
    options?: {
      bearer?: string;
      source?: CancelTokenSource;
    },
  ) => Promise<FetchTranslationsUrlResponse>;
  generateEmployeeOTP: (
    body: GenerateEmployeeOTPRequest,
    source?: CancelTokenSource,
  ) => Promise<GenerateEmployeeOTPResponse>;
  validateEmployeeOTP: (
    body: ValidateEmployeeOTPRequest,
    source?: CancelTokenSource,
  ) => Promise<ValidateEmployeeOTPResponse>;
  fetchDomain: (source?: CancelTokenSource) => Promise<FetchDomainResponse>;
  convertFile: (params: FileConversionRequest, source?: CancelTokenSource) => Promise<FileConversionResponse>;
  getFileConversionConfigurations: (source?: CancelTokenSource) => Promise<FileConversionConfigurationsResponse>;
  getFileConversionHistory: (
    params: FileConversionHistoryRequest,
    source?: CancelTokenSource,
  ) => Promise<FileConversionHistoryResponse>;
  downloadFileConversion: (
    params: FileConversionDownloadRequest,
    source?: CancelTokenSource,
  ) => Promise<FileConversionDownloadResponse>;
  downloadSourceFileConversion: (
    params: FileConversionDownloadSourceRequest,
    source?: CancelTokenSource,
  ) => Promise<FileConversionDownloadSourceResponse>;
  submitFileConversion: (
    params: FileConversionSubmitRequest,
    source?: CancelTokenSource,
  ) => Promise<FileConversionSubmitResponse>;
  deleteDomain: (params: DeleteDomainRequest, source?: CancelTokenSource) => Promise<DeleteDomainResponse>;
  addDomain: (params: AddDomainRequest, source?: CancelTokenSource) => Promise<AddDomainResponse>;
  verifyDomain: (params: VerifyDomainRequest, source?: CancelTokenSource) => Promise<VerifyDomainResponse>;
  linkGooglePay: (params: LinkGooglePayRequest, source?: CancelTokenSource) => Promise<LinkGooglePayResponse>;

  fetchAccountBillingSettings: (
    params: { VopayAccountID: string },
    source?: CancelTokenSource,
  ) => Promise<AccountBillingSettingsResponse>;

  fetchAccountTransactions: (
    params: AccountTransactionsRequest,
    bearer: string,
    source?: CancelTokenSource,
  ) => Promise<AccountTransactions>;

  fetchAccountUsers: (
    params: TimezoneRequest,
    bearer?: string,
    source?: CancelTokenSource,
  ) => Promise<FetchAccountUsersResponse>;

  fetchBusinessTypes: (source?: CancelTokenSource) => Promise<BusinessTypesResponse>;

  fetchBillingPackages: (source?: CancelTokenSource) => Promise<PartnerBillingPackagesResponse>;

  fetchBillingPackageDetails: (
    params: PartnerBillingPackageDetailsRequest,
    source?: CancelTokenSource,
  ) => Promise<PartnerBillingPackageDetailsResponse>;

  fetchBillingSettingsDetails: (
    params: PartnerBillingSettingsDetailsRequest,
    source?: CancelTokenSource,
  ) => Promise<PartnerBillingSettingsDetailsResponse>;

  fetchCreditCardTransaction: (
    params: TransactionId,
    bearer?: string,
    source?: CancelTokenSource,
  ) => Promise<CreditCardTransactionResponse>;

  fetchEFTFundTransaction: (
    params: TransactionRequest,
    bearer?: string,
    source?: CancelTokenSource,
  ) => Promise<EftFundingTransactionResponse>;

  fetchEFTWithdrawTransaction: (
    params: TransactionRequest,
    bearer?: string,
    source?: CancelTokenSource,
  ) => Promise<EftWithdrawTransactionResponse>;
  fetchFinancialInstitutionBranches: (
    params: FinancialInstitutionBranchesRequest,
    source?: CancelTokenSource,
  ) => Promise<FinancialInstitutionBranchesResponse>;
  fetchFinancialInstitutions: (
    params: FinancialInstitutionsRequest,
    source?: CancelTokenSource,
  ) => Promise<FinancialInstitutionsResponse>;
  fetchGlobalPayToken: (
    params: GlobalPayTokenRequest,
    bearer: string | undefined,
    source?: CancelTokenSource,
  ) => Promise<GlobalPayTokenResponse>;
  fetchInteracBulkPayoutTransaction: (
    params: TransactionId,
    bearer: string,
    source?: CancelTokenSource,
  ) => Promise<InteracBulkPayoutTransactionResponse>;

  fetchInteracMoneyRequestTransaction: (
    params: TransactionId,
    bearer: string,
    source?: CancelTokenSource,
  ) => Promise<InteracTransactionResponse>;

  fetchRTRWithdrawTransaction: (
    params: TransactionId,
    bearer: string,
    source?: CancelTokenSource,
  ) => Promise<RTRWithdrawTransactionResponse>;

  fetchFedwireFundTransaction: (
    params: TransactionRequest,
    bearer: string,
    source?: CancelTokenSource,
  ) => Promise<FedwireTransactionResponse>;
  fetchFedwireWithdrawTransaction: (
    params: TransactionRequest,
    bearer: string,
    source?: CancelTokenSource,
  ) => Promise<FedwireTransactionResponse>;
  fetchRTPWithdrawTransaction: (
    params: TransactionId,
    bearer: string,
    source?: CancelTokenSource,
  ) => Promise<RTPWithdrawTransactionResponse>;

  fetchiQ11TokenInfo: (params: TokenInfoRequest, source?: CancelTokenSource) => Promise<TokenInfoResponse>;
  fetchLeadsAgentId: (params: LeadsAgentRequest, source?: CancelTokenSource) => Promise<LeadsResponse>;
  fetchLeadsPartnerId: (
    params: LeadsParentRequest,
    source?: CancelTokenSource,
  ) => Promise<PartnerBillingPackagesResponse>;
  fetchLeadsDetails: (params: LeadsDetailsRequest, source?: CancelTokenSource) => Promise<LeadsResponse>;

  fetchPartnerAccounts: (params: PartnerAccountsRequest, source?: CancelTokenSource) => Promise<PartnerAccounts>;

  fetchPartnerAccountSummary: (
    params: PartnerAccountSummaryRequest,
    source?: CancelTokenSource,
  ) => Promise<PartnerAccountSummaryResponse>;

  fetchPartnerAccountSummaryDetails: (
    params: PartnerAccountSummaryDetailsRequest,
    source?: CancelTokenSource,
  ) => Promise<PartnerAccountSummaryDetailsResponse>;

  fetchPartnerInvoiceDetails: (
    params: PartnerInvoiceDetailsRequest,
    source?: CancelTokenSource,
  ) => Promise<PartnerInvoiceDetailsResponse>;

  fetchPartnerInvoicePdf: (params: InvoicePdfRequest, source?: CancelTokenSource) => Promise<InvoicePdfResponse>;

  fetchPartnerInvoices: (params: InvoicesRequest, source?: CancelTokenSource) => Promise<PartnerInvoicesResponse>;

  fetchPartnerStatementCsv: (params: StatementCsvRequest, source?: CancelTokenSource) => Promise<StatementCsvResponse>;
  fetchPartnerStatementPdf: (
    params: PartnerStatementPdfRequest,
    source?: CancelTokenSource,
  ) => Promise<PartnerStatementPdfResponse>;

  fetchPartnerStatistics: (source?: CancelTokenSource) => Promise<PartnerStatisticsResponse>;

  fetchPartners: (source?: CancelTokenSource) => Promise<PartnersResponse>;

  fetchUserLists: (params: UserListsRequest, source?: CancelTokenSource) => Promise<UserListsResponse>;

  fetchSingleUseToken: (params: SingleUseTokenRequest, source?: CancelTokenSource) => Promise<SingleUseTokenResponse>;

  fetchOnboardingAppUrl: (
    params: OnboardingAppUrlRequest,
    source?: CancelTokenSource,
  ) => Promise<OnboardingAppUrlResponse>;

  fetchAccountsOverviewActive: (source?: CancelTokenSource) => Promise<PartnerStatisticsDataResponse>;
  fetchTransactionsOverview: (source?: CancelTokenSource) => Promise<PartnerStatisticsDataResponse>;
  fetchPerformanceOverview: (source?: CancelTokenSource) => Promise<PartnerStatisticsDataResponse>;
  fetchUserDataOverview: (source?: CancelTokenSource) => Promise<UserDataOverviewResponse>;
  fetchActivityOverview: (
    params: ActivityOverviewRequest,
    source?: CancelTokenSource,
  ) => Promise<ActivityOverviewResponse>;

  fetchPartnerAccountUsers: (source?: CancelTokenSource) => Promise<FetchPartnerAccountUsersResponse>;

  fetchCreditCards: (
    params: CreditCardRequest,
    bearer?: string,
    source?: CancelTokenSource,
  ) => Promise<CreditCardResponse>;

  fetchBankAccounts: (
    params: BankAccountsRequest,
    bearer: string,
    source?: CancelTokenSource,
  ) => Promise<BankAccountsResponse>;

  fetchDebitCards: (params: GetDebitCardsRequest, source?: CancelTokenSource) => Promise<GetDebitCardsResponse>;

  fetchPaylinkSingleUseToken: (
    params: ELinxSingleUseTokenRequest,
    source?: CancelTokenSource,
  ) => Promise<ELinxSingleUseTokenResponse>;
  fetchAccountUserPermissions: (
    params: AccountUserPermissionsRequest,
    bearer?: string,
    source?: CancelTokenSource,
  ) => Promise<AccountUserPermissionsResponse>;

  fetchAgreementDetails: (bearer: string, source?: CancelTokenSource) => Promise<AgreementDetailsResponse>;
  fetchAgreementDownload: (
    params: AgreementDownloadRequest,
    bearer: string,
    source?: CancelTokenSource,
  ) => Promise<AgreementDownloadResponse>;
  fetchSigningAuthorityInfo: (bearer: string, source?: CancelTokenSource) => Promise<SigningAuthorityInfoResponse>;

  fetchAccountLimits: (params: AccountLimitsRequest, source?: CancelTokenSource) => Promise<AccountLimitsResponse>;

  fetchBankHolidays: (source?: CancelTokenSource) => Promise<BankHolidaysResponse>;

  fetchClientBankAccounts: (
    params: ClientBankAccountsRequest,
    source?: CancelTokenSource,
  ) => Promise<ClientBankAccountsResponse>;

  fetchClientCreditCards: (
    params: ClientCreditCardsRequest,
    source?: CancelTokenSource,
  ) => Promise<ClientCreditCardsResponse>;

  fetchClientDebitCards: (
    params: ClientDebitCardsRequest,
    source?: CancelTokenSource,
  ) => Promise<ClientDebitCardsResponse>;

  fetchClientTransactions: (
    params: ClientTransactionsRequest,
    source?: CancelTokenSource,
  ) => Promise<ClientTransactionsResponse>;

  fetchClientBalance: (params: ClientBalanceRequest, source?: CancelTokenSource) => Promise<ClientBalanceResponse>;

  fetchAccountPermissions: (
    params: AccountPermissionsRequest,
    source?: CancelTokenSource,
  ) => Promise<AccountPermissionsResponse>;

  fetchAutoBalanceTransfer: (
    params: AutoBalanceTransferRequest,
    source?: CancelTokenSource,
  ) => Promise<AutoBalanceTransferResponse>;

  fetchSubAccounts: (params: SubAccountsRequest, source?: CancelTokenSource) => Promise<SubAccountsResponse>;

  fetchContactGroups: (params: TimezoneRequest, source?: CancelTokenSource) => Promise<ContactGroupsResponse>;

  fetchGroupContact: (params: GroupContactRequest, source?: CancelTokenSource) => Promise<GroupContactResponse>;

  fetchScheduledTransactions: (
    params: ScheduledTransactionsRequest,
    source?: CancelTokenSource,
  ) => Promise<ScheduledTransactionsResponse>;

  fetchInboundETransferTransaction: (
    params: InboundETransferTransactionRequest,
    source?: CancelTokenSource,
  ) => Promise<InboundETransferTransactionResponse>;

  fetchVisaDirectPushTransaction: (
    params: TransactionId,
    source?: CancelTokenSource,
  ) => Promise<VisaDirectPushTransactionResponse>;

  fetchCurrencyConversionTransaction: (
    params: CurrencyConversionTransactionRequest,
    source?: CancelTokenSource,
  ) => Promise<CurrencyConversionTransactionResponse>;

  fetchClientAccountList: (
    params: ClientAccountListRequest,
    source?: CancelTokenSource,
  ) => Promise<ClientAccountListResponse>;

  fetchClientAccountBalanceRange: (source?: CancelTokenSource) => Promise<ClientAccountBalanceRangeResponse>;

  fetchClientAccountPaymentMethods: (
    params: ClientAccountPaymentMethodRequest,
    source?: CancelTokenSource,
  ) => Promise<PaymentMethodResponse>;

  fetchContactPaymentMethods: (
    params: ContactPaymentMethodRequest,
    source?: CancelTokenSource,
  ) => Promise<PaymentMethodResponse>;

  fetchClientBusinessTypes: (source?: CancelTokenSource) => Promise<BusinessTypesResponse>;

  fetchClientAccountBalance: (
    params: ClientAccountBalanceRequest,
    source?: CancelTokenSource,
  ) => Promise<ClientAccountBalanceResponse>;

  fetchDefaultBankAccount: (
    params: DefaultBankAccountRequest,
    source?: CancelTokenSource,
  ) => Promise<DefaultBankAccountResponse>;

  fetchPaasTransactions: (
    params: PaasTransactionsRequest,
    source?: CancelTokenSource,
  ) => Promise<PaasTransactionsResponse>;

  fetchBatchFileTransactions: (
    params: BatchFileTransactionsRequest,
    source?: CancelTokenSource,
  ) => Promise<BatchFileTransactionsResponse>;

  fetchInteracEmail: (params: InteracEmailRequest, source?: CancelTokenSource) => Promise<InteracEmailResponse>;
  fetchClientAccounts: (
    params: ClientAccountsRequest,
    bearer?: string,
    source?: CancelTokenSource,
  ) => Promise<ClientAccountsResponse>;

  fetchSendLegalAgreement: (bearer?: string, source?: CancelTokenSource) => Promise<SendLegalAgreementResponse>;

  fetchAccountVerification: (
    params: AccountVerificationRequest,
    source?: CancelTokenSource,
  ) => Promise<AccountVerificationResponse>;

  postVoidAgreement: (params: VoidAgreementRequest, bearer: string, source?: CancelTokenSource) => Promise<ApiResponse>;
  postDeleteSigner: (
    params: DeleteSignerRequest,
    bearer: string,
    source?: CancelTokenSource,
  ) => Promise<DeleteSignerResponse>;
  postAddSigner: (params: AddSignerRequest, bearer: string, source?: CancelTokenSource) => Promise<AddSignerResponse>;
  postEditSigner: (
    params: EditSignerRequest,
    bearer: string,
    source?: CancelTokenSource,
  ) => Promise<EditSignerResponse>;

  postSendOnboardingEmail: (params: SendOnboardingEmailRequest, source?: CancelTokenSource) => Promise<ApiResponse>;

  postCreateUser: (params: CreateUserRequest, source?: CancelTokenSource) => Promise<CreateUserResponse>;
  postDeleteUser: (params: DeleteUserRequest, source?: CancelTokenSource) => Promise<DeleteUserResponse>;

  postUserChangePassword: (
    params: UserChangePasswordRequest,
    source?: CancelTokenSource,
  ) => Promise<UserChangePasswordResponse>;

  postUserChangeRole: (params: UserChangeRoleRequest, source?: CancelTokenSource) => Promise<ApiResponse>;

  generateSingleUseToken: (params: SingleUseTokenRequest, source?: CancelTokenSource) => Promise<ApiResponse>;

  postAccountUsersChangePassword: (
    params: AccountUsersChangePasswordRequest,
    bearer?: string,
    source?: CancelTokenSource,
  ) => Promise<AccountUsersChangePasswordResponse>;

  postCreateAccountUser: (
    params: CreateAccountUserRequest,
    bearer?: string,
    source?: CancelTokenSource,
  ) => Promise<CreateAccountUserResponse>;
  postCreateLead: (params: CreateLeadRequest, source?: CancelTokenSource) => Promise<ApiResponse>;
  postDeleteAccountUser: (
    params: DeleteAccountUserRequest,
    bearer?: string,
    source?: CancelTokenSource,
  ) => Promise<DeleteAccountUserResponse>;
  postIFrameEvent: (params: IFrameEventRequest, source?: CancelTokenSource) => Promise<ApiResponse>;
  postIQ11Complete: (params: IQ11CompleteRequest, source?: CancelTokenSource) => Promise<IQ11TokenResponse>;
  postLinkBankAccountManually: (
    params: LinkBankAccountManuallyRequest,
    source?: CancelTokenSource,
  ) => Promise<IQ11TokenResponse>;
  postLinkCreditCard: (params: LinkCreditCardRequest, source?: CancelTokenSource) => Promise<IQ11TokenResponse>;

  postLinkPushToCard: (params: LinkPushToCardRequest, source?: CancelTokenSource) => Promise<IQ11TokenResponse>;

  postPartnerAccountBusinessCase: (
    params: PartnerAccountBusinessCaseRequest,
    source?: CancelTokenSource,
  ) => Promise<ApiResponse>;

  postPartnerAccountMatrix: (params: PartnerAccountMatrixRequest, source?: CancelTokenSource) => Promise<ApiResponse>;

  postPartnerAccount: (
    params: PartnerAccountRequest,
    source?: CancelTokenSource,
    overrideAuth?: { AccountID: string; Key: string; Signature: string },
  ) => Promise<PartnerAccountCreationResponse | PartnerAccountCreationLinkResponse | ApiResponse>;

  postPartnerAccountPermissions: (params: AccountPermissionRequest, source?: CancelTokenSource) => Promise<ApiResponse>;

  postAccountUserPermissions: (
    params: AccountUserPermissionsPostRequest,
    bearer?: string,
    source?: CancelTokenSource,
  ) => Promise<ApiResponse>;

  postPartnerAccountUsersChangePassword: (
    params: PartnerAccountUsersChangePasswordRequest,
    source?: CancelTokenSource,
  ) => Promise<PartnerAccountUsersChangePasswordResponse>;

  postPartnerCustomPackage: (
    params: PartnerCustomPackageRequest,
    source?: CancelTokenSource,
  ) => Promise<PartnerCustomPackageResponse>;

  postPaylinkDecline: (params: ELinxDeclineRequest, source?: CancelTokenSource) => Promise<ApiResponse>;
  postPaylinkSetAccount: (params: ELinxSetAccountRequest, source?: CancelTokenSource) => Promise<ApiResponse>;

  postPortalPartnerLogin: (params: Login, source?: CancelTokenSource) => Promise<AuthorizationResponse>;
  postPartnerAuthPing: (source?: CancelTokenSource) => Promise<ApiResponse>;

  postIQ11Login: (params: IQ11Login, source?: CancelTokenSource) => Promise<IQ11AuthorizationResponse>;
  postIQ11AuthPing: (bearer: string, source?: CancelTokenSource) => Promise<ApiResponse>;

  postIQ11CreditCardEmbedUrl: (
    params: IQ11CreditCardEmbedUrlRequest,
    bearer: string,
    source?: CancelTokenSource,
  ) => Promise<IQ11EmbedUrlResponse>;
  postIQ11EmbedUrl: (
    params: IQ11EmbedUrlRequest,
    bearer: string,
    source?: CancelTokenSource,
  ) => Promise<IQ11EmbedUrlResponse>;
  postDeleteCreditCard: (
    params: DeleteCreditCardRequest,
    bearer: string,
    source?: CancelTokenSource,
  ) => Promise<ApiResponse>;

  postSetMyCreditCard: (
    params: SetMyCreditCardRequest,
    bearer: string,
    source?: CancelTokenSource,
  ) => Promise<SetMyCreditCardResponse>;

  postDeleteBankAccount: (
    params: DeleteBankAccountRequest,
    bearer: string,
    source?: CancelTokenSource,
  ) => Promise<ApiResponse>;

  postDeleteBankAccountDocuments: (
    params: DeleteBankAccountDocumentsRequest,
    bearer: string,
    source?: CancelTokenSource,
  ) => Promise<ApiResponse>;

  postSetMyBankAccount: (
    params: SetMyBankAccountRequest,
    bearer: string,
    source?: CancelTokenSource,
  ) => Promise<SetMyBankAccountResponse>;
  postSetMyBankAccountWithPurpose: (
    params: SetMyBankAccountPurposeRequest,
    bearer: string,
    source?: CancelTokenSource,
  ) => Promise<SetMyBankAccountPurposeResponse>;
  fetchMyBankAccount: (
    params: MyBankAccountRequest,
    bearer: string,
    source?: CancelTokenSource,
  ) => Promise<MyBankAccountResponse>;

  postTransactionRefund: (
    params: TransactionsRefundRequest,
    bearer: string,
    source?: CancelTokenSource,
  ) => Promise<TransactionsRefundResponse>;

  postTransactionCancel: (
    params: TransactionsCancelRequest,
    bearer: string,
    source?: CancelTokenSource,
  ) => Promise<TransactionsCancelResponse>;

  postBankAccountConnection: (
    params: BankAccountConnectionRequest,
    source?: CancelTokenSource,
  ) => Promise<BankAccountConnectionResponse>;

  postLinkBankAccount: (params: LinkBankAccountRequest, source?: CancelTokenSource) => Promise<IQ11TokenResponse>;

  postClientChangePassword: (params: ClientChangePasswordRequest, source?: CancelTokenSource) => Promise<ApiResponse>;

  postPortalClientLogin: (params: ClientLogin, source?: CancelTokenSource) => Promise<ClientAuthorizationResponse>;
  postClientAuthPing: (source?: CancelTokenSource) => Promise<ApiResponse>;
  postClientResetPasswordEmail: (
    params: ClientResetPasswordEmailRequest,
    source?: CancelTokenSource,
  ) => Promise<ClientResetPasswordEmailResponse>;

  postPortalClientLoginHistory: (
    params: PortalClientLoginHistoryRequest,
    source?: CancelTokenSource,
  ) => Promise<PortalClientLoginHistoryResponse>;
  postVerifyRecaptcha: (params: VerifyRecaptchaRequest, source?: CancelTokenSource) => Promise<VerifyRecaptchaResponse>;

  postPortalGenerateQRCode: (
    params: PortalGenerateQRCodeRequest,
    source?: CancelTokenSource,
  ) => Promise<PortalGenerateQRCodeResponse>;
  postPortalAuthenticate: (
    params: PortalAuthenticateRequest,
    source?: CancelTokenSource,
  ) => Promise<PortalAuthenticateResponse>;
  postPortalChangePassword: (params: PortalChangePasswordRequest, source?: CancelTokenSource) => Promise<ApiResponse>;
  postPortalResetPassword: (params: PortalResetPasswordRequest, source?: CancelTokenSource) => Promise<ApiResponse>;

  postAccountBalance: (params: AccountBalanceRequest, source?: CancelTokenSource) => Promise<AccountBalanceResponse>;

  postDeleteClientBankAccount: (
    params: DeleteClientBankAccountRequest,
    source?: CancelTokenSource,
  ) => Promise<ApiResponse>;
  postDeletePaypalAccount: (params: DeletePaypalAccountRequest, source?: CancelTokenSource) => Promise<ApiResponse>;
  postDeleteVenmoAccount: (params: DeleteVenmoAccountRequest, source?: CancelTokenSource) => Promise<ApiResponse>;
  postSetMyClientBankAccount: (
    params: SetMyBankAccountRequest,
    source?: CancelTokenSource,
  ) => Promise<SetMyBankAccountResponse>;
  postSetClientMyCreditCard: (
    params: SetMyCreditCardRequest,
    source?: CancelTokenSource,
  ) => Promise<SetMyCreditCardResponse>;

  postSetClientMyDebitCard: (
    params: SetMyDebitCardRequest,
    source?: CancelTokenSource,
  ) => Promise<SetMyDebitCardResponse>;

  postSetClientEmail: (params: SetClientEmailRequest, source?: CancelTokenSource) => Promise<SetClientEmailResponse>;

  postSetClientGooglePay: (
    params: SetMyGooglePayRequest,
    source?: CancelTokenSource,
  ) => Promise<SetMyGooglePayResponse>;

  postSetClientApplePay: (params: SetMyApplePayRequest, source?: CancelTokenSource) => Promise<SetMyApplePayResponse>;

  postSetClientPayPal: (params: SetMyPayPalRequest, source?: CancelTokenSource) => Promise<SetMyPayPalResponse>;

  postSetClientVenmo: (params: SetMyVenmoRequest, source?: CancelTokenSource) => Promise<SetMyVenmoResponse>;

  postDeleteClientCreditCard: (
    params: DeleteClientCreditCardRequest,
    source?: CancelTokenSource,
  ) => Promise<ApiResponse>;

  postDeleteDebitCard: (params: DeleteDebitCardRequest, source?: CancelTokenSource) => Promise<ApiResponse>;

  postEmbedUrl: (params: EmbedUrlRequest, source?: CancelTokenSource, bearer?: string) => Promise<EmbedUrlResponse>;

  postSendPaylink: (params: SendELinxRequest, source?: CancelTokenSource) => Promise<SendELinxResponse>;

  postEftFund: (params: EftFundRequest, source?: CancelTokenSource) => Promise<EftFundResponse>;

  postScheduledEftFund: (
    params: ScheduledEftFundRequest,
    source?: CancelTokenSource,
  ) => Promise<ScheduledEftFundResponse>;

  postCancelAutoBalanceTransfer: (
    params: CancelAutoBalanceTransferRequest,
    source?: CancelTokenSource,
  ) => Promise<ApiResponse>;

  postChargeCreditCard: (
    params: ChargeCreditCardRequest,
    source?: CancelTokenSource,
  ) => Promise<ChargeCreditCardResponse>;

  postIdVerificationRequest: (
    params: IdVerificationRequest,
    source?: CancelTokenSource,
  ) => Promise<IdVerificationResponse>;

  postInteracMoneyRequest: (
    params: InteracMoneyRequestRequest,
    source?: CancelTokenSource,
  ) => Promise<InteracMoneyRequestResponse>;

  postBulkPayoutRequest: (
    params: InteracBulkPayoutRequest,
    source?: CancelTokenSource,
  ) => Promise<InteracBulkPayoutResponse>;

  postCancelInteracMoneyRequest: (
    params: CancelInteracMoneyRequest,
    source?: CancelTokenSource,
  ) => Promise<CancelInteracMoneyResponse>;

  postCancelInteracBulkPayout: (
    params: CancelInteracMoneyRequest,
    source?: CancelTokenSource,
  ) => Promise<CancelInteracMoneyResponse>;

  postCancelVoPayInstant: (
    params: CancelVoPayInstantRequest,
    source?: CancelTokenSource,
  ) => Promise<CancelVoPayInstantResponse>;

  postEftWithdraw: (params: EftWithdrawRequest, source?: CancelTokenSource) => Promise<EftWithdrawResponse>;

  postRtrWithdraw: (params: RtrWithdrawRequest, source?: CancelTokenSource) => Promise<RtrWithdrawResponse>;

  postScheduledEftWithdraw: (
    params: ScheduledEftWithdrawRequest,
    source?: CancelTokenSource,
  ) => Promise<ScheduledEftWithdrawResponse>;

  postCreateAutoBalanceTransfer: (
    params: CreateAutoBalanceTransferRequest,
    source?: CancelTokenSource,
  ) => Promise<ApiResponse>;

  saveSubAccountPermissions: (
    params: EditAccountPermissionsRequest,
    source?: CancelTokenSource,
  ) => Promise<ApiResponse>;

  postAccountTransferFrom: (
    params: AccountTransferFromRequest,
    source?: CancelTokenSource,
  ) => Promise<AccountTransferFromResponse>;

  postAccountTransferTo: (
    params: AccountTransferToRequest,
    source?: CancelTokenSource,
  ) => Promise<AccountTransferToResponse>;

  postAccountTransferFund: (
    params: AccountTransferFundRequest,
    source?: CancelTokenSource,
  ) => Promise<AccountTransferFundResponse>;

  postAccountTransferWithdraw: (
    params: AccountTransferWithdrawRequest,
    source?: CancelTokenSource,
  ) => Promise<AccountTransferWithdrawResponse>;

  postAccountTransferFundWithdraw: (
    params: AccountTransferFundWithdrawRequest,
    source?: CancelTokenSource,
  ) => Promise<AccountTransferFundWithdrawResponse>;

  postAccountTransferFundSchedule: (
    params: AccountTransferScheduleRequest,
    source?: CancelTokenSource,
  ) => Promise<AccountTransferScheduleResponse>;

  postAccountTransferWithdrawSchedule: (
    params: AccountTransferScheduleRequest,
    source?: CancelTokenSource,
  ) => Promise<AccountTransferScheduleResponse>;

  postAccountTransferFundWithdrawSchedule: (
    params: AccountTransferScheduleRequest,
    source?: CancelTokenSource,
  ) => Promise<AccountTransferScheduleResponse>;

  fetchAccountTransferSchedules: (
    params: AccountTransferSchedulesRequest,
    source?: CancelTokenSource,
  ) => Promise<AccountTransferSchedulesResponse>;

  postAccountTransferScheduleCancel: (
    params: AccountTransferScheduleCancelRequest,
    source?: CancelTokenSource,
  ) => Promise<AccountTransferScheduleCancelResponse>;

  fetchTransactionsHistory: (
    params: TransactionsHistoryRequest,
    source?: CancelTokenSource,
  ) => Promise<TransactionsHistoryResponse>;

  getSubAccountTransactionsHistory: (
    params: SubAccountTransactionsHistoryRequest,
    source?: CancelTokenSource,
  ) => Promise<SubAccountTransactionsHistoryResponse>;

  postCreateSubAccount: (
    params: CreateSubAccountRequest,
    source?: CancelTokenSource,
  ) => Promise<CreateSubAccountResponse>;

  postCreateGroup: (params: CreateGroupRequest, source?: CancelTokenSource) => Promise<CreateGroupResponse>;

  postEditGroup: (params: EditGroupRequest, source?: CancelTokenSource) => Promise<ApiResponse>;

  postDeleteGroup: (params: DeleteGroupRequest, source?: CancelTokenSource) => Promise<ApiResponse>;

  postAddGroupContact: (params: AddGroupContactRequest, source?: CancelTokenSource) => Promise<AddGroupContactResponse>;

  postDeleteGroupContact: (params: DeleteGroupContactRequest, source?: CancelTokenSource) => Promise<ApiResponse>;

  postBulkPayment: (params: BulkPaymentRequest, source?: CancelTokenSource) => Promise<BulkResponse>;

  postBulkCollection: (params: BulkCollectionRequest, source?: CancelTokenSource) => Promise<BulkResponse>;

  postBulkRTRPayment: (params: BulkPaymentRequest, source?: CancelTokenSource) => Promise<BulkResponse>;

  postBulkRTRCollection: (params: BulkCollectionRequest, source?: CancelTokenSource) => Promise<BulkResponse>;
  postBulkMoneyRequest: (params: BulkPaymentRequest, source?: CancelTokenSource) => Promise<BulkResponse>;

  postBulkBulkPayout: (params: BulkPaymentRequest, source?: CancelTokenSource) => Promise<BulkResponse>;
  postBulkPaylink: (params: BulkCollectionRequest, source?: CancelTokenSource) => Promise<BulkResponse>;
  postBulkPushToCardPayment: (params: BulkPaymentRequest, source?: CancelTokenSource) => Promise<BulkResponse>;
  postBulkCreditCardCollection: (params: BulkCollectionRequest, source?: CancelTokenSource) => Promise<BulkResponse>;
  postBulkACHCollection: (params: BulkCollectionRequest, source?: CancelTokenSource) => Promise<BulkResponse>;
  postBulkACHPayment: (params: BulkPaymentRequest, source?: CancelTokenSource) => Promise<BulkResponse>;
  PostBulkRTPPayment: (params: BulkPaymentRequest, source?: CancelTokenSource) => Promise<BulkResponse>;
  postBulkFedwireCollection: (params: BulkCollectionRequest, source?: CancelTokenSource) => Promise<BulkResponse>;
  postBulkFedwirePayment: (params: BulkPaymentRequest, source?: CancelTokenSource) => Promise<BulkResponse>;
  postBulkClientAccountTransfer: (params: BulkPaymentRequest, source?: CancelTokenSource) => Promise<BulkResponse>;

  fetchUploadedFiles: (params: UploadBatchRequest, source?: CancelTokenSource) => Promise<UploadBatchFileResponse>;
  fetchUploadedFileDetails: (
    params: UploadBatchFileDetailsRequest,
    source?: CancelTokenSource,
  ) => Promise<UploadBatchFileDetailseResponse>;

  fetchDefaultPaymentMethod: (
    params: DefaultPaymentMethodRequest,
    source?: CancelTokenSource,
  ) => Promise<DefaultPaymentMethodResponse>;
  postSetDefaultPaymentMethod: (
    params: SetDefaultPaymentMethodRequest,
    source?: CancelTokenSource,
    bearer?: string,
  ) => Promise<ApiResponse>;

  postCancelScheduledWithdraw: (
    params: CancelScheduledWithdrawRequest,
    source?: CancelTokenSource,
  ) => Promise<CancelScheduledWithdrawResponse>;

  postCancelScheduledFund: (
    params: CancelScheduledFundRequest,
    source?: CancelTokenSource,
  ) => Promise<CancelScheduledFundResponse>;

  postEditScheduledWithdraw: (params: EditScheduledWithdrawRequest, source?: CancelTokenSource) => Promise<ApiResponse>;

  postEditScheduledFund: (params: EditScheduledFundRequest, source?: CancelTokenSource) => Promise<ApiResponse>;

  postConfirmTransaction: (params: ConfirmTransactionRequest, source?: CancelTokenSource) => Promise<ApiResponse>;

  fetchTransactionCounts: (
    params: GetTransactionCountsRequest,
    source?: CancelTokenSource,
  ) => Promise<GetTransactionCountsResponse>;

  postCreateClientAccountIndividual: (
    params: CreateClientAccountIndividualRequest,
    source?: CancelTokenSource,
  ) => Promise<CreateClientAccountResponse>;

  postCreateClientAccountBusiness: (
    params: CreateClientAccountBusinessRequest,
    source?: CancelTokenSource,
  ) => Promise<CreateClientAccountResponse>;

  createClientAccountReceiveOnly: (
    params: CreateClientAccountReceiveOnlyRequest,
    source?: CancelTokenSource,
  ) => Promise<CreateClientAccountResponse>;

  createClientAccountStandard: (
    params: CreateClientAccountStandardRequest,
    source?: CancelTokenSource,
  ) => Promise<CreateClientAccountResponse>;

  postEditClientAccount: (params: EditClientAccountRequest, source?: CancelTokenSource) => Promise<ApiResponse>;

  postRenewAccount: (params: RenewAccountRequest, source?: CancelTokenSource) => Promise<ApiResponse>;
  template: (params: unknown, source?: CancelTokenSource) => Promise<unknown>;
  fetchClientAccountLabels: (
    params: FetchClientAccountLabelsRequest,
    source?: CancelTokenSource,
  ) => Promise<FetchLabelsResponse>;
  addClientAccountLabel: (
    params: AddClientAccountLabelRequest,
    source?: CancelTokenSource,
  ) => Promise<AddLabelResponse>;
  deleteClientAccountLabel: (
    params: DeleteClientAccountLabelRequest,
    source?: CancelTokenSource,
  ) => Promise<DeleteLabelResponse>;
  fetchContactLabels: (params: FetchContactLabelsRequest, source?: CancelTokenSource) => Promise<FetchLabelsResponse>;
  addContactLabel: (params: AddContactLabelRequest, source?: CancelTokenSource) => Promise<AddLabelResponse>;
  deleteContactLabel: (params: DeleteContactLabelRequest, source?: CancelTokenSource) => Promise<DeleteLabelResponse>;
  fetchTicketDetails: (params: TicketDetialRequest, source?: CancelTokenSource) => Promise<TicketDetailsResponse>;
  fetchTickets: (params?: EmailRequest, source?: CancelTokenSource) => Promise<TicketsResponse>;
  submitPartnerSupportTicket: (
    params: SubmitTicketRequest,
    source?: CancelTokenSource,
  ) => Promise<SubmitTicketResponse>;
  postSubmitTicketComment: (params: SubmitTicketCommentRequst, source?: CancelTokenSource) => Promise<ApiResponse>;
  postTicketAttachment: (params: TicketAttachmentRequst, source?: CancelTokenSource) => Promise<ApiResponse>;
  fetchTicketDetailsClient: (
    params: TicketDetailsClientRequest,
    source?: CancelTokenSource,
  ) => Promise<TicketDetailsResponse>;
  fetchTicketsClient: (params?: TicketClientsRequest, source?: CancelTokenSource) => Promise<TicketsResponse>;
  submitClientSupportTicket: (params: SubmitTicketRequest, source?: CancelTokenSource) => Promise<SubmitTicketResponse>;
  postSubmitTicketCommentClient: (
    params: SubmitTicketCommentRequst,
    source?: CancelTokenSource,
  ) => Promise<ApiResponse>;
  postSubmitTicketAttachment: (
    params: TicketAttachmentRequest,
    source?: CancelTokenSource,
  ) => Promise<TicketAttachmentResponse>;
  postSubmitTicketAttachmentClient: (
    params: TicketAttachmentRequest,
    source?: CancelTokenSource,
  ) => Promise<TicketAttachmentResponse>;
  fetchWebhookInfo: (params: PortalUser, source?: CancelTokenSource) => Promise<WebhookResponse>;
  fetchWebhooks: (params: ClientWebhooksRequest, source?: CancelTokenSource) => Promise<WebhooksResponse>;
  fetchPartnerWebhooks: (params: WebhooksRequest, source?: CancelTokenSource) => Promise<WebhooksResponse>;
  fetchPartnerWebhook: (params: PartnerUser, source?: CancelTokenSource) => Promise<WebhookResponse>;
  fetchTestWebhook: (params: PortalUser, source?: CancelTokenSource) => Promise<ApiResponse>;
  fetchPartnerTestWebhook: (params: PortalUser, source?: CancelTokenSource) => Promise<ApiResponse>;
  postPartnerUpdateWebhook: (params: UpdateWebhookRequest, source?: CancelTokenSource) => Promise<ApiResponse>;
  postUpdateWebhook: (params: UpdateWebhookRequest, source?: CancelTokenSource) => Promise<ApiResponse>;
  postResendWebhook: (params: RetriggerWebhookRequest, source?: CancelTokenSource) => Promise<ApiResponse>;
  postPartnerResendWebhook: (params: RetriggerWebhookRequest, source?: CancelTokenSource) => Promise<ApiResponse>;
  postSetPlaidCredentials: (params: SetPlaidCredentialsRequest, source?: CancelTokenSource) => Promise<ApiResponse>;
  postSetFlinksCredentials: (params: SetFlinksCredentialsRequest, source?: CancelTokenSource) => Promise<ApiResponse>;
  postSetInveriteCredentials: (
    params: SetInveriteCredentialsRequest,
    source?: CancelTokenSource,
  ) => Promise<ApiResponse>;
  postSetGlobalPayCredentials: (
    params: SetGlobalPayCredentialsRequest,
    source?: CancelTokenSource,
  ) => Promise<ApiResponse>;
  postSaveAuthorizedIPs: (params: SaveAuthorizedIPsRequest, source?: CancelTokenSource) => Promise<ApiResponse>;
  fetchAuthorizedIPs: (params: PortalUser, source?: CancelTokenSource) => Promise<AuthorizedIPsResponse>;
  fetchELinxHistory: (params: ELinxHistoryRequest, source?: CancelTokenSource) => Promise<ELinxHistoryResponse>;
  fetchPaylinkSettings: (params: PortalUser, source?: CancelTokenSource) => Promise<ELinxSettingsResponse>;
  postPaylinkSettingsSetSendeeEmail: (
    params: ELinxSettingsSetSendeeEmailRequest,
    source?: CancelTokenSource,
  ) => Promise<ApiResponse>;
  postPaylinkSettingsSetSenderEmail: (
    params: ELinxSettingsSetSenderEmailRequest,
    source?: CancelTokenSource,
  ) => Promise<ApiResponse>;
  postPaylinkSettingsSetTokenExpiration: (
    params: ELinxSettingsSetTokenExpirationRequest,
    source?: CancelTokenSource,
  ) => Promise<ApiResponse>;
  postPaylinkSettingsUpdate: (
    params: PostELinxSettingsUpdateRequestRequest,
    source?: CancelTokenSource,
  ) => Promise<ApiResponse>;
  postPaylinkSettingsSetDecline: (
    params: ELinxSettingsSetDeclineRequest,
    source?: CancelTokenSource,
  ) => Promise<ApiResponse>;
  postPaylinkCancel: (params: ELinxCancelRequest, source?: CancelTokenSource) => Promise<ApiResponse>;
  postDeleteInteracEmail: (params: DeleteInteracEmailRequest, source?: CancelTokenSource) => Promise<ApiResponse>;
  postAddInteracEmail: (
    params: InteracAddEmailRequest,
    source?: CancelTokenSource,
    bearer?: string,
  ) => Promise<InteracAddEmailResponse>;
  postLinkInteracEmail: (params: InteracLinkEmailRequest, source?: CancelTokenSource) => Promise<IQ11TokenResponse>;
  postIQ11Tokenize: (params: IQ11TokenizeRequest, source?: CancelTokenSource) => Promise<IQ11TokenizeResponse>;
  fetchiQ11AccessTokenInfo: (
    params: AccessTokenInfoRequest,
    source?: CancelTokenSource,
  ) => Promise<AccessTokenInfoResponse>;
  postLinkPushToCardRequest: (
    params: LinkPushToCardMapRequest,
    source?: CancelTokenSource,
  ) => Promise<PushToCardMapResponse>;
  fetchPartnerAccountTransactions: (
    params: ParentAccountTransactionsRequest,
    source?: CancelTokenSource,
  ) => Promise<AccountTransactions>;
  fetchHubSpotDropdownData: (source?: CancelTokenSource) => Promise<HubSpotDropdownDataResponse>;
  fetchHubSpotDealData: (
    params: HubSpotDealDataRequest,
    source?: CancelTokenSource,
  ) => Promise<HubSpotDealDetailsResponse>;
  postAccountBillingSavePaymentInformation: (
    params: AccountBillingSavePaymentInformationRequest,
    source?: CancelTokenSource,
    bearer?: string,
  ) => Promise<AccountBillingSavePaymentInformationResponse>;
  fetchAccountBillingDetails: (
    params: AccountBillingDetailsRequest,
    source?: CancelTokenSource,
    bearer?: string,
  ) => Promise<AccountBillingDetailsResponse>;
  postAccountBillingDetails: (
    params: AccountBillingDetailsSaveRequest,
    source?: CancelTokenSource,
    bearer?: string,
  ) => Promise<ApiResponse>;
  postAccountBillingForDisconnection: (
    params: AccountBillingDisconnectionRequest,
    source?: CancelTokenSource,
    bearer?: string,
  ) => Promise<ApiResponse>;
  fetchAccountDocument: (
    params: AccountDocumentRequest,
    source?: CancelTokenSource,
    bearer?: string,
  ) => Promise<AccountDocumentResponse>;
  postHubSpotDealUpdate: (params: HubSpotDealUpdateRequest, source?: CancelTokenSource) => Promise<ApiResponse>;
  postAccountDocumentUpload: (
    params: DocumentUploadRequest,
    bearer: string,
    source?: CancelTokenSource,
  ) => Promise<DocumentUploadResponse>;
  postBankAccountDocumentUpload: (
    params: BankAccountDocumentUploadRequest,
    bearer: string,
    source?: CancelTokenSource,
  ) => Promise<DocumentUploadResponse>;
  postBankAccountDocumentDelete: (
    params: BankAccountDocumentDeleteRequest,
    bearer: string,
    source?: CancelTokenSource,
  ) => Promise<ApiResponse>;
  postPartnerAccountTransfer: (
    params: PartnerAccountTransferRequest,
    source?: CancelTokenSource,
  ) => Promise<PartnerAccountTransferResponse>;
  postPartnerAccountFundAndTransfer: (
    params: PartnerAccountTransferRequest,
    source?: CancelTokenSource,
  ) => Promise<PartnerAccountFundAndTransferResponse>;
  postPartnerAccountTransferAndWithdraw: (
    params: PartnerAccountTransferRequest,
    source?: CancelTokenSource,
  ) => Promise<PartnerAccountTransferAndWithdrawResponse>;
  postPartnerAccountFundTransferAndWithdraw: (
    params: PartnerAccountTransferRequest,
    source?: CancelTokenSource,
  ) => Promise<PartnerAccountFundTransferAndWithdrawResponse>;
  postWithdrawMyAccount: (
    params: WithdrawMyAccountRequest,
    source?: CancelTokenSource,
  ) => Promise<WithdrawMyAccountResponse>;
  postFundMyAccount: (params: FundMyAccountRequest, source?: CancelTokenSource) => Promise<FundMyAccountResponse>;
  postVoidACHTransaction: (
    params: VoidACHTransactionRequest,
    source?: CancelTokenSource,
  ) => Promise<VoidACHTransactionResponse>;
  postRefundACHTransaction: (
    params: RefundACHTransactionRequest,
    source?: CancelTokenSource,
  ) => Promise<RefundACHTransactionResponse>;
  postACHFund: (params: ACHFundRequest, source?: CancelTokenSource) => Promise<ACHFundResponse>;
  postACHWithdraw: (params: ACHWithdrawRequest, source?: CancelTokenSource) => Promise<ACHWithdrawResponse>;
  postACHFundSchedule: (params: ACHFundScheduleRequest, source?: CancelTokenSource) => Promise<ACHFundScheduleResponse>;
  postACHWithdrawSchedule: (
    params: ACHFundScheduleRequest,
    source?: CancelTokenSource,
  ) => Promise<ACHFundScheduleResponse>;
  fetchACHFundTransaction: (
    params: ACHFundTransactionRequest,
    source?: CancelTokenSource,
  ) => Promise<ACHFundTransactionResponse>;
  fetchACHWithdrawTransaction: (
    params: ACHWithdrawTransactionRequest,
    source?: CancelTokenSource,
  ) => Promise<ACHWithdrawTransactionResponse>;
  fetchACHSheduleTransaction: (
    params: ACHSheduleTransactionRequest,
    source?: CancelTokenSource,
  ) => Promise<ACHSheduleTransactionResponse>;
  postRefundCreditCardTransaction: (
    params: RefundCreditCardTransactionRequest,
    source?: CancelTokenSource,
  ) => Promise<RefundCreditCardTransactionResponse>;

  postOnboardingToken: (params: OnboardingTokenRequest, source?: CancelTokenSource) => Promise<OnboardingTokenResponse>;

  postSendLegalAgreement: (bearer?: string, source?: CancelTokenSource) => Promise<PostLegalAgreementResponse>;

  fetchTicketCategories: (source?: CancelTokenSource) => Promise<TicketCategoriesResponse>;
  fetchClientDashboardTicketCategories: (source?: CancelTokenSource) => Promise<TicketCategoriesResponse>;
  fetchGeolocation: (source?: CancelTokenSource) => Promise<GeoLocationResponse>;

  fetchPortalVideos: (params: PortalVideoRequest, source?: CancelTokenSource) => Promise<PortalVideoResponse>;

  postOnboardingAppToken: (
    params: OnboardingAppTokenRequest,
    source?: CancelTokenSource,
  ) => Promise<OnboardingAppTokenResponse>;

  getAssignedBillingPackage: (
    params: AssignedBillingPackagesRequest,
    source?: CancelTokenSource,
    bearer?: string,
  ) => Promise<AssignedBillingPackagesResponse>;

  getAccountTransactionTypes: (
    params: AccountTransactionTypesRequest,
    source?: CancelTokenSource,
    bearer?: string,
  ) => Promise<AccountTransactionTypesResponse>;

  fetchSubscriptions: (params: ReportsRequest, source?: CancelTokenSource) => Promise<FetchSubscriptionListResponse>;

  fetchSubscriptionDetail: (params: ReportsRequest, source?: CancelTokenSource) => Promise<ReportDetailResponse>;

  postEditReport: (params: EditReportRequest, source?: CancelTokenSource) => Promise<ApiResponse>;

  postCreateReport: (params: CreateReportRequest, source?: CancelTokenSource) => Promise<CreateReportsResponse>;

  postDownloadReport: (params: DownloadReportRequest, source?: CancelTokenSource) => Promise<DownloadReportsResponse>;

  fetchAccountInvoices: (params: InvoicesRequest, source?: CancelTokenSource) => Promise<AccountInvoicesResponse>;
  fetchAccountInvoicePdf: (params: InvoicePdfRequest, source?: CancelTokenSource) => Promise<InvoicePdfResponse>;
  fetchAccountStatementCsv: (params: StatementCsvRequest, source?: CancelTokenSource) => Promise<StatementCsvResponse>;
  fetchAccountStatementPDF: (params: StatementCsvRequest, source?: CancelTokenSource) => Promise<StatementPDFResponse>;

  postAccountVerificationInitiate: (
    params: AccountVerificationInitiateRequest,
    source?: CancelTokenSource,
  ) => Promise<AccountVerificationInitiateResponse>;

  postInteracVerification: (
    params: InteractVerificationRequest,
    source?: CancelTokenSource,
  ) => Promise<InteractVerificationResponse>;

  postAccountVerificationConfirm: (
    params: AccountVerificationConfirmRequest,
    source?: CancelTokenSource,
  ) => Promise<AccountVerificationConfirmResponse>;

  fetchDebitAuthority: (
    params: GetDebitAuthorityAgreementRequest,
    source?: CancelTokenSource,
  ) => Promise<GetDebitAuthorityAgreementResponse>;

  postDebitAuthority: (
    params: CreateDebitAuthorityAgreementRequest,
    source?: CancelTokenSource,
  ) => Promise<ApiResponse>;
  fetchAccountBranding: (
    params: AccountBrandingRequest,
    source?: CancelTokenSource,
    bearer?: string,
  ) => Promise<AccountBrandingResponse>;
  fetchPartnerBranding: (
    params: AccountBrandingRequest,
    source?: CancelTokenSource,
  ) => Promise<AccountBrandingResponse>;
  fetchPartnerAccountPermissionFields: (
    // eslint-disable-next-line @typescript-eslint/no-empty-object-type
    params: {},
    source?: CancelTokenSource,
  ) => Promise<PartnerAccountPermissionFieldsResponse>;
  postAccountBranding: (params: SaveAccountBrandingRequest, source?: CancelTokenSource) => Promise<ApiResponse>;
  postPartnerBranding: (params: SaveAccountBrandingRequest, source?: CancelTokenSource) => Promise<ApiResponse>;
  fetchFAQs: (source?: CancelTokenSource) => Promise<FAQResponse>;
  fetchFAQAllQuestions: (source?: CancelTokenSource) => Promise<FAQAllQuestionsResponse>;
  postFAQUpdateClickCount: (params: FAQRequest, source?: CancelTokenSource) => Promise<FAQResponse>;
  fetchFAQCategories: (source?: CancelTokenSource) => Promise<FAQCategoriesResponse>;
  fetchLoginPageBranding: (
    params: AccountBrandingLoginPageRequest,
    source?: CancelTokenSource,
  ) => Promise<LoginBrandingResponse>;
  fetchApprovalTransactions: (
    params: ApprovalInboxTransactionsRequest,
    source?: CancelTokenSource,
  ) => Promise<ApprovalInboxTransactionsResponse>;
  fetchApprovalTransactionsInbox: (
    params: ApprovalInboxTransactionsRequest,
    source?: CancelTokenSource,
  ) => Promise<ApprovalInboxTransactionsResponse>;
  postVerifyApprovalTransation: (
    params: VerifyTransactionApproveRequest,
    source?: CancelTokenSource,
  ) => Promise<ApiResponse>;
  postVerifyMarkAsProceedTransaction: (
    params: VerifyTransactionApproveRequest,
    source?: CancelTokenSource,
  ) => Promise<ApiResponse>;
  postApproveApprovalTransation: (params: InboxStatusChageRequest, source?: CancelTokenSource) => Promise<ApiResponse>;
  postUnapproveApprovalTransation: (
    params: InboxStatusChageRequest,
    source?: CancelTokenSource,
  ) => Promise<ApiResponse>;
  postRejectApprovalTransation: (params: InboxStatusChageRequest, source?: CancelTokenSource) => Promise<ApiResponse>;

  fetchUserRoles: (params: UserRolesRequest, source?: CancelTokenSource) => Promise<UserRolesResponse>;

  postCreateUserRole: (params: UpdateUserRoleRequest, source?: CancelTokenSource) => Promise<ApiResponse>;
  postDeleteUserRole: (params: UpdateUserRoleRequest, source?: CancelTokenSource) => Promise<ApiResponse>;
  postCreateUserRolePermission: (
    params: UpdateUserRolePermissionRequest,
    source?: CancelTokenSource,
  ) => Promise<ApiResponse>;
  postUpdateUserRolePermission: (
    params: UpdateUserRolePermissionRequest,
    source?: CancelTokenSource,
  ) => Promise<ApiResponse>;
  postDeleteUserRolePermission: (
    params: UpdateUserRolePermissionRequest,
    source?: CancelTokenSource,
  ) => Promise<ApiResponse>;

  fetchWorkflows: (params: PortalUser, source?: CancelTokenSource) => Promise<WorkFlowsResponse>;
  postCreateWorkflow: (params: WorkflowCreateRequest, source?: CancelTokenSource) => Promise<ApiResponse>;
  postUpdateWorkflow: (params: WorkflowUpdateRequest, source?: CancelTokenSource) => Promise<ApiResponse>;

  fetchTransactionTypes: (
    params: TransactionTypesRequst,
    source?: CancelTokenSource,
  ) => Promise<TransactionTypesResponse>;
  fetchRailzBusinessAccount: (params: PortalUser, source?: CancelTokenSource) => Promise<RailzBusinessAccountResponse>;
  fetchRailzAccount: (params: GetChartOfAccountRequest, source?: CancelTokenSource) => Promise<ChartOfAccountResponse>;
  fetchRailzChartOfAccounts: (
    params: GetChartOfAccountRequest,
    source?: CancelTokenSource,
  ) => Promise<ChartOfAccountsResponse>;
  postSetRailzChartOfAccount: (params: ChartOfAccountRequest, source?: CancelTokenSource) => Promise<ApiResponse>;

  fetchRailzBills: (params: RailzBillsOrInvoicesRequest, source?: CancelTokenSource) => Promise<RailzBillsResponse>;
  postBills: (params: PayBillsRequest, source?: CancelTokenSource) => Promise<ApiResponse>;
  fetchRailzInvoices: (
    params: RailzBillsOrInvoicesRequest,
    source?: CancelTokenSource,
  ) => Promise<RailzInvoicesResponse>;
  postInvoices: (params: PayInvoicesRequest, source?: CancelTokenSource) => Promise<ApiResponse>;
  postRailzSync: (params: BaseRailzeRequest, source?: CancelTokenSource) => Promise<ApiResponse>;
  fetchRailzSyncStatus: (params: BaseRailzeRequest, source?: CancelTokenSource) => Promise<RailzStatusSyncData>;
  fetchPortalDocument: (source?: CancelTokenSource) => Promise<PortalDocumentResponse>;
  postCancelInvoice: (params: RailzInvoiceCancelRequest, source?: CancelTokenSource) => Promise<ApiResponse>;
  postCancelBill: (params: RailzBillsCancelRequest, source?: CancelTokenSource) => Promise<ApiResponse>;
  postFundMyAccountSchedule: (
    paramse: MyAccountScheduleRequest,
    source?: CancelTokenSource,
  ) => Promise<MyAccountScheduleResponse>;
  postWithdrawMyAccountSchedule: (
    paramse: MyAccountScheduleRequest,
    source?: CancelTokenSource,
  ) => Promise<MyAccountScheduleResponse>;
  fetchAutoBalanceTransferReport: (
    params: TransactionId,
    source?: CancelTokenSource,
  ) => Promise<AutoBalanceTransferReportResponse>;
  fetchRailzBillCreditNotes: (
    params: CreditNotesBillsRequest,
    source?: CancelTokenSource,
  ) => Promise<CreditNotesResponse>;
  fetchRailzInvoiceCreditNotes: (
    params: CreditNotesInvoicesRequest,
    source?: CancelTokenSource,
  ) => Promise<CreditNotesResponse>;
  fetchContacts: (params?: ContactsRequest, source?: CancelTokenSource) => Promise<ContactsResponse>;

  fetchRecentGlobalSendTransactions: (
    params: GlobalSendRecentTransactionsRequest,
    source?: CancelTokenSource,
  ) => Promise<GlobalSendRecentTransactionsResponse>;

  postCrossBorderUrl: (
    params: CrossBorderUrlRequest,
    source?: CancelTokenSource,
    bearer?: string,
  ) => Promise<CrossBorderUrlResponse>;
  fetchCrossBorderUrlInfo: (
    params: CrossBorderUrlInfoRequest,
    source?: CancelTokenSource,
  ) => Promise<CrossBorderUrlInfoResponse>;
  generateEmbedUrlDisputeManagement: (
    params: GenerateEmbedUrlDisputeManagementRequest,
    source?: CancelTokenSource,
  ) => Promise<GenerateEmbedUrlDisputeManagementResponse>;
  fetchDisputeManagementUrlInfo: (
    params: DisputeManagementUrlInfoRequest,
    source?: CancelTokenSource,
  ) => Promise<DisputeManagementUrlInfoResponse>;
  fetchCrossBorderBankCities: (
    params: CrossBorderBankCitiesRequest,
    source?: CancelTokenSource,
  ) => Promise<CrossBorderBankCitiesResponse>;
  fetchCrossBorderBankBranches: (
    params: CrossBorderBankBranchesRequest,
    source?: CancelTokenSource,
  ) => Promise<CrossBorderBankBranchesResponse>;
  fetchCrossBorderBankNames: (
    params: CrossBorderBankNamesRequest,
    source?: CancelTokenSource,
  ) => Promise<CrossBorderBankNamesResponse>;
  fetchCrossBorderBankLocations: (
    params: CrossBorderBankLocationsRequest,
    source?: CancelTokenSource,
  ) => Promise<CrossBorderBankLocationsResponse>;
  postCrossBorderAddDeliveryMethod: (
    params: CrossBorderAddDeliveryMethodRequest,
    source?: CancelTokenSource,
  ) => Promise<CrossBorderAddDeliveryMethodResponse>;

  fetchCrossBorderCountries: (
    params: CrossBorderCountriesRequest,
    source?: CancelTokenSource,
  ) => Promise<CrossBorderCountriesResponse>;
  fetchDeliveryChannelsFields: (
    params: DeliveryChannelsFieldsRequest,
    source?: CancelTokenSource,
  ) => Promise<DeliveryChannelsFieldsResponse>;
  fetchDeliveryChannelsFieldsData: (
    params: DeliveryChannelsFieldsDataRequest,
    source?: CancelTokenSource,
  ) => Promise<DeliveryChannelsFieldsDataResponse>;
  fetchDeliveryChannels: (
    params: DeliveryChannelsRequest,
    source?: CancelTokenSource,
  ) => Promise<DeliveryChannelsResponse>;
  postCrossBorderQuotes: (
    paramse: CrossBorderQuotesRequest,
    source?: CancelTokenSource,
  ) => Promise<CrossBorderQuotesResponse>;
  fetchCrossBorderRecipientContacts: (
    params: CrossBorderRecipientContactsRequest,
    source?: CancelTokenSource,
  ) => Promise<CrossBorderRecipientContactsResponse>;
  postAddFavoriteRecipientContact: (
    params: AddFavoriteRecipientContactRequest,
    source?: CancelTokenSource,
  ) => Promise<AddFavoriteRecipientContactResponse>;
  postDeleteFavoriteRecipientContact: (
    params: DeleteFavoriteRecipientContactRequest,
    source?: CancelTokenSource,
  ) => Promise<DeleteFavoriteRecipientContactResponse>;
  fetchRecipientContactDeliveryMethods: (
    params: RecipientContactDeliveryMethodsRequest,
    source?: CancelTokenSource,
  ) => Promise<RecipientContactDeliveryMethodsResponse>;
  fetchGovernmentIdentifier: (
    params: GovernmentIdentifierRequest,
    source?: CancelTokenSource,
  ) => Promise<GovernmentIdentifierResponse>;
  fetchGovernmentIdentifierSchemas: (
    params: GovernmentIdentifierSchemasRequest,
    source?: CancelTokenSource,
  ) => Promise<GovernmentIdentifierSchemasResponse>;
  postGovernmentIdentifier: (
    params: AddGovernmentIdentifierRequest,
    source?: CancelTokenSource,
  ) => Promise<AddGovernmentIdentifierResponse>;
  postEditRecipient: (params: EditRecipientRequest, source?: CancelTokenSource) => Promise<EditRecipientResponse>;
  postAddRecipient: (params: AddRecipientRequest, source?: CancelTokenSource) => Promise<AddRecipientResponse>;
  postCreateGlobalSendTransaction: (
    params: CreateGlobalSendTransactionRequest,
    source?: CancelTokenSource,
  ) => Promise<CreateGlobalSendTransactionResponse>;
  postEditContact: (params: EditContactRequest, source?: CancelTokenSource) => Promise<ApiResponse>;
  fetchBankSchema: (params: BankSchemaRequest, source?: CancelTokenSource) => Promise<BankSchemaResponse>;
  confirmEmailVerification: (
    params: ContactVerificationConfirmRequest,
    source?: CancelTokenSource,
  ) => Promise<ContactVerificationConfirmResponse>;
  confirmPhoneVerification: (
    params: ContactVerificationConfirmRequest,
    source?: CancelTokenSource,
  ) => Promise<ContactVerificationConfirmResponse>;
  postGenerateOtp: (params: GenerateOtpRequest, source?: CancelTokenSource) => Promise<GenerateOtpResponse>;
  postValidateOtp: (params: ValidateOtpRequest, source?: CancelTokenSource) => Promise<ApiResponse>;
  fetchElinxPaymentMethods: (
    params: ElinxPaymentMethodsRequest,
    source?: CancelTokenSource,
    bearer?: string,
  ) => Promise<ElinxPaymentMethodsResponse>;
  postRequirePadAgreement: (
    params: RequirePadAgreementRequest,
    source: CancelTokenSource,
    bearer: string,
  ) => Promise<RequirePadAgreementResponse>;
  postAcceptAgreement: (params: AcceptAgreementRequest, source?: CancelTokenSource) => Promise<ApiResponse>;
  postInterestAccount: (source?: CancelTokenSource) => Promise<ApiResponse>;
  fetchInterestAccount: (
    params: InterestAccountRequest,
    source?: CancelTokenSource,
  ) => Promise<InterestAccountResponse>;
  fetchInterestAccountDetail: (
    params: InterestAccountDetailRequest,
    source?: CancelTokenSource,
  ) => Promise<InterestAccountDetailResponse>;
  postInterestAccountTransfer: (
    params: InterestAccountTransferRequest,
    source?: CancelTokenSource,
  ) => Promise<InterestAccountTransferResponse>;
  fetchInterestAccountRates: (
    params: InterestAccountRatesRequest,
    source?: CancelTokenSource,
  ) => Promise<InterestAccountRatesResponse>;
  fetchInterestAccountAccruedInterest: (
    params: InterestAccountAccruedInterestRequest,
    source?: CancelTokenSource,
  ) => Promise<InterestAccountAccruedInterestResponse>;
  fetchInterestAccountTransactions: (
    params: InterestAccountTransactionsRequest,
    source?: CancelTokenSource,
  ) => Promise<InterestAccountTransactionsResponse>;
  fetchUserSettings: (params: UserSettingsRequest, source?: CancelTokenSource) => Promise<UserSettingsResponse>;
  postSubmitUserSettings: (
    params: SubmitUserSettingsRequest,
    source?: CancelTokenSource,
  ) => Promise<UserSettingsResponse>;
  postRecipientContactGenerateEmbedUrl: (
    params: RecipientContactsGenerateEmbedUrlRequest,
    source?: CancelTokenSource,
  ) => Promise<RecipientContactsGenerateEmbedUrlResponse>;
  activateClientAccount: (
    params: ActivateDeactiveteClientAccountRequest,
    source?: CancelTokenSource,
  ) => Promise<ActivateDeactiveteClientAccountResponse>;
  deactivateClientAccount: (
    params: ActivateDeactiveteClientAccountRequest,
    source?: CancelTokenSource,
  ) => Promise<ActivateDeactiveteClientAccountResponse>;
  activateContact: (
    params: ActivateDeactiveteContactRequest,
    source?: CancelTokenSource,
  ) => Promise<ActivateDeactiveteContactResponse>;
  deactivateContact: (
    params: ActivateDeactiveteContactRequest,
    source?: CancelTokenSource,
  ) => Promise<ActivateDeactiveteContactResponse>;
  postRtpWithdraw: (params: RtpWithdrawRequest, source?: CancelTokenSource) => Promise<RtpWithdrawResponse>;
  postFedwireWithdraw: (params: FedwireWithdrawRequest, source?: CancelTokenSource) => Promise<FedwireWithdrawResponse>;
  fetchAccountExtendedInfo: (bearer?: string, source?: CancelTokenSource) => Promise<AccountExtendedInfoResponse>;
  postELinxSettingsSetNotification: (
    params: ELinxSettingsSetNotificationRequest,
    source?: CancelTokenSource,
  ) => Promise<ApiResponse>;
  postELinxSettingsSetReminder: (
    params: ELinxSettingsSetReminderRequest,
    source?: CancelTokenSource,
  ) => Promise<ApiResponse>;
  fetchWatchList: (params: WatchListRequest, source?: CancelTokenSource) => Promise<WatchListResponse>;
  addWatchList: (params: AddWatchListRequest, source?: CancelTokenSource) => Promise<ApiResponse>;
  deleteWatchList: (params: DeleteWatchListRequest, source?: CancelTokenSource) => Promise<ApiResponse>;
  setWatchList: (params: SetWatchListRequest, source?: CancelTokenSource) => Promise<ApiResponse>;
  fetchCurrencies: (params: CurrenciesRequest, source?: CancelTokenSource) => Promise<CurrenciesResponse>;
  postEditInteracEmail: (
    params: InteracEditEmailRequest,
    source?: CancelTokenSource,
    bearer?: string,
  ) => Promise<InteracEditEmailResponse>;
  addContact: (params: AddContactRequest, source?: CancelTokenSource) => Promise<AddContactResponse>;
  generateGCMEmbedUrl: (
    params: GenerateGCMEmbedUrlRequest,
    source?: CancelTokenSource,
  ) => Promise<GenerateGCMEmbedUrlResponse>;
  railzCreateInvoice: (params: RailzCreateInvoiceRequest, source?: CancelTokenSource) => Promise<ApiResponse>;
  railzCustomers: (params: RailzCustomersRequest, source?: CancelTokenSource) => Promise<RailzCustomersResponse>;
  railzInventory: (params: RailzInventoryRequest, source?: CancelTokenSource) => Promise<RailzInventoryResponse>;
  railzTaxRates: (params: RailzTaxRatesRequest, source?: CancelTokenSource) => Promise<RailzTaxRatesResponse>;
  railzTrackingCategories: (
    params: RailzTrackingCategoriesRequest,
    source?: CancelTokenSource,
  ) => Promise<RailzTrackingCategoriesResponse>;
  railzCreateBill: (params: RailzCreateBillRequest, source?: CancelTokenSource) => Promise<ApiResponse>;
  railzVendors: (params: RailzVendorsRequest, source?: CancelTokenSource) => Promise<RailzVendorsResponse>;
  railzCategories: (params: RailzCategories, source?: CancelTokenSource) => Promise<RailzCategoriesResponse>;
  authenticateMerchantUser: (source?: CancelTokenSource) => Promise<AuthenticateMerchantUserResponse>;

  fetchCrossBorderCashPickupStates: (
    params: CrossBorderCashPickupStatesRequest,
    source?: CancelTokenSource,
  ) => Promise<CrossBorderCashPickupStatesResponse>;
  fetchCrossBorderCashPickupCities: (
    params: CrossBorderCashPickupCitiesRequest,
    source?: CancelTokenSource,
  ) => Promise<CrossBorderCashPickupCitiesResponse>;
  fetchCrossBorderCashPickupLocations: (
    params: CrossBorderCashPickupLocationsRequest,
    source?: CancelTokenSource,
  ) => Promise<CrossBorderCashPickupLocationsResponse>;
  fetchPartnerSigningAuthorityInfo: (
    bearer: string,
    source?: CancelTokenSource,
  ) => Promise<PartnerSigningAuthorityInfoResponse>;
  postAddPartnerSigner: (
    params: AddSignerRequest,
    bearer: string,
    source?: CancelTokenSource,
  ) => Promise<AddSignerResponse>;
  postEditPartnerSigner: (
    params: EditSignerRequest,
    bearer: string,
    source?: CancelTokenSource,
  ) => Promise<EditSignerResponse>;
  postDeletePartnerSigner: (
    params: DeleteSignerRequest,
    bearer: string,
    source?: CancelTokenSource,
  ) => Promise<DeleteSignerResponse>;
  fetchGCMBalance: (params: GCMBalanceRequest, source?: CancelTokenSource) => Promise<GCMBalanceResponse>;
  fetchGCMRatesFeesComparison: (
    params: GCMRatesFeesComparisonRequest,
    source?: CancelTokenSource,
  ) => Promise<GCMRatesFeesComparisonResponse>;
  fetchGCMFundingRails: (source?: CancelTokenSource) => Promise<GCMFundingRailsResponse>;
  postLinkPaypal: (
    params: LinkPaypalRequest,
    bearer: string,
    source?: CancelTokenSource,
  ) => Promise<LinkPaypalResponse>;
  postLinkVenmo: (params: LinkVenmoRequest, bearer: string, source?: CancelTokenSource) => Promise<LinkVenmoResponse>;
  setMyGooglePay: (params: SetMyGooglePayRequest, source?: CancelTokenSource) => Promise<SetMyGooglePayResponse>;
  postAFTPullFunds: (params: AFTPullFundsRequest, source?: CancelTokenSource) => Promise<AFTPullFundsResponse>;
  postChargeGooglePay: (params: ChargeGooglePayRequest, source?: CancelTokenSource) => Promise<ChargeGooglePayResponse>;

  fetchBillPaymentMerchants: (
    params: BillPaymentMerchantsRequest,
    source?: CancelTokenSource,
  ) => Promise<BillPaymentMerchantsResponse>;
  fetchBillPaymentAccountMerchants: (
    params: BillPaymentAccountMerchantsRequest,
    source?: CancelTokenSource,
  ) => Promise<BillPaymentAccountMerchantsResponse>;
  fetchBillPayInfo: (params: BillPayInfoRequest, source?: CancelTokenSource) => Promise<BillPayInfoResponse>;
  createBillPayment: (params: BillPayCreateRequest, source?: CancelTokenSource) => Promise<BillPayCreateResponse>;
  addBillPaymentMerchant: (
    params: BillPayAccountMerchantAddRequest,
    source?: CancelTokenSource,
  ) => Promise<BillPayAccountMerchantAddResponse>;
  editBillPaymentMerchant: (
    params: BillPayAccountMerchantEditRequest,
    source?: CancelTokenSource,
  ) => Promise<BillPayAccountMerchantEditResponse>;
  fetchGlobalSendAdminUrlInfo: (
    params: GlobalSendAdminUrlInfoRequest,
    source?: CancelTokenSource,
  ) => Promise<GlobalSendAdminUrlInfoResponse>;
  fetchGlobalSendAdminDeliveryChannels: () => Promise<GlobalSendDeliveryChannelsResponse>;
  publishGlobalSendAdminDeliveryChannels: (
    params: PublishGlobalSendAdminDeliveryChannelsRequest,
    source?: CancelTokenSource,
  ) => Promise<ApiResponse>;
  fetchGlobalSendAdminFundingOptions: () => Promise<FetchGlobalSendAdminFundingOptionsResponse>;
  fetchDisputeManagementOverview: (
    params: DisputeManagementOverviewRequest,
    source?: CancelTokenSource,
  ) => Promise<DisputeManagementOverviewResponse>;
  fetchDisputeManagementList: (
    params: DisputeManagementListRequest,
    source?: CancelTokenSource,
  ) => Promise<DisputeManagementListResponse>;
  fetchDisputeStages: (params: DisputeStagesRequest, source?: CancelTokenSource) => Promise<DisputeStagesResponse>;
  fetchDisputeDetails: (params: DisputeDetailsRequest, source?: CancelTokenSource) => Promise<DisputeDetailsResponse>;
  fetchDisputeDocuments: (
    params: DisputeDocumentsRequest,
    source?: CancelTokenSource,
  ) => Promise<DisputeDocumentsResponse>;
  respondToDispute: (
    params: RespondAndChallengeDisputeRequest,
    source?: CancelTokenSource,
  ) => Promise<RespondAndChallengeDisputeResponse>;
  challengeDispute: (
    params: RespondAndChallengeDisputeRequest,
    source?: CancelTokenSource,
  ) => Promise<RespondAndChallengeDisputeResponse>;
  addDisputeNote: (params: AddDisputeNoteRequest, source?: CancelTokenSource) => Promise<AddDisputeNoteResponse>;
  acceptDispute: (params: AcceptDisputeRequest, source?: CancelTokenSource) => Promise<AcceptDisputeResponse>;
  fetchDisputeReasons: (params: DisputeReasonsRequest, source?: CancelTokenSource) => Promise<DisputeReasonsResponse>;
  submitGlobalSendAdminFundingOptions: (
    params: SubmitGlobalSendAdminFundingOptionsRequest,
    source?: CancelTokenSource,
  ) => Promise<ApiResponse>;
  fetchInteracAutoDepositStatus: (
    params: InteracAutoDepositStatusRequest,
    bearer?: string,
    source?: CancelTokenSource,
  ) => Promise<InteracAutoDepositStatusResponse>;
  fetchConvenienceFees: (
    params: ConvenienceFeesRequest,
    source?: CancelTokenSource,
  ) => Promise<ConvenienceFeesResponse>;
  fetchBillPayTransaction: (
    params: BillPayTransactionRequest,
    source?: CancelTokenSource,
  ) => Promise<BillPayTransactionResponse>;
  generateBillPayEmbedUrl: (
    params: BillPayGenerateEmbedUrlRequest,
    source?: CancelTokenSource,
  ) => Promise<BillPayGenerateEmbedUrlResponse>;
  fetchGooglePayChargeTransaction: (
    params: TransactionRequest,
    source?: CancelTokenSource,
  ) => Promise<GooglePayChargeTransactionResponse>;
  fetchPayPalWithdrawTransaction: (
    params: TransactionRequest,
    source?: CancelTokenSource,
  ) => Promise<PayPalWithdrawTransactionResponse>;
  fetchVenmoWithdrawTransaction: (
    params: TransactionRequest,
    source?: CancelTokenSource,
  ) => Promise<VenmoWithdrawTransactionResponse>;
  postVenmoWithdraw: (params: VenmoWithdrawRequest, source?: CancelTokenSource) => Promise<VenmoWithdrawResponse>;
  postPayPalWithdraw: (params: PayPalWithdrawRequest, source?: CancelTokenSource) => Promise<PayPalWithdrawResponse>;
  fetchVenmoRecipients: (
    params: VenmoRecipientsRequest,
    source?: CancelTokenSource,
  ) => Promise<VenmoRecipientsResponse>;
  fetchPayPalRecipients: (
    params: PayPalRecipientsRequest,
    source?: CancelTokenSource,
  ) => Promise<PayPalRecipientsResponse>;
  fetchPayPalDefaultRecipient: (
    params: PayPalDefaultRecipientRequest,
    source?: CancelTokenSource,
  ) => Promise<PayPalDefaultRecipientResponse>;
  fetchVenmoDefaultRecipient: (
    params: VenmoDefaultRecipientRequest,
    source?: CancelTokenSource,
  ) => Promise<VenmoDefaultRecipientResponse>;
  fetchAccountUserPermissionsNew: (
    params: AccountUserPermissionsRequest,
    bearer?: string,
    source?: CancelTokenSource,
  ) => Promise<AccountUserPermissionsNewResponse>;
  fetchSubAccountPermissions: (
    params: SubAccountPermissionsRequest,
    source?: CancelTokenSource,
  ) => Promise<SubAccountPermissionResponse>;
};
